import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Plus, Trash2, Play, RefreshCw, Upload, Globe, Edit2, Bookmark,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle,
  XCircle, TrendingUp, TrendingDown, Minus, BarChart2,
  Calendar, Download, Info, Save, X, Link2, FileText,
  Activity, Layers, Zap, Eye, Filter, ExternalLink, Copy,
  GitBranch, Clock, Cpu, Bell, Search, ArrowRight, ArrowLeft, ToggleLeft, ToggleRight, DollarSign
} from 'lucide-react';
import { resolveFilterShape, buildFilters, supportsFilters, defaultOperator } from '../lib/filterShape';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v, type) => {
  if (v === null || v === undefined) return '—';
  if (type === 'currency') return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (type === 'integer') return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return String(v);
};
const fmtPct = v => v === null || v === undefined ? '—' : `${Math.abs(Number(v)).toFixed(2)}%`;
const timeAgo = iso => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff/60000), hrs = Math.floor(diff/3600000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
};

const SEV = {
  critical: { bg:'color-mix(in srgb, var(--red) 8%, transparent)', border:'color-mix(in srgb, var(--red) 30%, transparent)', text:'var(--red)', badge:'badge-red', icon:'🔴' },
  warning:  { bg:'color-mix(in srgb, var(--amber) 8%, transparent)', border:'color-mix(in srgb, var(--amber) 25%, transparent)', text:'var(--amber)', badge:'badge-amber', icon:'🟡' },
  ok:       { bg:'color-mix(in srgb, var(--cyan) 6%, transparent)', border:'color-mix(in srgb, var(--cyan) 20%, transparent)', text:'var(--cyan)', badge:'badge-cyan', icon:'🔍' },
  favorable:{ bg:'color-mix(in srgb, var(--green) 6%, transparent)', border:'color-mix(in srgb, var(--green) 20%, transparent)', text:'var(--green)', badge:'badge-green', icon:'✅' },
};
const getSev = pct => { const a=Math.abs(pct||0); return a>20?'critical':a>5?'warning':'ok'; };
const getSevStyle = pct => SEV[getSev(pct)] || SEV.ok;

// Format a Date as YYYY-MM-DD using LOCAL date parts. NEVER use
// toISOString().split('T')[0] for user-facing dates — that converts to UTC and
// rolls the date back a day for anyone east of UTC (e.g. IST), which is the
// recurring "Yesterday shows the wrong day" bug. Always go through this.
const localYMD = dt => {
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};
const localYesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return localYMD(d); };

// ─── Monthly grouping (shared by all daily-row results tables) ────────────────
// Buckets per-date comparison rows into YYYY-MM groups, filtered by an optional
// date range, and computes per-month subtotals. The % subtotal is RECOMPUTED
// from the summed values (never averaged from daily %s, which would be wrong).
// `keys` maps the generic fields to whatever the caller's rows use, since the
// different tables name their columns differently (adServerImps vs leftImps…).
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function monthLabel(ym) { const [y,m] = ym.split('-'); return `${MONTH_NAMES[parseInt(m,10)-1]} ${y}`; }
const pctDiff = (a, b) => { const base = Math.max(Math.abs(a), Math.abs(b)); return base === 0 ? 0 : ((a - b) / base) * 100; };

function groupRowsByMonth(rows, keys, dateFrom, dateTo) {
  const { leftImps, rightImps, leftRev, rightRev } = keys;
  const filtered = (rows || []).filter(r =>
    (!dateFrom || r.date >= dateFrom) && (!dateTo || r.date <= dateTo)
  );
  const byMonth = new Map();
  for (const r of filtered) {
    const ym = String(r.date).slice(0, 7); // YYYY-MM
    if (!byMonth.has(ym)) byMonth.set(ym, { ym, label: monthLabel(ym), rows: [],
      sumLeftImps:0, sumRightImps:0, sumLeftRev:0, sumRightRev:0, critical:0, warning:0, ok:0 });
    const g = byMonth.get(ym);
    g.rows.push(r);
    g.sumLeftImps  += Number(r[leftImps])  || 0;
    g.sumRightImps += Number(r[rightImps]) || 0;
    g.sumLeftRev   += Number(r[leftRev])   || 0;
    g.sumRightRev  += Number(r[rightRev])  || 0;
    const sev = r.severity || getSev(r.impPct);
    if (sev === 'critical') g.critical++; else if (sev === 'warning') g.warning++; else g.ok++;
  }
  // Per-month aggregate diffs + recomputed %s + worst-severity rollup.
  // Also sort each month's daily rows DESCENDING — users expect latest-first,
  // mirroring how the months themselves are sorted (newest first) below.
  for (const g of byMonth.values()) {
    g.impDiff = g.sumLeftImps - g.sumRightImps;
    g.revDiff = g.sumLeftRev - g.sumRightRev;
    g.impPct  = pctDiff(g.sumLeftImps, g.sumRightImps);
    g.revPct  = pctDiff(g.sumLeftRev, g.sumRightRev);
    g.severity = g.critical > 0 ? 'critical' : g.warning > 0 ? 'warning' : 'ok';
    g.rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }
  // Newest month first
  return [...byMonth.values()].sort((a, b) => b.ym.localeCompare(a.ym));
}

// Reusable date-range filter bar. Drives groupRowsByMonth via parent state.
function DateRangeFilter({ dateFrom, dateTo, setDateFrom, setDateTo }) {
  return (
    <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap', marginBottom:'10px', fontSize:'11px', color:'var(--text-muted)' }}>
      <span style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' }}>Filter dates</span>
      <input type="date" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'150px', colorScheme:'dark' }}
        value={dateFrom || ''} onChange={e => setDateFrom(e.target.value || null)} />
      <span>→</span>
      <input type="date" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'150px', colorScheme:'dark' }}
        value={dateTo || ''} onChange={e => setDateTo(e.target.value || null)} />
      {(dateFrom || dateTo) && (
        <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(null); setDateTo(null); }}>Clear</button>
      )}
    </div>
  );
}

// Reusable monthly-grouped daily table. `columns` describes the daily columns;
// month header rows render subtotals and expand/collapse on click (collapsed by
// default). Optional `renderRowExpansion(row)` lets a daily row itself expand
// (used by BasicResults for its advertiser drill-down); `rowExpandable(row)`
// + `onRowClick(row)` drive that. Tables that don't pass these get plain rows.
function MonthlyDateTable({ rows, keys, columns, dateFrom, dateTo, renderRowExpansion, rowExpandable, onRowClick, isRowExpanded, rowLoading }) {
  const groups = groupRowsByMonth(rows, keys, dateFrom, dateTo);
  const [open, setOpen] = React.useState(() => new Set()); // collapsed by default
  const toggle = ym => setOpen(prev => { const n = new Set(prev); n.has(ym) ? n.delete(ym) : n.add(ym); return n; });
  const colCount = columns.length + 1; // +1 for the leading date/▸ cell

  if (!groups.length) {
    return <div style={{ textAlign:'center', padding:'16px', fontSize:'12px', color:'var(--text-muted)' }}>No data in this date range.</div>;
  }

  return (
    <div style={{ overflowX:'auto' }}>
      <table className="data-table">
        <thead><tr>
          <th>Date</th>
          {columns.map((c, i) => <th key={i} style={c.thStyle}>{c.header}</th>)}
        </tr></thead>
        <tbody>
          {groups.map(g => {
            const sv = SEV[g.severity] || SEV.ok;
            const isOpen = open.has(g.ym);
            const totalCols = columns.length + 1;
            return (
              <React.Fragment key={g.ym}>
                {/* ── Month banner row: large chevron chip + accent bar + subtotals ── */}
                <tr onClick={() => toggle(g.ym)}
                  style={{ cursor:'pointer', background:'var(--bg-elevated)', borderTop:'2px solid var(--border)' }}>
                  <td style={{ fontWeight:800, borderLeft:`3px solid ${sv.text}`, paddingLeft:'10px' }}>
                    <span style={{
                      display:'inline-flex', alignItems:'center', justifyContent:'center',
                      width:'22px', height:'22px', borderRadius:'6px', marginRight:'10px',
                      background: isOpen ? 'var(--cyan)' : 'transparent',
                      border: isOpen ? 'none' : '1.5px solid var(--cyan)',
                      color: isOpen ? 'var(--bg-base)' : 'var(--cyan)',
                      fontSize:'13px', fontWeight:900, verticalAlign:'middle', transition:'all 0.12s',
                    }}>{isOpen ? '▾' : '▸'}</span>
                    <span style={{ fontSize:'13px', color:'var(--text-primary)' }}>{g.label}</span>
                    <span style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:600, marginLeft:'6px' }}>· {g.rows.length} {g.rows.length===1?'day':'days'}</span>
                  </td>
                  {columns.map((c, i) => (
                    <td key={i} className={c.mono ? 'mono' : undefined} style={{ fontWeight:800 }}>
                      {c.subtotal ? c.subtotal(g, sv) : ''}
                    </td>
                  ))}
                </tr>

                {/* ── Labeled totals strip (only when expanded) ── */}
                {isOpen && (
                  <tr style={{ background:'var(--bg-base)' }}>
                    <td colSpan={totalCols} style={{ padding:'6px 14px 6px 23px', borderLeft:`3px solid ${sv.text}` }}>
                      <span style={{ display:'inline-flex', gap:'18px', flexWrap:'wrap', fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>
                        <span>MONTH TOTAL</span>
                        <span>Imp Δ <b style={{ color: g.impDiff>0?'var(--amber)':'var(--green)' }}>{g.impDiff>0?'+':''}{fmt(g.impDiff,'integer')}</b> <span style={{ color:sv.text }}>({fmtPct(g.impPct)})</span></span>
                        <span>Rev Δ <b style={{ color: g.revDiff>0?'var(--amber)':'var(--green)' }}>{g.revDiff>0?'+':''}{fmt(g.revDiff,'currency')}</b> <span style={{ color:(SEV[getSev(g.revPct)]||SEV.ok).text }}>({fmtPct(g.revPct)})</span></span>
                      </span>
                    </td>
                  </tr>
                )}

                {/* ── Daily breakdown divider (only when expanded) ── */}
                {isOpen && (
                  <tr style={{ background:'var(--bg-base)' }}>
                    <td colSpan={totalCols} style={{ padding:'3px 14px 3px 23px', fontSize:'9px', letterSpacing:'1.5px', color:'var(--text-muted)', textTransform:'uppercase', borderBottom:'1px solid var(--border)' }}>
                      Daily breakdown
                    </td>
                  </tr>
                )}

                {/* ── Daily rows — demoted/indented, only when expanded ── */}
                {isOpen && g.rows.map((r, ri) => {
                  const rsv = SEV[r.severity || getSev(r.impPct)] || SEV.ok;
                  const canExpand = rowExpandable ? rowExpandable(r) : false;
                  const rExpanded = isRowExpanded ? isRowExpanded(r) : false;
                  return (
                    <React.Fragment key={ri}>
                      <tr
                        onClick={canExpand && onRowClick ? () => onRowClick(r) : undefined}
                        style={{ fontSize:'11px', cursor: canExpand ? 'pointer' : undefined,
                          background: rExpanded ? 'var(--bg-hover)' : (r.severity || getSev(r.impPct)) !== 'ok' ? rsv.bg : (ri % 2 ? 'color-mix(in srgb, var(--text-primary) 2%, transparent)' : undefined) }}>
                        <td style={{ paddingLeft:'45px', color:'var(--cyan)', fontSize:'13px', fontWeight:600 }}>
                          {canExpand && (
                            <span style={{ marginRight:'5px', color: rExpanded ? 'var(--cyan)' : 'var(--text-muted)' }}>
                              {rowLoading && rowLoading(r) ? '⟳' : rExpanded ? '▾' : '▸'}
                            </span>
                          )}
                          {r.date}
                        </td>
                        {columns.map((c, i) => (
                          <td key={i} className={c.mono ? 'mono' : undefined}>{c.cell(r, rsv)}</td>
                        ))}
                      </tr>
                      {rExpanded && renderRowExpansion && (
                        <tr style={{ background:'var(--bg-surface)' }}>
                          <td colSpan={colCount} style={{ padding:0 }}>{renderRowExpansion(r)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── File readers ─────────────────────────────────────────────────────────────
const readAsText   = f => new Promise((res,rej) => { const r=new FileReader(); r.onload=e=>res(e.target.result); r.onerror=rej; r.readAsText(f); });
const readAsBase64 = f => new Promise((res,rej) => { const r=new FileReader(); r.onload=e=>res(e.target.result.split(',')[1]); r.onerror=rej; r.readAsDataURL(f); });

// ─── Monitor type definitions ─────────────────────────────────────────────────
const MONITOR_TYPES = [
  {
    id: 'master_basic',
    icon: Layers,
    label: 'Ad Server → Demand Partners',
    color: 'var(--cyan)',
    desc: 'Compare Ad Server totals vs the sum of all Demand Partners. Date-wise discrepancy on Impressions and Gross Revenue. Deep Dive to find which partner is causing it.',
    badge: 'Recommended',
    role: 'demand',
  },
  {
    id: 'master_advanced',
    icon: Zap,
    label: 'Ad Server → Demand (Advertiser-wise)',
    color: 'var(--purple)',
    desc: 'Advertiser-wise breakdown. Match each Demand Partner to its Advertiser line in the Ad Server. Identifies exactly which partner and advertiser combination is causing the discrepancy.',
    badge: 'Advanced',
    role: 'demand',
  },
  {
    id: 'supply',
    icon: Activity,
    label: 'Ad Server → Supply Partners',
    color: 'var(--green)',
    desc: 'Compare Ad Server totals vs SSP / Supply Partner reporting. Detects under-reporting or over-reporting from your Supply stack. Date-wise Impressions and Revenue check.',
    badge: 'Supply',
    role: 'supply',
  },
  {
    id: 'freeform',
    icon: GitBranch,
    label: 'Free Form Monitor',
    color: 'var(--amber)',
    desc: 'Full Supply → Ad Server → Demand chain analysis. Add any mix of Supply, Ad Server, and Demand partners. Runs two-sided discrepancy: Supply vs Ad Server and Ad Server vs Demand. All upload modes supported.',
    badge: 'Multi-Party',
    badgeExtra: [{ label: 'BETA', style: 'badge-amber' }, { label: 'NEW', style: 'badge-purple' }],
    role: 'freeform',
  },
  {
    id: 'auto_monitor',
    icon: Cpu,
    label: 'Auto Monitor',
    color: 'var(--green-soft)',
    desc: 'Automated discrepancy check running once daily at a time you choose, using your Saved API Configs. Default is 8 AM UTC — late enough that demand partner data reported in PST is fully available. No CSV upload. Critical discrepancies trigger email + notification bar alerts. Requires saved API configs in Account → Partner Configurations.',
    badge: 'Automated',
    badgeExtra: [{ label: 'BETA', style: 'badge-green' }, { label: 'NEW', style: 'badge-purple' }],
    role: 'auto',
  },
];

// ─── Date Range Preset Buttons ────────────────────────────────────────────────
// ─── Date format utility ─────────────────────────────────────────────────────
// Different APIs use different date formats — user selects which one matches their API
const DATE_FORMATS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD',  example: '2026-04-12' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY',  example: '12/04/2026' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY',  example: '04/12/2026' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY',  example: '12-04-2026' },
  { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY',  example: '04-12-2026' },
  { value: 'YYYYMMDD',   label: 'YYYYMMDD',    example: '20260412'   },
];

// isoDate: 'YYYY-MM-DD' string from date picker → reformatted string
function formatDateForApi(isoDate, fmt) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  switch (fmt) {
    case 'DD/MM/YYYY': return `${d}/${m}/${y}`;
    case 'MM/DD/YYYY': return `${m}/${d}/${y}`;
    case 'DD-MM-YYYY': return `${d}-${m}-${y}`;
    case 'MM-DD-YYYY': return `${m}-${d}-${y}`;
    case 'YYYYMMDD':   return `${y}${m}${d}`;
    default:           return isoDate; // YYYY-MM-DD
  }
}

// Per-party API URL source inputs for the Auto Monitor builder (URL + date format).
function UrlSourceInputs({ urlVal, urlDateFmt, onUrl, onFmt }) {
  return (
    <div style={{ marginTop:'6px', padding:'8px 10px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
      <input className="input-field w-full" style={{ marginBottom:'6px', fontSize:'11px' }}
        placeholder="https://api.example.com/report?from={date_from}&to={date_to}"
        value={urlVal || ''} onChange={e => onUrl(e.target.value)} />
      <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>Date format:</span>
        {DATE_FORMATS.map(f => (
          <button key={f.value} className={`btn btn-sm ${(urlDateFmt || 'YYYY-MM-DD') === f.value ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize:'10px', padding:'2px 8px' }} onClick={() => onFmt(f.value)}>{f.label}</button>
        ))}
      </div>
      <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'5px' }}>
        Use <code style={{ fontFamily:'var(--font-mono)', color:'var(--cyan)' }}>{'{date_from}'}</code> and <code style={{ fontFamily:'var(--font-mono)', color:'var(--cyan)' }}>{'{date_to}'}</code> — the monitor substitutes the run date in this format.
      </div>
      <TestFetchButton getPayload={() => ({ urlVal, urlDateFmt })} />
    </div>
  );
}

// Builder submit helper: turn the '__url__' dropdown sentinel into a clean URL source.
function normalizeAutoParty(p) {
  if (p.savedConfigId === '__url__')
    return { ...p, savedConfigId: '', inputType: 'url', urlVal: (p.urlVal || '').trim(), urlDateFmt: p.urlDateFmt || 'YYYY-MM-DD' };
  return { ...p, urlVal: '', urlDateFmt: '', inputType: 'saved' };
}

// Shows how the Ad Server's revenue maps to partner roles (net↔supply, gross↔demand),
// auto-derived from the partners present, plus optional explicit field overrides.
function RevenueMetricPanel({ hasSupply, hasDemand, mapping, onChange, savedConfigId, savedConfigs, filters }) {
  const m = mapping || { gross: '', net: '', discoveredColumns: [] };
  const cols = m.discoveredColumns || [];
  const isSaved = savedConfigId && savedConfigId !== '__url__';
  const cfg = (savedConfigs || []).find(c => String(c.id) === String(savedConfigId));
  const [discovering, setDiscovering] = useState(false);

  async function discover() {
    if (!cfg) { toast.error('Select a saved Ad Server config first.'); return; }
    setDiscovering(true);
    try {
      const yd = localYesterday();
      const customBody = buildBodyWithFilters(cfg, []);
      const { data } = await axios.post(`/api/partner-configs/${savedConfigId}/fetch-raw`, { date_from: yd, date_to: yd, custom_body: customBody });
      const found = data.columns || Object.keys(data.rows?.[0] || {});
      if (!found.length) { toast.error('No columns returned — check the config or try another date.'); return; }
      const lower = found.map(c => ({ key: c, l: String(c).toLowerCase() }));
      const grossGuess = lower.find(c => c.l === 'revenue' || c.l === 'gross_revenue' || c.l === 'advertiser_spend')?.key || '';
      const netGuess   = lower.find(c => c.l === 'supply_cost' || c.l === 'net_revenue' || c.l === 'publisher_revenue' || c.l === 'pub_revenue' || c.l === 'payout')?.key || '';
      onChange({ ...m, discoveredColumns: found, gross: m.gross || grossGuess, net: m.net || netGuess });
      toast.success(`${found.length} API fields discovered — revenue fields pre-filled`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to fetch sample response');
    } finally { setDiscovering(false); }
  }

  return (
    <div style={{ marginTop:'10px', paddingTop:'10px', borderTop:'1px solid var(--border)' }}>
      <label style={{ fontSize:'10px', color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px', fontWeight:700 }}>Revenue metric mapping</label>
      {hasSupply && <div style={{ fontSize:'11px', color:'var(--green)', marginBottom:'3px' }}>● Net revenue → compared against Supply partners</div>}
      {hasDemand && <div style={{ fontSize:'11px', color:'var(--purple)', marginBottom:'3px' }}>● Gross revenue → compared against Demand partners</div>}
      {!hasSupply && !hasDemand && <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>Add a Supply or Demand partner to set which Ad Server metric is compared.</div>}

      {!isSaved ? (
        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'8px' }}>Select a saved Ad Server config to discover fields. URL/CSV sources auto-detect net &amp; gross by column.</div>
      ) : cols.length === 0 ? (
        <div style={{ marginTop:'8px' }}>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px', lineHeight:1.5 }}>First, fetch a sample response to discover the actual API field names available for revenue mapping.</div>
          <button className="btn btn-secondary btn-sm" style={{ fontSize:'11px' }} disabled={discovering} onClick={discover}>
            {discovering ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Fetching fields...</> : <><BarChart2 size={11}/> Discover API Fields</>}
          </button>
        </div>
      ) : (
        <div style={{ marginTop:'8px' }}>
          <div style={{ fontSize:'11px', color:'var(--green)', marginBottom:'8px', display:'flex', alignItems:'center', gap:'5px', justifyContent:'space-between' }}>
            <span>✓ {cols.length} fields from API</span>
            <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:'10px', color:'var(--text-muted)', padding:0 }} onClick={() => onChange({ ...m, discoveredColumns: [] })}>Re-discover</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <div>
              <label style={{ fontSize:'10px', color:'var(--green)', display:'block', marginBottom:'3px', fontWeight:600 }}>Gross Revenue (vs Demand)</label>
              <select className="select-field" style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }} value={m.gross || ''} onChange={e => onChange({ ...m, gross: e.target.value })}>
                <option value="">— Use config default —</option>
                {cols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'10px', color:'var(--purple)', display:'block', marginBottom:'3px', fontWeight:600 }}>Net Revenue (vs Supply)</label>
              <select className="select-field" style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }} value={m.net || ''} onChange={e => onChange({ ...m, net: e.target.value })}>
                <option value="">— Use config default —</option>
                {cols.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {(m.gross || m.net) && (
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'5px', lineHeight:1.5 }}>
              {m.gross && <div>Gross → <code style={{ fontFamily:'var(--font-mono)', color:'var(--green)' }}>{m.gross}</code> · compared vs Demand</div>}
              {m.net   && <div>Net → <code style={{ fontFamily:'var(--font-mono)', color:'var(--purple)' }}>{m.net}</code> · compared vs Supply</div>}
            </div>
          )}
        </div>
      )}

      {isSaved && <TestFetchButton getPayload={() => ({ savedConfigId, revenueMapping: m, filters: filters || [] })} />}
    </div>
  );
}

// Dry-run a data source from the builder and preview the parsed/auto-detected rows.
function TestFetchButton({ getPayload }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  async function run() {
    const payload = getPayload();
    if (!payload || (!payload.savedConfigId && !payload.urlVal)) { setResult({ ok: false, error: 'Set a URL or saved config first.' }); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post('/api/disc-monitor-v2/auto-monitors/test-fetch', payload);
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.error || 'Test failed' });
    } finally { setBusy(false); }
  }
  return (
    <div style={{ marginTop:'6px' }}>
      <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px' }} disabled={busy} onClick={run}>
        {busy ? 'Testing…' : 'Test fetch'}
      </button>
      {result && <TestFetchResult result={result} />}
    </div>
  );
}

function TestFetchResult({ result }) {
  if (!result.ok) return <div style={{ marginTop:'6px', fontSize:'11px', color:'var(--red)' }}>✗ {result.error}</div>;
  const num = n => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const cur = n => '$' + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const dr = result.dateRange || {};
  return (
    <div style={{ marginTop:'6px', fontSize:'11px' }}>
      <div style={{ color:'var(--green)', marginBottom:'4px' }}>✓ {result.rowCount} row{result.rowCount === 1 ? '' : 's'} for {dr.from}{dr.to && dr.to !== dr.from ? ` – ${dr.to}` : ''}</div>
      {result.rowCount === 0 && <div style={{ color:'var(--amber)' }}>No rows returned — check the URL, auth token, or that the report has data for this date.</div>}
      {result.sample && result.sample.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'10px' }}>
            <thead><tr style={{ color:'var(--text-muted)' }}>
              <th style={{ textAlign:'left',  padding:'2px 6px' }}>Date</th>
              <th style={{ textAlign:'right', padding:'2px 6px' }}>Impressions</th>
              <th style={{ textAlign:'right', padding:'2px 6px' }}>Gross</th>
              <th style={{ textAlign:'right', padding:'2px 6px' }}>Net</th>
            </tr></thead>
            <tbody>
              {result.sample.map((r, i) => (
                <tr key={i} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'2px 6px' }}>{r.date}</td>
                  <td style={{ textAlign:'right', padding:'2px 6px' }}>{num(r.impressions)}</td>
                  <td style={{ textAlign:'right', padding:'2px 6px' }}>{cur(r.gross_revenue)}</td>
                  <td style={{ textAlign:'right', padding:'2px 6px' }}>{cur(r.net_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ color: result.netDistinct ? 'var(--green)' : 'var(--text-muted)', marginTop:'3px' }}>
            {result.netDistinct ? '● Distinct net revenue detected (net ≠ gross).' : '● Net = gross — source returns a single revenue figure.'}
          </div>
        </div>
      )}
    </div>
  );
}

function DatePresetButtons({ dateFrom, dateTo, onRange }) {
  // Use constructor form (year, month, day) to avoid DST-shift bugs from ms arithmetic
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  const fmt = localYMD; // local date parts — see localYMD note (avoids UTC rollback)

  const yesterday = new Date(y, m, d - 1);
  const presets = [
    { label: 'Today',      from: fmt(new Date(y,m,d)),   to: fmt(new Date(y,m,d)) },
    { label: 'Yesterday',  from: fmt(yesterday),           to: fmt(yesterday) },
    // Last N days: ends at yesterday (today not included)
    { label: 'Last 3 days',from: fmt(new Date(y,m,d-3)),  to: fmt(yesterday) },
    { label: 'Last 7 days',from: fmt(new Date(y,m,d-7)),  to: fmt(yesterday) },
    // This Month: 1st of current month → yesterday (today not included)
    { label: 'This Month', from: fmt(new Date(y,m,1)),     to: fmt(yesterday) },
    // Last Month: 1st → last day of last month (both inclusive)
    { label: 'Last Month', from: fmt(new Date(y,m-1,1)),   to: fmt(new Date(y,m,0)) },
  ];
  const active = presets.find(p => p.from === dateFrom && p.to === dateTo);
  return (
    <div>
      <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'5px', fontWeight:600 }}>Date Range</div>
      <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'8px' }}>
        {presets.map(p => (
          <button key={p.label}
            className={`btn btn-sm ${active?.label === p.label ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize:'11px', padding:'3px 10px' }}
            onClick={() => onRange(p.from, p.to)}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Inject filters into a saved config request body (client-side) ───────────
// Mirrors the server-side logic in fetchFromSavedConfig / applyFiltersToBody.
function buildBodyWithFilters(cfg, filters) {
  const body = JSON.parse(JSON.stringify(cfg?.request_body || {}));
  const valid = (filters || []).filter(f => f.key && f.value?.trim());
  if (!valid.length) return body;

  const shape = resolveFilterShape(cfg);
  if (!supportsFilters(shape)) return body;

  // Dispatcher builds correctly-shaped entries. We APPEND to any pre-existing
  // filters on the saved monitor config (e.g. Criteo's publisher.network_id),
  // preserving DM's contract that user-input filters add to baseline.
  const entries = buildFilters(shape, valid);
  if (shape === 'json-field-value-operator') {
    body.filterBy = [...(body.filterBy || []), ...entries];
  } else {
    body.filters = [...(body.filters || []), ...entries];
  }
  return body;
}

// ─── Upload Panel ─────────────────────────────────────────────────────────────
function UploadPanel({ label, color, partyId, monitorId, partyType, partyRole, onUploaded, existingStatus, monitor, onRefreshMonitor }) {
  // Accurate role label — fixes supply partners showing as "Demand Partner".
  // Falls back to partyType only if role wasn't passed (defensive).
  const roleLabel = partyType === 'adserver' ? 'Ad Server'
    : partyRole === 'supply' ? 'Supply Partner'
    : partyRole === 'demand' ? 'Demand Partner'
    : partyRole === 'adserver' ? 'Ad Server'
    : 'Demand Partner';
  const fileRef = useRef(null);
  const [mode, setMode] = useState(() => {
    const sv = monitor?.partySavedSettings?.[partyId];
    return sv?.mode || (sv?.urlVal ? 'url' : sv?.configId ? 'saved' : 'file');
  });
  const [uploading, setUploading] = useState(false);
  const [urlVal, setUrlVal] = useState('');
  const [showExisting, setShowExisting] = useState(false);
  const [existingRows, setExistingRows] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [manualRows, setManualRows] = useState([{ date:'', impressions:'', gross_revenue:'', net_revenue:'', advertiser:'' }]);
  const [detectedAdvertisers, setDetectedAdvertisers] = useState([]);
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [editingRowData, setEditingRowData] = useState(null);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [configDateFrom, setConfigDateFrom] = useState('');
  const [configDateTo, setConfigDateTo] = useState('');
  const [fetchingConfig, setFetchingConfig] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [fetchFilters, setFetchFilters] = useState(() => monitor?.partySavedSettings?.[partyId]?.filters || []);
  const [grossRevenueField, setGrossRevenueField] = useState(() => monitor?.revenueMapping?.gross || '');
  const [netRevenueField, setNetRevenueField] = useState(() => monitor?.revenueMapping?.net || '');
  const [urlDateFrom, setUrlDateFrom] = useState('');
  const [urlDateTo, setUrlDateTo] = useState('');
  const [urlDateFmt, setUrlDateFmt] = useState('YYYY-MM-DD');
  const [savingSettings, setSavingSettings] = useState(false);
  const [discoveredColumns, setDiscoveredColumns] = useState(() => monitor?.partySavedSettings?.[partyId]?.discoveredColumns || []);
  const [discoveringColumns, setDiscoveringColumns] = useState(false);

  // Re-sync saved settings whenever the monitor prop is refreshed from the server
  useEffect(() => {
    const saved = monitor?.partySavedSettings?.[partyId];
    if (saved?.filters)            setFetchFilters(saved.filters);
    if (saved?.configId)           setSelectedConfigId(saved.configId);
    if (saved?.urlVal)             setUrlVal(saved.urlVal);
    if (saved?.urlDateFmt)         setUrlDateFmt(saved.urlDateFmt);
    if (saved?.grossRevenueField)  setGrossRevenueField(saved.grossRevenueField);
    if (saved?.netRevenueField)    setNetRevenueField(saved.netRevenueField);
    if (saved?.discoveredColumns)  setDiscoveredColumns(saved.discoveredColumns);
  }, [monitor?.partySavedSettings, partyId]);

  // Load saved partner configs once
  useEffect(() => {
    axios.get('/api/partner-configs')
      .then(r => setSavedConfigs(r.data))
      .catch(() => {});
  }, []);

  // Load previously detected advertisers from DB for this monitor
  useEffect(() => {
    if (partyType === 'adserver' && monitorId) {
      axios.get(`/api/disc-monitor-v2/data-status/${monitorId}`)
        .then(r => {
          const asData = r.data?.find?.(d => d.party_id === 'adserver');
          if (asData?.advertiser_list) setDetectedAdvertisers(asData.advertiser_list);
        }).catch(() => {});
    }
  }, [monitorId, partyType]);
  const borderColor = partyType === 'adserver' ? 'var(--cyan)' : 'var(--purple)';

  async function upload(inputType, data, manualRowsData) {
    setUploading(true);
    try {
      const payload = { partyId, partyType, inputType };
      if (inputType === 'manual') payload.manualRows = manualRowsData;
      else payload.data = data;
      const { data: result } = await axios.post(`/api/disc-monitor-v2/upload/${monitorId}`, payload);
      const msg = result.truncated
        ? `✓ ${result.upserted} rows uploaded (file had ${result.totalLines?.toLocaleString()} rows — first 100,000 processed)`
        : `✓ ${result.upserted} rows uploaded for ${label}`;
      toast.success(msg, { duration: result.truncated ? 6000 : 3000 });
      if (result.truncated) toast(`⚠️ Large file: only the first 100,000 rows were processed. Consider splitting your file if you need all data.`, { duration: 8000, icon: '⚠️' });
      if (result.filteredZeroRows) {
        toast(`ℹ️ ${result.zeroImpressionRows?.toLocaleString()} zero-impression rows were automatically removed. Only rows with impressions > 0 are stored to keep analysis accurate and prevent dashboard slowdowns.`, { duration: 8000, icon: '🔍' });
      }
      if (result.hasBundleData) {
        toast(`📦 Bundle ID column detected ("${result.bundleCol}"). For best performance and to avoid browser slowdowns, ensure your report only contains non-zero impression rows before uploading. Large bundle reports with zero rows can significantly slow down the tool.`, { duration: 10000, icon: '⚠️' });
      }
      if (result.suggestAdvanced) {
        toast('💡 Advertiser names detected — consider upgrading to Master Monitor Advanced for deeper insights.', { duration: 6000 });
      }
      onUploaded && onUploaded(result);
    } catch (e) { toast.error(e.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function handleFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls') upload('xlsx', await readAsBase64(file));
    else upload('csv', await readAsText(file));
  }

  const addManualRow = () => setManualRows(r => [...r, { date:'', impressions:'', gross_revenue:'', net_revenue:'', advertiser:'' }]);
  const updateRow = (i, f, v) => setManualRows(r => r.map((x,idx) => idx===i ? {...x,[f]:v} : x));
  const removeRow = i => setManualRows(r => r.filter((_,idx) => idx!==i));

  return (
    <div style={{ background: partyType==='adserver' ? 'color-mix(in srgb, var(--cyan) 3%, transparent)' : 'color-mix(in srgb, var(--purple) 3%, transparent)', border:`1px solid ${borderColor}`, borderRadius:'var(--radius)', padding:'14px', marginBottom:'10px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', flexWrap:'wrap', gap:'6px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:borderColor }} />
          <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>{label}</span>
          <span style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{roleLabel}</span>
        </div>
        {existingStatus && (
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={{ fontSize:'10px', color:'var(--green)', background:'var(--green-dim)', padding:'2px 8px', borderRadius:'4px', border:'1px solid color-mix(in srgb, var(--green) 20%, transparent)' }}>
              ✓ {existingStatus.row_count} rows · {existingStatus.date_from?.slice(0,10)} → {existingStatus.date_to?.slice(0,10)}
            </span>
            <button onClick={() => setShowExisting(e => !e)} style={{ fontSize:'10px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'4px', padding:'2px 8px', cursor:'pointer', color:'var(--cyan)' }}>
              {showExisting ? 'Hide Data' : 'Edit Data'}
            </button>
          </div>
        )}
      </div>

      {/* Mode tabs */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'10px' }}>
        {[{id:'file',icon:Upload,label:'File'},{id:'url',icon:Globe,label:'API URL'},{id:'manual',icon:Edit2,label:'Manual'},{id:'saved',icon:Bookmark,label:'Saved Config'}].map(({id,icon:Icon,label:lbl}) => (
          <button key={id} className={`btn btn-sm ${mode===id?'btn-primary':'btn-secondary'}`} onClick={() => setMode(id)}>
            <Icon size={11} /> {lbl}
          </button>
        ))}
      </div>

      {mode === 'file' && (
        <div onClick={() => fileRef.current.click()} style={{ border:'2px dashed var(--border)', borderRadius:'var(--radius-sm)', padding:'16px', textAlign:'center', cursor:'pointer' }}
          onMouseEnter={e => e.currentTarget.style.borderColor=borderColor}
          onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}
        >
          <Upload size={18} style={{ color:'var(--text-muted)', marginBottom:'6px' }} />
          <div style={{ fontSize:'12px', color:'var(--text-secondary)' }}>Click to upload CSV or XLSX</div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px' }}>
            {partyType === 'adserver'
              ? 'Needs: Date, Impressions, Gross Revenue (Advertiser column optional for Advanced)'
              : 'Needs: Date, Impressions, Revenue'}
          </div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }} onChange={handleFile} />
        </div>
      )}

      {mode === 'url' && (
        <div>
          <DatePresetButtons dateFrom={urlDateFrom} dateTo={urlDateTo} onRange={(f,t) => { setUrlDateFrom(f); setUrlDateTo(t); }} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Date From</label>
              <input type="date" className="input-field" style={{ colorScheme:'dark', width:'100%' }}
                value={urlDateFrom} onChange={e => setUrlDateFrom(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Date To</label>
              <input type="date" className="input-field" style={{ colorScheme:'dark', width:'100%' }}
                value={urlDateTo} onChange={e => setUrlDateTo(e.target.value)} />
            </div>
          </div>
          {/* Date format selector */}
          <div style={{ marginBottom:'8px' }}>
            <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>
              API Date Format
              <span style={{ fontWeight:400, marginLeft:'6px', color:'var(--text-muted)' }}>
                — match the format your API expects
              </span>
            </label>
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
              {DATE_FORMATS.map(f => (
                <button key={f.value}
                  className={`btn btn-sm ${urlDateFmt === f.value ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize:'11px', padding:'3px 10px' }}
                  onClick={() => setUrlDateFmt(f.value)}
                  title={`e.g. ${f.example}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {urlDateFrom && (
              <div style={{ fontSize:'10px', color:'var(--cyan)', marginTop:'5px' }}>
                Preview: {formatDateForApi(urlDateFrom, urlDateFmt)} → {formatDateForApi(urlDateTo || urlDateFrom, urlDateFmt)}
              </div>
            )}
          </div>
          <input className="input-field w-full" style={{ marginBottom:'8px' }}
            placeholder="https://api.example.com/report?from={date_from}&to={date_to}"
            value={urlVal} onChange={e => setUrlVal(e.target.value)} />
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'8px' }}>
            Use <code style={{ fontFamily:'var(--font-mono)', color:'var(--cyan)' }}>{'{date_from}'}</code> and <code style={{ fontFamily:'var(--font-mono)', color:'var(--cyan)' }}>{'{date_to}'}</code> as placeholders in the URL.
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button className="btn btn-primary btn-sm" disabled={uploading || !urlVal}
              onClick={() => {
                const from = formatDateForApi(urlDateFrom, urlDateFmt);
                const to   = formatDateForApi(urlDateTo, urlDateFmt);
                const processedUrl = urlVal
                  .replace(/\{date_from\}/gi, from)
                  .replace(/\{date_to\}/gi,   to)
                  .replace(/\{start_date\}/gi, from)
                  .replace(/\{end_date\}/gi,   to);
                upload('url', processedUrl);
              }}>
              {uploading ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }} /> Fetching...</> : <><Globe size={11} /> Fetch & Upload</>}
            </button>
            {monitorId && urlVal && (
              <button className="btn btn-secondary btn-sm" disabled={savingSettings}
                title="Save this URL and date format so the monitor's Fetch Yesterday / schedule can use them"
                onClick={async () => {
                  setSavingSettings(true);
                  try {
                    await axios.patch(`/api/disc-monitor-v2/update/${monitorId}`, {
                      partySavedSettings: { [partyId]: { urlVal, urlDateFmt, configId: selectedConfigId, mode: 'url' } }
                    });
                    toast.success('URL & date format saved — will pre-fill next time');
                    onRefreshMonitor && onRefreshMonitor();
                  } catch { toast.error('Save failed'); }
                  finally { setSavingSettings(false); }
                }}>
                {savingSettings ? <><RefreshCw size={10} style={{ animation:'spin 1s linear infinite' }}/> Saving...</> : <><Save size={10}/> Save URL &amp; Date format</>}
              </button>
            )}
          </div>
        </div>
      )}

      {mode === 'saved' && (() => {
        const selectedCfg = savedConfigs.find(c => String(c.id) === String(selectedConfigId));
        const dims = (selectedCfg?.dimensions || []).filter(d => d.filterable !== false);
        const shape = resolveFilterShape(selectedCfg);

        function addFetchFilter() {
          setFetchFilters(prev => [...prev, { key: '', value: '', operator: defaultOperator(shape) }]);
        }
        function updateFetchFilter(fi, field, val) {
          setFetchFilters(prev => prev.map((x, xi) => xi === fi ? { ...x, [field]: val } : x));
        }
        function removeFetchFilter(fi) {
          setFetchFilters(prev => prev.filter((_, xi) => xi !== fi));
        }

        return (
          <div>
            {savedConfigs.length === 0 ? (
              <div className="alert alert-info">
                <Info size={12}/>
                No Partner API Configs saved yet. Go to Account → Partner API Configurations to add one.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>

                {/* Config selector */}
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Select Partner Config</label>
                  <select className="select-field" value={selectedConfigId}
                    onChange={e => {
                      setSelectedConfigId(e.target.value);
                      setFetchFilters([]);
                      setGrossRevenueField('');
                      setNetRevenueField('');
                      setDiscoveredColumns([]);
                    }}>
                    <option value="">— Choose a saved config —</option>
                    {savedConfigs
                      .filter(c => partyType === 'adserver' ? c.role === 'adserver' : c.role !== 'adserver')
                      .map(c => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
                  </select>
                </div>

                {/* Revenue field mapping — only shown for ad server */}
                {selectedCfg && partyType === 'adserver' && (
                  <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                    <div style={{ fontSize:'10px', color:'var(--cyan)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px', display:'flex', alignItems:'center', gap:'5px' }}>
                      Revenue Field Mapping
                      <span style={{ fontWeight:400, color:'var(--text-muted)', textTransform:'none', letterSpacing:0 }}>(for net vs gross split)</span>
                    </div>

                    {/* Step 1: Discover columns from API */}
                    {discoveredColumns.length === 0 ? (
                      <div>
                        <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px', lineHeight:1.5 }}>
                          First, fetch a sample response to discover the actual API field names available for revenue mapping.
                        </div>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize:'11px' }}
                          disabled={discoveringColumns || !selectedConfigId}
                          onClick={async () => {
                            setDiscoveringColumns(true);
                            try {
                              // Use yesterday as sample date (local tz)
                              const yd = localYesterday();
                              const customBody = buildBodyWithFilters(selectedCfg, []);
                              const { data } = await axios.post(`/api/partner-configs/${selectedConfigId}/fetch-raw`, {
                                date_from: yd, date_to: yd, custom_body: customBody,
                              });
                              const cols = data.columns || Object.keys(data.rows?.[0] || {});
                              if (cols.length > 0) {
                                setDiscoveredColumns(cols);
                                // Auto-set dates to yesterday so Fetch from API is immediately enabled
                                setConfigDateFrom(yd);
                                setConfigDateTo(yd);
                                // Auto-guess revenue fields from column names
                                const colsLower = cols.map(c => ({ key: c, l: c.toLowerCase() }));
                                const grossGuess = colsLower.find(c => c.l === 'revenue' || c.l === 'gross_revenue' || c.l === 'advertiser_spend')?.key || '';
                                const netGuess   = colsLower.find(c => c.l === 'supply_cost' || c.l === 'net_revenue' || c.l === 'publisher_revenue' || c.l === 'pub_revenue' || c.l === 'payout')?.key || '';
                                if (grossGuess && !grossRevenueField) setGrossRevenueField(grossGuess);
                                if (netGuess   && !netRevenueField)   setNetRevenueField(netGuess);
                                toast.success(`${cols.length} API fields discovered — dates set to yesterday, revenue fields pre-filled`);
                              } else {
                                toast.error('No columns returned — check the API config or try a different date');
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.error || 'Failed to fetch sample response');
                            } finally { setDiscoveringColumns(false); }
                          }}>
                          {discoveringColumns
                            ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Fetching fields...</>
                            : <><BarChart2 size={11}/> Discover API Fields</>}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize:'11px', color:'var(--green)', marginBottom:'8px', display:'flex', alignItems:'center', gap:'5px', justifyContent:'space-between' }}>
                          <span>✓ {discoveredColumns.length} fields from API</span>
                          <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:'10px', color:'var(--text-muted)', padding:0 }}
                            onClick={() => setDiscoveredColumns([])}>Re-discover</button>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                          <div>
                            <label style={{ fontSize:'10px', color:'var(--green)', display:'block', marginBottom:'3px', fontWeight:600 }}>Gross Revenue (vs Demand)</label>
                            <select className="select-field" style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }}
                              value={grossRevenueField}
                              onChange={e => setGrossRevenueField(e.target.value)}>
                              <option value="">— Use config default —</option>
                              {discoveredColumns.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize:'10px', color:'var(--purple)', display:'block', marginBottom:'3px', fontWeight:600 }}>Net Revenue (vs Supply)</label>
                            <select className="select-field" style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }}
                              value={netRevenueField}
                              onChange={e => setNetRevenueField(e.target.value)}>
                              <option value="">— Use config default —</option>
                              {discoveredColumns.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                        {(grossRevenueField || netRevenueField) && (
                          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'5px', lineHeight:1.5 }}>
                            {grossRevenueField && <div>Gross → <code style={{ fontFamily:'var(--font-mono)', color:'var(--green)' }}>{grossRevenueField}</code> · compared vs Demand</div>}
                            {netRevenueField && <div>Net → <code style={{ fontFamily:'var(--font-mono)', color:'var(--purple)' }}>{netRevenueField}</code> · compared vs Supply</div>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Date range with presets */}
                <div>
                  <DatePresetButtons dateFrom={configDateFrom} dateTo={configDateTo} onRange={(f,t) => { setConfigDateFrom(f); setConfigDateTo(t); }} />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div>
                      <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Date From</label>
                      <input type="date" className="input-field" style={{ colorScheme:'dark' }}
                        value={configDateFrom} onChange={e => setConfigDateFrom(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px', fontWeight:600 }}>Date To</label>
                      <input type="date" className="input-field" style={{ colorScheme:'dark' }}
                        value={configDateTo} onChange={e => setConfigDateTo(e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Filters — only shown when a config is selected */}
                {selectedCfg && (
                  <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--amber) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                    <div style={{ fontSize:'10px', color:'var(--amber)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px', display:'flex', alignItems:'center', gap:'5px' }}>
                      <Filter size={11}/> API Filters <span style={{ fontWeight:400, color:'var(--text-muted)', textTransform:'none', letterSpacing:0 }}>(optional — scope the data returned)</span>
                    </div>

                    {fetchFilters.map((f, fi) => {
                      const vals = f.value ? f.value.split(',').filter(v => v.trim()).length : 0;
                      const isMulti = vals > 1;
                      return (
                        <div key={fi} style={{ marginBottom:'6px' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 1fr auto', gap:'5px', alignItems:'center' }}>
                            <select className="select-field" style={{ fontSize:'11px' }}
                              value={f.key} onChange={e => updateFetchFilter(fi, 'key', e.target.value)}>
                              <option value="">— Select dimension —</option>
                              {dims.map(o => <option key={o.key} value={o.key}>{o.label} ({o.key})</option>)}
                              <option value="__custom__">Custom key...</option>
                            </select>
                            <select className="select-field" style={{ fontSize:'11px', padding:'4px 5px' }}
                              value={f.operator || defaultOperator(shape)}
                              onChange={e => updateFetchFilter(fi, 'operator', e.target.value)}>
                              {(() => {
                                // Filter the partner's operator vocabulary to what this specific
                                // dimension supports, if the dimension declares an `operators` list.
                                const dim = dims.find(d => d.key === f.key);
                                const allowedWires = dim?.operators;  // may be undefined
                                const vocab = selectedCfg?.operator_vocabulary || [];
                                const opts = allowedWires
                                  ? vocab.filter(op => allowedWires.includes(op.wire))
                                  : vocab;
                                // If partner has no vocab (e.g. legacy unmigrated config), fall back
                                // to a minimal set so the UI doesn't render empty.
                                if (opts.length === 0) {
                                  return <option value={defaultOperator(shape)}>{defaultOperator(shape)}</option>;
                                }
                                return opts.map(op => (
                                  <option key={op.wire} value={op.wire}>{op.display}</option>
                                ));
                              })()}
                            </select>
                            {f.key === '__custom__' ? (
                              <input className="input-field" placeholder="Type filter key"
                                style={{ fontSize:'11px' }}
                                value={f.customKey || ''} onChange={e => updateFetchFilter(fi, 'customKey', e.target.value)} />
                            ) : (
                              <input className="input-field" placeholder="Value — comma-separate for multiple"
                                style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }}
                                value={f.value} onChange={e => updateFetchFilter(fi, 'value', e.target.value)} />
                            )}
                            <button className="btn btn-danger btn-sm" onClick={() => removeFetchFilter(fi)}><Trash2 size={10}/></button>
                          </div>
                          {isMulti && (
                            <div style={{ fontSize:'9px', color:'var(--cyan)', marginTop:'2px', paddingLeft:'2px' }}>
                              ✓ {vals} values →{' '}
                              {(f.operator === '=' || f.operator === 'equals' || !f.operator)
                                ? <code style={{ fontFamily:'var(--font-mono)' }}>IN [...]</code>
                                : <><code style={{ fontFamily:'var(--font-mono)' }}>{f.operator}</code> applied to each</>
                              }
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div style={{ display:'flex', gap:'8px', alignItems:'center', marginTop:'6px' }}>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px' }} onClick={addFetchFilter}>
                        <Plus size={10}/> Add filter
                      </button>
                      {monitorId && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px', borderColor:'color-mix(in srgb, var(--cyan) 40%, transparent)', color:'var(--cyan)' }}
                          disabled={savingSettings}
                          title="Save filters + config selection so they pre-fill next time"
                          onClick={async () => {
                            setSavingSettings(true);
                            try {
                              await axios.patch(`/api/disc-monitor-v2/update/${monitorId}`, {
                                partySavedSettings: {
                                  [partyId]: {
                                    filters: fetchFilters,
                                    configId: selectedConfigId,
                                    grossRevenueField: grossRevenueField.trim(),
                                    netRevenueField: netRevenueField.trim(),
                                    discoveredColumns,
                                    mode: 'saved',
                                  }
                                }
                              });
                              toast.success('Filters & revenue mapping saved');
                              onRefreshMonitor && onRefreshMonitor();
                            } catch { toast.error('Save failed'); }
                            finally { setSavingSettings(false); }
                          }}>
                          {savingSettings ? <><RefreshCw size={10} style={{ animation:'spin 1s linear infinite' }}/> Saving...</> : <><Save size={10}/> Save Filters</>}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Fetch buttons */}
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  {(() => {
                    async function doFetch(dateFrom, dateTo) {
                      if (!selectedConfigId || !dateFrom || !dateTo)
                        return toast.error('Select a config and date range');
                      setFetchingConfig(true);
                      try {
                        const customBody = buildBodyWithFilters(selectedCfg, fetchFilters);
                        const { data } = await axios.post(`/api/partner-configs/${selectedConfigId}/fetch-raw`, {
                          date_from: dateFrom, date_to: dateTo, custom_body: customBody,
                        });
                        const rawRows = data.rows || [];
                        const normalizedRows = data.normalizedRows || [];
                        // Discover and save columns from the response if not already known
                        if (rawRows.length > 0 && discoveredColumns.length === 0) {
                          setDiscoveredColumns(data.columns || Object.keys(rawRows[0] || {}));
                        }
                        let rows;
                        if ((grossRevenueField || netRevenueField) && rawRows.length) {
                          const mapping = selectedCfg?.response_mapping || {};
                          const dateField  = mapping.date   || 'date';
                          const impsField  = mapping.impressions || 'impressions';
                          const advField   = mapping.advertiser  || null;
                          const grossField = grossRevenueField || mapping.revenue || mapping.gross_revenue || 'revenue';
                          const netField   = netRevenueField   || mapping.net_revenue || grossField;
                          rows = rawRows.map(r => ({
                            date:          String(r[dateField] ?? '').split('T')[0].trim(),
                            impressions:   parseFloat(r[impsField] ?? 0) || 0,
                            gross_revenue: parseFloat(r[grossField] ?? 0) || 0,
                            net_revenue:   parseFloat(r[netField]  ?? 0) || 0,
                            advertiser:    advField ? (r[advField] || '__ALL__') : '__ALL__',
                          })).filter(r => r.date);
                        } else {
                          rows = normalizedRows.length ? normalizedRows : rawRows;
                        }
                        if (!rows.length) {
                          const hint = data.rawResponse
                            ? `API returned 0 rows. Response: ${String(data.rawResponse).slice(0, 200)}`
                            : 'API returned no rows for this date range and filters';
                          return toast.error(hint, { duration: 8000 });
                        }
                        await upload('manual', null, rows.map(r => ({
                          date:          r.date,
                          impressions:   r.impressions,
                          gross_revenue: r.gross_revenue ?? r.revenue ?? 0,
                          net_revenue:   r.net_revenue   ?? r.revenue ?? 0,
                          advertiser:    r.advertiser || '__ALL__',
                        })));
                        toast.success(`✓ ${rows.length} rows fetched from ${data.configName || selectedCfg?.name}`);
                      } catch (e) {
                        const err = e.response?.data;
                        toast.error(err?.error || 'Fetch failed');
                        if (err?.hint) toast(err.hint, { icon: 'ℹ️', duration: 6000 });
                        if (err?.rawBody) toast(`API said: ${String(err.rawBody).slice(0, 200)}`, { icon: '⚠️', duration: 8000 });
                      } finally { setFetchingConfig(false); }
                    }
                    const yd = localYesterday();
                    return (<>
                      <button className="btn btn-primary btn-sm"
                        disabled={fetchingConfig || !selectedConfigId}
                        onClick={() => {
                          // Default to yesterday if no dates selected
                          const from = configDateFrom || yd;
                          const to   = configDateTo   || yd;
                          if (!configDateFrom) setConfigDateFrom(from);
                          if (!configDateTo)   setConfigDateTo(to);
                          doFetch(from, to);
                        }}>
                        {fetchingConfig
                          ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Fetching...</>
                          : <><Bookmark size={11}/> Fetch from API{(!configDateFrom || !configDateTo) ? ' (Yesterday)' : ''}</>}
                      </button>
                      {selectedConfigId && (
                        <button className="btn btn-secondary btn-sm"
                          disabled={fetchingConfig}
                          title="Fetch yesterday's data using the filters set above"
                          onClick={() => { setConfigDateFrom(yd); setConfigDateTo(yd); doFetch(yd, yd); }}>
                          <RefreshCw size={11}/> Fetch Yesterday's data
                        </button>
                      )}
                    </>);
                  })()}
                </div>{/* end fetch buttons div */}

              </div>
            )}
          </div>
        );
      })()}

      {mode === 'manual' && (
        <div>
          {partyType === 'adserver' && (
            <div style={{ fontSize:'11px', color:'var(--amber)', marginBottom:'8px', display:'flex', alignItems:'center', gap:'5px' }}>
              <AlertTriangle size={11}/> Advertiser name is required for each row — used to match data accurately across steps.
            </div>
          )}
          <div style={{ overflowX:'auto', marginBottom:'8px' }}>
            <table className="data-table" style={{ minWidth: partyType==='adserver' ? '600px' : '380px' }}>
              <thead><tr>
                <th style={{ minWidth:'110px' }}>Date</th>
                <th style={{ minWidth:'90px' }}>Imps</th>
                <th style={{ minWidth:'90px' }}>Gross Rev</th>
                {partyType==='adserver' && <th style={{ minWidth:'90px' }}>Net Rev</th>}
                {partyType==='adserver' && <th style={{ minWidth:'130px' }}>Advertiser *</th>}
                <th style={{width:28}}></th>
              </tr></thead>
              <tbody>
                {manualRows.map((row,i) => (
                  <tr key={i} style={{ background: partyType==='adserver' && !row.advertiser ? 'color-mix(in srgb, var(--amber) 5%, transparent)' : undefined }}>
                    <td><input type="date" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'100%', colorScheme:'var(--bg-base, dark)' }} value={row.date} onChange={e=>updateRow(i,'date',e.target.value)} /></td>
                    <td><input type="number" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'100%' }} value={row.impressions} onChange={e=>updateRow(i,'impressions',e.target.value)} /></td>
                    <td><input type="number" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'100%' }} step="0.01" value={row.gross_revenue} onChange={e=>updateRow(i,'gross_revenue',e.target.value)} /></td>
                    {partyType==='adserver' && <td><input type="number" className="input-field" style={{ padding:'3px 6px', fontSize:'11px', width:'100%' }} step="0.01" value={row.net_revenue} onChange={e=>updateRow(i,'net_revenue',e.target.value)} /></td>}
                    {partyType==='adserver' && (
                      <td>
                        <div style={{ display:'flex', gap:'3px' }}>
                          {detectedAdvertisers.length > 0 ? (
                            <select className="select-field" style={{ padding:'3px 6px', fontSize:'11px', flex:1,
                              border: !row.advertiser ? '1px solid var(--amber)' : '1px solid var(--border)' }}
                              value={row.advertiser} onChange={e=>updateRow(i,'advertiser',e.target.value)}>
                              <option value="">— Select advertiser —</option>
                              {detectedAdvertisers.map(a => <option key={a} value={a}>{a}</option>)}
                              <option value="__new__">+ Type new name...</option>
                            </select>
                          ) : null}
                          {(!detectedAdvertisers.length || row.advertiser === '__new__') && (
                            <input className="input-field" style={{ padding:'3px 6px', fontSize:'11px', flex:1,
                              border: !row.advertiser || row.advertiser==='__new__' ? '1px solid var(--amber)' : '1px solid var(--border)' }}
                              placeholder="Required — e.g. DV360" value={row.advertiser==='__new__'?'':row.advertiser}
                              onChange={e=>updateRow(i,'advertiser',e.target.value)} autoFocus={row.advertiser==='__new__'} />
                          )}
                        </div>
                        {!row.advertiser && <div style={{ fontSize:'9px', color:'var(--amber)', marginTop:'2px' }}>Required</div>}
                      </td>
                    )}
                    <td><button onClick={()=>removeRow(i)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', padding:'2px' }}><X size={12}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button className="btn btn-secondary btn-sm" onClick={addManualRow}><Plus size={12}/> Add Row</button>
            <button className="btn btn-primary btn-sm" disabled={uploading} onClick={() => {
              if (partyType === 'adserver') {
                const missing = manualRows.filter(r => r.date && !r.advertiser);
                if (missing.length > 0) {
                  toast.error(`Please fill in the Advertiser name for all ${missing.length} row(s). It is required for accurate analysis.`);
                  return;
                }
              }
              upload('manual', null, manualRows);
            }}>
              {uploading ? 'Uploading...' : <><Save size={11}/> Save Rows</>}
            </button>
          </div>
        </div>
      )}

      {uploading && <div style={{ marginTop:'8px', fontSize:'11px', color:'var(--cyan)' }}><RefreshCw size={10} style={{ animation:'spin 1s linear infinite', verticalAlign:'middle', marginRight:'5px' }} />Uploading & deduplicating...</div>}

      {/* Existing data viewer/editor */}
      {showExisting && (
        <div style={{ marginTop:'10px', borderTop:'1px solid var(--border)', paddingTop:'10px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
            <span style={{ fontSize:'11px', fontWeight:700, color:'var(--text-primary)' }}>Uploaded Data <span style={{ color:'var(--cyan)', fontWeight:400 }}>— click any row to load it into the manual entry above</span></span>
            <button className="btn btn-secondary btn-sm" disabled={loadingExisting} onClick={async () => {
              setLoadingExisting(true);
              try {
                const { data } = await axios.get(`/api/disc-monitor-v2/party-data/${monitorId}/${partyId}`);
                setExistingRows(data);
              } catch { toast.error('Failed to load data'); }
              finally { setLoadingExisting(false); }
            }}>
              <RefreshCw size={10} style={{ animation: loadingExisting ? 'spin 1s linear infinite' : 'none' }}/> {loadingExisting ? 'Loading...' : 'Load Rows'}
            </button>
          </div>
          {existingRows.length > 0 && (
            <div style={{ overflowX:'auto', maxHeight:'240px', overflowY:'auto' }}>
              <table className="data-table" style={{ fontSize:'11px' }}>
                <thead><tr>
                  <th>Date</th><th>Impressions</th><th>Gross Rev</th>
                  {partyType==='adserver' && <th>Net Rev</th>}
                  {existingRows.some(r => r.advertiser && r.advertiser !== '__ALL__') && <th>Advertiser</th>}
                  <th>Uploaded</th>
                </tr></thead>
                <tbody>
                  {existingRows.map((r,i) => {
                    const isEditing = editingRowIdx === i;
                    const hasAdv = existingRows.some(x => x.advertiser && x.advertiser !== '__ALL__');
                    return (
                      <React.Fragment key={i}>
                        <tr style={{ background: isEditing ? 'var(--bg-hover)' : undefined }}>
                          <td className="mono" style={{ color:'var(--cyan)', fontSize:'13px', fontWeight:600 }}>{r.date}</td>
                          <td className="mono">{Number(r.impressions).toLocaleString()}</td>
                          <td className="mono">${Number(r.gross_revenue).toFixed(2)}</td>
                          {partyType==='adserver' && <td className="mono">${Number(r.net_revenue).toFixed(2)}</td>}
                          {hasAdv && <td style={{ color:'var(--purple)', fontWeight:600 }}>{r.advertiser !== '__ALL__' ? r.advertiser : '—'}</td>}
                          <td>
                            {!isEditing ? (
                              <button onClick={() => {
                                setEditingRowIdx(i);
                                setEditingRowData({ date:r.date||''
                                  , impressions:r.impressions||''
                                  , gross_revenue:r.gross_revenue||''
                                  , net_revenue:r.net_revenue||''
                                  , advertiser:r.advertiser!=='__ALL__'?r.advertiser||'':'' });
                              }} style={{ background:'none', border:'1px solid var(--border)', borderRadius:'4px', padding:'2px 8px', cursor:'pointer', color:'var(--cyan)', fontSize:'10px' }}>
                                ✎ Edit
                              </button>
                            ) : (
                              <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                                <button onClick={() => { setEditingRowIdx(null); setEditingRowData(null); }}
                                  style={{ fontSize:'10px', padding:'2px 7px', background:'color-mix(in srgb, var(--amber) 12%, transparent)', border:'1px solid var(--amber)', borderRadius:'4px', cursor:'pointer', color:'var(--amber)', fontWeight:700 }}>
                                  ✕ Cancel
                                </button>
                                <button onClick={async () => {
                                  if (!window.confirm('⚠️ Confirm Edit\n\nThis will overwrite the existing data in the database with your edited values. The previous data cannot be retrieved once updated.\n\nProceed?')) return;
                                  try {
                                    await axios.post(`/api/disc-monitor-v2/upload/${monitorId}`, {
                                      partyId, partyType, inputType:'manual',
                                      manualRows:[editingRowData]
                                    });
                                    toast.success('Row updated');
                                    setEditingRowIdx(null); setEditingRowData(null);
                                    const { data } = await axios.get(`/api/disc-monitor-v2/party-data/${monitorId}/${partyId}`);
                                    setExistingRows(data);
                                    onUploaded && onUploaded({});
                                  } catch { toast.error('Update failed'); }
                                }} style={{ fontSize:'10px', padding:'2px 7px', background:'color-mix(in srgb, var(--cyan) 15%, transparent)', border:'1px solid var(--cyan)', borderRadius:'4px', cursor:'pointer', color:'var(--cyan)', fontWeight:700 }}>
                                  ✓ Confirm
                                </button>
                                <button onClick={async () => {
                                  if (!r.id) { toast.error('Row ID missing'); return; }
                                  if (!window.confirm('⚠️ Delete Row\n\nThis will permanently delete this row from the database. This action cannot be undone and the data cannot be retrieved once deleted.\n\nAre you sure?')) return;
                                  try {
                                    await axios.delete(`/api/disc-monitor-v2/row/${monitorId}`, { data:{ rowId: r.id } });
                                    toast.success('Row deleted');
                                    setEditingRowIdx(null); setEditingRowData(null);
                                    const { data } = await axios.get(`/api/disc-monitor-v2/party-data/${monitorId}/${partyId}`);
                                    setExistingRows(data);
                                    onUploaded && onUploaded({});
                                  } catch { toast.error('Delete failed'); }
                                }} style={{ fontSize:'10px', padding:'2px 7px', background:'color-mix(in srgb, var(--red) 12%, transparent)', border:'1px solid var(--red)', borderRadius:'4px', cursor:'pointer', color:'var(--red)', fontWeight:700 }}>
                                  🗑 Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {isEditing && (
                          <tr style={{ background:'var(--bg-surface)' }}>
                            <td colSpan={hasAdv ? (partyType==='adserver'?6:5) : (partyType==='adserver'?5:4)} style={{ padding:'8px 12px' }}>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', gap:'6px', alignItems:'center' }}>
                                <div>
                                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'2px', textTransform:'uppercase' }}>Date</div>
                                  <input type="date" className="input-field" style={{ fontSize:'11px', width:'100%', colorScheme:'var(--bg-base, dark)' }}
                                    value={editingRowData.date} onChange={e=>setEditingRowData(d=>({...d,date:e.target.value}))} />
                                </div>
                                <div>
                                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'2px', textTransform:'uppercase' }}>Impressions</div>
                                  <input type="number" className="input-field" style={{ fontSize:'11px', width:'100%' }}
                                    value={editingRowData.impressions} onChange={e=>setEditingRowData(d=>({...d,impressions:e.target.value}))} />
                                </div>
                                <div>
                                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'2px', textTransform:'uppercase' }}>Gross Rev</div>
                                  <input type="number" className="input-field" style={{ fontSize:'11px', width:'100%' }} step="0.01"
                                    value={editingRowData.gross_revenue} onChange={e=>setEditingRowData(d=>({...d,gross_revenue:e.target.value}))} />
                                </div>
                                {partyType==='adserver' && <div>
                                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'2px', textTransform:'uppercase' }}>Net Rev</div>
                                  <input type="number" className="input-field" style={{ fontSize:'11px', width:'100%' }} step="0.01"
                                    value={editingRowData.net_revenue} onChange={e=>setEditingRowData(d=>({...d,net_revenue:e.target.value}))} />
                                </div>}
                                <div>
                                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'2px', textTransform:'uppercase' }}>Advertiser</div>
                                  {detectedAdvertisers.length > 0 ? (
                                    <select className="select-field" style={{ fontSize:'11px', width:'100%' }}
                                      value={editingRowData.advertiser} onChange={e=>setEditingRowData(d=>({...d,advertiser:e.target.value}))}>
                                      <option value="">— Select —</option>
                                      {detectedAdvertisers.map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                  ) : (
                                    <input className="input-field" style={{ fontSize:'11px', width:'100%' }}
                                      value={editingRowData.advertiser} onChange={e=>setEditingRowData(d=>({...d,advertiser:e.target.value}))} />
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {existingRows.length === 0 && !loadingExisting && (
            <div style={{ fontSize:'11px', color:'var(--text-muted)', textAlign:'center', padding:'12px' }}>Click "Load Rows" to view uploaded data</div>
          )}
          <div style={{ marginTop:'8px', fontSize:'11px', color:'var(--text-muted)' }}>
            To update existing data, upload a new file or add manual rows above — they will overwrite matching dates automatically.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Missing Partners Modal ───────────────────────────────────────────────────
function MissingPartnersModal({ missing, monitorId, partners, onConfirm, onCancel }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onCancel}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'560px' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'var(--amber)', display:'flex', alignItems:'center', gap:'8px' }}>
            <AlertTriangle size={16} /> Missing Demand Partners Detected
          </div>
          <button onClick={onCancel} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px' }}>✕</button>
        </div>
        <div style={{ padding:'20px' }}>
          <p style={{ fontSize:'13px', color:'var(--text-secondary)', marginBottom:'14px', lineHeight:1.6 }}>
            The Ad Server report contains data for the following advertisers, but their corresponding Demand Partner reports have not been uploaded yet. Running without them may produce inaccurate totals.
          </p>
          <div style={{ marginBottom:'16px' }}>
            {missing.map((adv, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'6px' }}>
                <AlertTriangle size={12} style={{ color:'var(--amber)', flexShrink:0 }} />
                <span style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>{adv}</span>
                <span style={{ fontSize:'11px', color:'var(--text-muted)', marginLeft:'auto' }}>No report uploaded</span>
              </div>
            ))}
          </div>
          <div className="alert alert-info" style={{ marginBottom:'16px' }}>
            <Info size={12} />
            <span style={{ fontSize:'12px' }}>You can add their reports from the monitor's Upload section, or proceed and the discrepancy will reflect only the partners you've uploaded.</span>
          </div>
          <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={onCancel}>Go Back & Add Reports</button>
            <button className="btn btn-primary" onClick={onConfirm}>Run Anyway</button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ total, page, perPage, onPage }) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;
  const pages = [];
  // Show first, last, current ±2
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent:'center', padding:'12px 0', flexWrap:'wrap' }}>
      <button onClick={() => onPage(page - 1)} disabled={page === 1}
        style={{ padding:'4px 10px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-elevated)', color: page===1?'var(--text-muted)':'var(--text-primary)', cursor:page===1?'not-allowed':'pointer', fontSize:'12px' }}>
        ‹
      </button>
      {pages.map((p, i) => (
        <button key={i} onClick={() => p !== '...' && onPage(p)} disabled={p === '...'}
          style={{ padding:'4px 10px', borderRadius:'var(--radius-sm)', fontSize:'12px', cursor: p==='...'?'default':'pointer',
            border: p === page ? '1px solid var(--cyan)' : '1px solid var(--border)',
            background: p === page ? 'color-mix(in srgb, var(--cyan) 12%, transparent)' : 'var(--bg-elevated)',
            color: p === page ? 'var(--cyan)' : p==='...'?'var(--text-muted)':'var(--text-primary)',
            fontWeight: p === page ? 800 : 400 }}>
          {p}
        </button>
      ))}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages}
        style={{ padding:'4px 10px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-elevated)', color: page===totalPages?'var(--text-muted)':'var(--text-primary)', cursor:page===totalPages?'not-allowed':'pointer', fontSize:'12px' }}>
        ›
      </button>
      <span style={{ fontSize:'11px', color:'var(--text-muted)', marginLeft:'8px' }}>
        {((page-1)*perPage)+1}–{Math.min(page*perPage, total)} of {total.toLocaleString()} rows
      </span>
    </div>
  );
}

// ─── Basic Results ────────────────────────────────────────────────────────────
const ROWS_PER_PAGE = 50;

function BasicResults({ result, adServerName, monitorId, monitorType }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);
  const [advertiserData, setAdvertiserData] = useState({});
  const [loadingDate, setLoadingDate] = useState(null);
  // Bundle-level discrepancy (auto monitors only), keyed by date:
  // { [date]: { loading, error, data, cached, fetchedAt } }
  const [bundleData, setBundleData] = useState({});
  const isAutoMonitor = monitorType === 'auto_monitor';

  // Stored per-bundle rows, keyed by date. Separate from bundleData: that one
  // holds the auto-monitor's live API comparison, this one reads what was
  // uploaded. Sharing the key would make one overwrite the other.
  const [bundleBreak, setBundleBreak] = useState({});
  async function fetchBundleBreakdown(date) {
    setBundleBreak(prev => ({ ...prev, [date]: { loading: true } }));
    try {
      const { data } = await axios.get(
        `/api/disc-monitor-v2/monitors/${monitorId}/bundle-breakdown?date=${encodeURIComponent(date)}`);
      setBundleBreak(prev => ({ ...prev, [date]: { loading: false, data } }));
    } catch (e) {
      setBundleBreak(prev => ({ ...prev, [date]: { loading: false,
        error: e.response?.data?.error || 'Could not load the bundle breakdown' } }));
    }
  }

  async function fetchBundleDiscrepancy(date) {
    setBundleData(prev => ({ ...prev, [date]: { loading: true } }));
    try {
      const { data } = await axios.post(`/api/disc-monitor-v2/auto-monitors/bundle-discrepancy/${monitorId}`, { date });
      if (data.success) {
        setBundleData(prev => ({ ...prev, [date]: { loading:false, data, cached:data.cached, fetchedAt:data.fetchedAt } }));
      } else {
        setBundleData(prev => ({ ...prev, [date]: { loading:false, error: data.message || 'Could not fetch bundle data' } }));
      }
    } catch (e) {
      setBundleData(prev => ({ ...prev, [date]: { loading:false, error: e.response?.data?.error || 'Bundle fetch failed' } }));
    }
  }

  // Guard: old result_snapshots may not have rows (stored only type+dateRange)
  if (!result?.rows) return (
    <div style={{ padding:'16px', textAlign:'center', color:'var(--text-muted)', fontSize:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
      No detailed row data for this run — click <strong>Run Check</strong> to fetch fresh results.
    </div>
  );

  const s = result.summary;
  const filteredRows = result.rows.filter(r => (!dateFrom || r.date >= dateFrom) && (!dateTo || r.date <= dateTo));

  async function toggleDateRow(date) {
    if (expandedDate === date) { setExpandedDate(null); return; }
    setExpandedDate(date);
    if (advertiserData[date]) return; // already loaded
    if (!monitorId) return;
    setLoadingDate(date);
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/party-data/${monitorId}/adserver`);
      // Group by date → advertiser
      const byDate = {};
      data.forEach(row => {
        if (!byDate[row.date]) byDate[row.date] = {};
        const adv = row.advertiser || '__ALL__';
        if (!byDate[row.date][adv]) byDate[row.date][adv] = { impressions:0, gross_revenue:0, net_revenue:0 };
        byDate[row.date][adv].impressions   += parseFloat(row.impressions || 0);
        byDate[row.date][adv].gross_revenue += parseFloat(row.gross_revenue || 0);
        byDate[row.date][adv].net_revenue   += parseFloat(row.net_revenue || 0);
      });
      setAdvertiserData(prev => ({ ...prev, ...byDate }));
    } catch { toast.error('Failed to load advertiser breakdown'); }
    finally { setLoadingDate(null); }
  }

  function exportCSV() {
    const rows = [['Date','Ad Server Imps','Partners Total Imps','Imp Diff','Imp %','Ad Server Rev','Partners Total Rev','Rev Diff','Rev %','Status']];
    filteredRows.forEach(r => rows.push([r.date, r.adServerImps, r.partnerImps, r.impDiff, r.impPct, r.adServerRev, r.partnerRev, r.revDiff, r.revPct, r.severity]));
    const csv = rows.map(r => r.map(c => `"${String(c??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], {type:'text/csv'}));
    a.download = `basic-monitor-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
        {[
          { label:'Impressions', asVal:s.totalAdServerImps, ptVal:s.totalPartnerImps, diff:s.impDiff, pct:s.impPct, type:'integer' },
          { label:'Gross Revenue', asVal:s.totalAdServerRev, ptVal:s.totalPartnerRev, diff:s.revDiff, pct:s.revPct, type:'currency' },
        ].map(({ label, asVal, ptVal, diff, pct, type }) => {
          const sv = getSevStyle(pct);
          return (
            <div key={label} style={{ padding:'16px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:`1px solid ${sv.border}` }}>
              <div style={{ fontSize:'11px', fontWeight:800, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'10px' }}>{label}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                <div>
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:700, marginBottom:'3px' }}>{adServerName} (Total)</div>
                  <div style={{ fontSize:'15px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(asVal, type)}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:700, marginBottom:'3px' }}>All Partners (Sum)</div>
                  <div style={{ fontSize:'15px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(ptVal, type)}</div>
                </div>
              </div>
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:'10px', display:'flex', justifyContent:'center', alignItems:'baseline', gap:'8px' }}>
                <span style={{ fontSize:'26px', fontWeight:900, color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(pct)}</span>
                <span style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>({diff>0?'+':''}{fmt(diff,type)})</span>
              </div>
              <div style={{ textAlign:'center', fontSize:'11px', fontWeight:700, color:sv.text, marginTop:'4px' }}>
                {Math.abs(pct)<=5 ? 'OK — Less than 5%' : diff > 0 ? 'Ad Server Over-counting' : 'Ad Server Under-counting'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Date range + export */}
      <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'12px', flexWrap:'wrap', padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
        <Calendar size={13} style={{ color:'var(--text-primary)' }} />
        <span style={{ fontSize:'12px', fontWeight:600, color:'var(--text-primary)' }}>
          Data: <span style={{ color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>{result.dateRange?.from}</span> → <span style={{ color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>{result.dateRange?.to}</span>
        </span>
        <span style={{ color:'var(--text-muted)', fontSize:'11px', marginLeft:'8px' }}>Filter:</span>
        <input type="date" className="input-field" style={{ width:'150px', colorScheme:'dark' }} value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
        <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>→</span>
        <input type="date" className="input-field" style={{ width:'150px', colorScheme:'dark' }} value={dateTo} onChange={e=>setDateTo(e.target.value)} />
        {(dateFrom||dateTo) && <button className="btn btn-secondary btn-sm" onClick={()=>{setDateFrom('');setDateTo('');}}>✕ Clear</button>}
        <button className="btn btn-secondary btn-sm" style={{ marginLeft:'auto' }} onClick={exportCSV}><Download size={11}/> CSV</button>
      </div>

      {/* Monthly-grouped daily table (advertiser drill-down preserved per day) */}
      <MonthlyDateTable
        rows={filteredRows}
        keys={{ leftImps:'adServerImps', rightImps:'partnerImps', leftRev:'adServerRev', rightRev:'partnerRev' }}
        onRowClick={r => toggleDateRow(r.date)}
        rowExpandable={() => true}
        isRowExpanded={r => expandedDate === r.date}
        rowLoading={r => loadingDate === r.date}
        renderRowExpansion={r => {
          const advBreakdown = advertiserData[r.date];
          const hasAdvertiserData = advBreakdown && Object.keys(advBreakdown).some(k => k !== '__ALL__');
          return (
            <div style={{ padding:'12px 16px 14px 45px', borderBottom:'2px solid var(--border)' }}>
              {loadingDate === r.date ? (
                <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Loading advertiser breakdown...</div>
              ) : hasAdvertiserData ? (
                <>
                  <div style={{ fontSize:'11px', fontWeight:800, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>
                    📊 Advertiser Breakdown for {r.date}
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead>
                      <tr style={{ borderBottom:'1px solid var(--border)' }}>
                        {['Advertiser','Impressions','Gross Revenue','Net Revenue','% of Day Total'].map(h => (
                          <th key={h} style={{ padding:'5px 10px', textAlign:'left', fontWeight:700, color:'var(--text-primary)', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(advBreakdown)
                        .filter(([k]) => k !== '__ALL__')
                        .sort(([,a],[,b]) => b.impressions - a.impressions)
                        .map(([adv, data]) => {
                          const pct = r.adServerImps > 0 ? ((data.impressions / r.adServerImps)*100).toFixed(1) : 0;
                          return (
                            <tr key={adv} style={{ borderBottom:'1px solid var(--border)' }}>
                              <td style={{ padding:'6px 10px', fontWeight:600, color:'var(--purple)' }}>{adv}</td>
                              <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)' }}>{Number(data.impressions).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                              <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)' }}>${Number(data.gross_revenue).toFixed(2)}</td>
                              <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)' }}>${Number(data.net_revenue).toFixed(2)}</td>
                              <td style={{ padding:'6px 10px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                  <div style={{ flex:1, height:'6px', background:'var(--bg-elevated)', borderRadius:'3px', overflow:'hidden' }}>
                                    <div style={{ width:`${Math.min(pct,100)}%`, height:'100%', background:'var(--purple)', borderRadius:'3px' }}/>
                                  </div>
                                  <span style={{ fontFamily:'var(--font-mono)', fontSize:'11px', color:'var(--text-secondary)', minWidth:'38px' }}>{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </>
              ) : null}

              {/* ── Per-bundle breakdown from UPLOADED rows (any monitor type) ──
                  Distinct from the auto-monitor block above, which re-fetches the
                  APIs live. This reads monitor_report_bundles, so it works for
                  free-form monitors and for any number of partners. */}
              {!isAutoMonitor && (() => {
                const bb = bundleBreak[r.date] || {};
                const d = bb.data;
                const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
                return (
                  <div style={{ marginTop:'14px', paddingTop:'12px', borderTop:'1px dashed var(--border)' }}>
                    {!d && !bb.loading && !bb.error && (
                      <button className="btn btn-secondary btn-sm"
                        style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}
                        onClick={() => fetchBundleBreakdown(r.date)}>
                        {'\u{1F4E6}'} Bundle breakdown for {r.date}
                      </button>
                    )}
                    {bb.loading && (
                      <div style={{ fontSize:'12px', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'6px' }}>
                        <RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Loading stored bundle rows{'\u2026'}
                      </div>
                    )}
                    {bb.error && (
                      <div style={{ fontSize:'12px', color:'var(--amber)', display:'flex', alignItems:'center', gap:'8px', justifyContent:'space-between' }}>
                        <span>{'\u26A0'} {bb.error}</span>
                        <button className="btn btn-secondary btn-sm" onClick={() => fetchBundleBreakdown(r.date)}>Retry</button>
                      </div>
                    )}
                    {d && !d.hasData && (
                      <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>
                        {d.note || 'No bundle rows stored for this date.'}
                      </div>
                    )}
                    {d && d.hasData && (
                      <>
                        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'8px' }}>
                          <span style={{ fontSize:'11px', letterSpacing:'0.5px', textTransform:'uppercase', color:'var(--text-muted)' }}>
                            Bundle breakdown {'\u00b7'} {r.date}
                          </span>
                          <span style={{ fontSize:'11px', color:'var(--text-secondary)' }}>
                            {fmt(d.summary.bundles)} apps {'\u00b7'} {fmt(d.summary.inAll)} reported by everyone
                            {d.summary.partial ? ` \u00b7 ${fmt(d.summary.partial)} missing from at least one party` : ''}
                          </span>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:'9px', marginLeft:'auto' }}
                                  onClick={() => fetchBundleBreakdown(r.date)}>{'\u21BB'} Refresh</button>
                        </div>
                        <div style={{ overflowX:'auto', maxHeight:'420px', overflowY:'auto' }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
                            <thead>
                              <tr style={{ borderBottom:'1px solid var(--border)', textAlign:'left' }}>
                                <th style={{ padding:'6px 10px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>App bundle</th>
                                {d.parties.map(p => (
                                  <th key={p.partyId} colSpan={2}
                                      style={{ padding:'6px 10px', position:'sticky', top:0, background:'var(--bg-elevated)',
                                               color: p.partyType === 'adserver' ? 'var(--text-primary)' : 'var(--purple)' }}>
                                    {p.partyId === 'adserver' ? (adServerName || 'Ad Server') : p.partyId}
                                    {p.partyType === 'adserver' ? ' (baseline)' : ''}
                                  </th>
                                ))}
                              </tr>
                              <tr style={{ borderBottom:'1px solid var(--border)', textAlign:'left', color:'var(--text-muted)' }}>
                                <th style={{ padding:'4px 10px' }} />
                                {d.parties.map(p => (
                                  <React.Fragment key={p.partyId}>
                                    <th style={{ padding:'4px 10px', fontWeight:400 }}>Imps</th>
                                    <th style={{ padding:'4px 10px', fontWeight:400 }}>
                                      {p.partyId === d.adServerId ? 'Revenue' : '\u0394 vs baseline'}
                                    </th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {d.bundles.map(b => (
                                <tr key={b.bundle} style={{ borderBottom:'1px solid var(--border)' }}>
                                  <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)', maxWidth:'260px',
                                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={b.bundle}>
                                    {b.bundle}
                                    {b.missingFrom.length > 0 && (
                                      <span title={`Not reported by: ${b.missingFrom.join(', ')}`}
                                            style={{ marginLeft:'6px', color:'var(--amber)' }}>{'\u26A0'}</span>
                                    )}
                                  </td>
                                  {d.parties.map(p => {
                                    const v = b.parties[p.partyId];
                                    const dl = b.deltas[p.partyId];
                                    return (
                                      <React.Fragment key={p.partyId}>
                                        {/* An em dash, never 0 — a party that never reported this
                                            app is not a party reporting nothing, and a 0 here would
                                            read as a 100% discrepancy that does not exist. */}
                                        <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)',
                                                     color: v ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                          {v ? fmt(v.impressions) : '\u2014'}
                                        </td>
                                        <td style={{ padding:'6px 10px', fontFamily:'var(--font-mono)' }}>
                                          {p.partyId === d.adServerId
                                            ? (v ? `$${Number(v.grossRevenue).toFixed(2)}` : '\u2014')
                                            : (!dl || dl.impDelta === null
                                                ? <span style={{ color:'var(--text-muted)' }} title="This party did not report this app">{'\u2014'}</span>
                                                : <span style={{ color: dl.impDelta < 0 ? 'var(--red)' : dl.impDelta > 0 ? 'var(--green)' : 'var(--text-secondary)' }}>
                                                    {dl.impDelta > 0 ? '+' : ''}{fmt(dl.impDelta)}
                                                    {dl.impPct !== null ? ` (${dl.impPct > 0 ? '+' : ''}${dl.impPct}%)` : ''}
                                                  </span>)}
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* ── Bundle-level discrepancy (auto monitors only, on-demand) ── */}
              {isAutoMonitor && (() => {
                const bd = bundleData[r.date] || {};
                return (
                  <div style={{ marginTop:'14px', paddingTop:'12px', borderTop:'1px dashed var(--border)' }}>
                    {!bd.data && !bd.loading && (
                      <button className="btn btn-secondary btn-sm"
                        style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}
                        onClick={() => fetchBundleDiscrepancy(r.date)}>
                        📦 Fetch bundle-level discrepancy for {r.date}
                      </button>
                    )}
                    {bd.loading && (
                      <div style={{ fontSize:'12px', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'6px' }}>
                        <RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Fetching bundle data from both APIs…
                      </div>
                    )}
                    {bd.error && (
                      <div style={{ fontSize:'12px', color:'var(--amber)', display:'flex', alignItems:'center', gap:'6px', justifyContent:'space-between' }}>
                        <span>⚠ {bd.error}</span>
                        <button className="btn btn-secondary btn-sm" onClick={() => fetchBundleDiscrepancy(r.date)}>Retry</button>
                      </div>
                    )}
                    {bd.data && (
                      <>
                        <div style={{ fontSize:'11px', fontWeight:800, color:'var(--purple)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px', display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                          📦 Top {bd.data.rows.length} bundles by revenue gap — {r.date}
                          {bd.cached && <span style={{ fontSize:'9px', color:'var(--text-muted)', fontWeight:600, textTransform:'none' }}>cached · {new Date(bd.fetchedAt).toLocaleTimeString()}</span>}
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:'9px', marginLeft:'auto' }} onClick={() => fetchBundleDiscrepancy(r.date)}>↻ Refresh</button>
                        </div>
                        {bd.data.rows.length === 0 ? (
                          <div style={{ fontSize:'12px', color:'var(--text-muted)', fontStyle:'italic' }}>No overlapping bundles found for this date.</div>
                        ) : (
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
                            <thead><tr style={{ borderBottom:'1px solid var(--border)' }}>
                              {['Bundle ID',`${bd.data.adServerName} Imps`,`${bd.data.partnerName} Imps`,'Imp Δ',`${bd.data.adServerName} Rev`,`${bd.data.partnerName} Rev`,'Rev Δ'].map(h => (
                                <th key={h} style={{ padding:'5px 8px', textAlign:'left', fontWeight:700, color:'var(--text-primary)', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {bd.data.rows.map((br, bi) => (
                                  <tr key={bi} style={{ borderBottom:'1px solid var(--border)' }}>
                                    <td style={{ padding:'5px 8px', fontWeight:600, color:'var(--purple)', fontFamily:'var(--font-mono)' }}>
                                      {br.bundle}
                                      {br.onlyInAdServer && <span style={{ fontSize:'8px', color:'var(--amber)', marginLeft:'5px' }}>AD-ONLY</span>}
                                      {br.onlyInPartner && <span style={{ fontSize:'8px', color:'var(--cyan)', marginLeft:'5px' }}>PARTNER-ONLY</span>}
                                    </td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)' }}>{fmt(br.adServerImps,'integer')}</td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)' }}>{fmt(br.partnerImps,'integer')}</td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)', color:br.impDiff>0?'var(--amber)':'var(--green)' }}>{br.impDiff>0?'+':''}{fmt(br.impDiff,'integer')}</td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)' }}>{fmt(br.adServerRev,'currency')}</td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)' }}>{fmt(br.partnerRev,'currency')}</td>
                                    <td style={{ padding:'5px 8px', fontFamily:'var(--font-mono)', fontWeight:700, color:br.revDiff>0?'var(--amber)':'var(--green)' }}>{br.revDiff>0?'+':''}{fmt(br.revDiff,'currency')}</td>
                                  </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        }}
        columns={[
          { header:`${adServerName} Imps`, mono:true, cell:r=>fmt(r.adServerImps,'integer'), subtotal:g=>fmt(g.sumLeftImps,'integer') },
          { header:'Partners Total Imps', mono:true, cell:r=>fmt(r.partnerImps,'integer'), subtotal:g=>fmt(g.sumRightImps,'integer') },
          { header:'Imp Δ', mono:true,
            cell:r=><span style={{ color:r.impDiff>0?'var(--amber)':'var(--green)' }}>{r.impDiff>0?'+':''}{fmt(r.impDiff,'integer')}</span>,
            subtotal:g=><span style={{ color:g.impDiff>0?'var(--amber)':'var(--green)' }}>{g.impDiff>0?'+':''}{fmt(g.impDiff,'integer')}</span> },
          { header:'Imp %', cell:(r,sv)=><span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.impPct)}</span>,
            subtotal:(g,sv)=><span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.impPct)}</span> },
          { header:`${adServerName} Rev`, mono:true, cell:r=>fmt(r.adServerRev,'currency'), subtotal:g=>fmt(g.sumLeftRev,'currency') },
          { header:'Partners Total Rev', mono:true, cell:r=>fmt(r.partnerRev,'currency'), subtotal:g=>fmt(g.sumRightRev,'currency') },
          { header:'Rev Δ', mono:true,
            cell:r=><span style={{ color:r.revDiff>0?'var(--amber)':'var(--green)' }}>{r.revDiff>0?'+':''}{fmt(r.revDiff,'currency')}</span>,
            subtotal:g=><span style={{ color:g.revDiff>0?'var(--amber)':'var(--green)' }}>{g.revDiff>0?'+':''}{fmt(g.revDiff,'currency')}</span> },
          { header:'Rev %', cell:(r,sv)=><span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.revPct)}</span>,
            subtotal:(g,sv)=><span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.revPct)}</span> },
          { header:'Status', cell:(r,sv)=><span className={`badge ${sv.badge}`}>{sv.icon} {r.severity==='ok'?'OK < 5%':r.severity==='critical'?'Critical':'Warning'}</span>,
            subtotal:(g,sv)=><span className={`badge ${sv.badge}`}>{sv.icon} {g.severity}</span> },
        ]}
      />
    </div>
  );
}

// ─── Advanced Results ─────────────────────────────────────────────────────────
function AdvancedResults({ result, adServerName }) {
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [mapModal, setMapModal] = useState(null);
  if (!result?.partners) return (
    <div style={{ padding:'16px', textAlign:'center', color:'var(--text-muted)', fontSize:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
      No detailed data for this run — click <strong>Run Check</strong> to fetch fresh results.
    </div>
  );
  return (
    <div>
      {/* Unmatched partners warning */}
      {result.unmatchedPartners?.length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom:'14px' }}>
          <AlertTriangle size={13} />
          <div style={{ fontSize:'12px' }}>
            <strong>Could not auto-match {result.unmatchedPartners.length} partner(s):</strong>{' '}
            {result.unmatchedPartners.map(p => p.partnerName).join(', ')}.
            Use the "Map Manually" option in each partner to link them to an advertiser.
          </div>
        </div>
      )}

      {/* Summary row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'16px' }}>
        {[
          { label:'Partners Matched', value:`${result.summary.matchedPartners}/${result.summary.totalPartners}`, color:'var(--cyan)' },
          { label:'Critical', value:result.partnerResults.filter(p=>p.summary.severity==='critical').length, color:'var(--red)' },
          { label:'Warnings', value:result.partnerResults.filter(p=>p.summary.severity==='warning').length, color:'var(--amber)' },
        ].map(({label,value,color}) => (
          <div key={label} style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', textAlign:'center' }}>
            <div style={{ fontSize:'24px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{value}</div>
            <div style={{ fontSize:'11px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginTop:'4px' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Per-partner panels */}
      {result.partnerResults.map((pr, i) => {
        const sv = SEV[pr.summary.severity] || SEV.ok;
        const isExpanded = expandedPartner === pr.partnerId;
        return (
          <div key={i} style={{ background:sv.bg, border:`1px solid ${sv.border}`, borderRadius:'var(--radius)', marginBottom:'10px', overflow:'hidden' }}>
            <div onClick={() => setExpandedPartner(isExpanded ? null : pr.partnerId)}
              style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 16px', cursor:'pointer', background:'var(--bg-elevated)', borderBottom:isExpanded?'1px solid var(--border)':'none', flexWrap:'wrap' }}>
              {isExpanded ? <ChevronDown size={13} style={{ color:'var(--text-muted)' }}/> : <ChevronRight size={13} style={{ color:'var(--text-muted)'}}/>}
              <span style={{ fontWeight:800, fontSize:'14px', flex:1 }}>
                <span style={{ color:'var(--purple)' }}>{pr.partnerName}</span>
                <span style={{ color:'var(--text-muted)', fontSize:'11px', fontWeight:400, marginLeft:'8px' }}>→</span>
                <span style={{ color:'var(--cyan)', marginLeft:'8px' }}>
                  {pr.matchedAdvertisers?.length > 1
                    ? <>
                        {pr.matchedAdvertisers.map((a,i) => (
                          <span key={a}>
                            {i > 0 && <span style={{ color:'var(--text-muted)', margin:'0 4px' }}>+</span>}
                            {a}
                          </span>
                        ))}
                        <span style={{ fontSize:'9px', background:'color-mix(in srgb, var(--green) 15%, transparent)', color:'var(--green)', border:'1px solid color-mix(in srgb, var(--green) 30%, transparent)', borderRadius:'3px', padding:'1px 5px', marginLeft:'6px' }}>
                          {pr.matchedAdvertisers.length} combined
                        </span>
                      </>
                    : pr.matchedAdvertiser}
                </span>
                <span className={`badge ${pr.matchConfidence==='high'?'badge-green':pr.matchConfidence==='medium'?'badge-amber':'badge-muted'}`}
                  style={{ marginLeft:'8px', fontSize:'10px' }}>
                  {pr.matchConfidence==='high'?'✓ Auto-matched':pr.matchConfidence==='medium'?'~ Fuzzy match':'Manual'}
                </span>
              </span>
              <span className={`badge ${sv.badge}`}>{sv.icon} {pr.summary.severity}</span>
              <div style={{ display:'flex', gap:'6px' }}>
                {[{label:'Imp',pct:pr.summary.impPct},{label:'Rev',pct:pr.summary.revPct}].map(({label,pct}) => (
                  <div key={label} style={{ background:'var(--bg-base)', borderRadius:'6px', padding:'4px 10px', border:`1px solid ${getSevStyle(pct).border}`, textAlign:'center', minWidth:'70px' }}>
                    <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase' }}>{label}</div>
                    <div style={{ fontSize:'14px', fontWeight:900, color:getSevStyle(pct).text, fontFamily:'var(--font-mono)' }}>{fmtPct(pct)}</div>
                  </div>
                ))}
              </div>
            </div>
            {isExpanded && (
              <div style={{ padding:'14px 16px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  {[
                    { label:'Impressions', asVal:pr.summary.totalAdServerImps, ptVal:pr.summary.totalPartnerImps, pct:pr.summary.impPct, diff:pr.summary.impDiff, type:'integer' },
                    { label:'Gross Revenue', asVal:pr.summary.totalAdServerRev, ptVal:pr.summary.totalPartnerRev, pct:pr.summary.revPct, diff:pr.summary.revDiff, type:'currency' },
                  ].map(({label,asVal,ptVal,pct,diff,type}) => {
                    const sv2 = getSevStyle(pct);
                    return (
                      <div key={label} style={{ padding:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:`1px solid ${sv2.border}` }}>
                        <div style={{ fontSize:'11px', fontWeight:800, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>{label}</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px', marginBottom:'8px' }}>
                          <div><div style={{ fontSize:'10px', color:'var(--text-muted)' }}>{pr.matchedAdvertiser}</div><div style={{ fontSize:'14px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(asVal,type)}</div></div>
                          <div style={{ textAlign:'right' }}><div style={{ fontSize:'10px', color:'var(--text-muted)' }}>{pr.partnerName}</div><div style={{ fontSize:'14px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(ptVal,type)}</div></div>
                        </div>
                        <div style={{ borderTop:'1px solid var(--border)', paddingTop:'8px', textAlign:'center' }}>
                          <span style={{ fontSize:'22px', fontWeight:900, color:sv2.text, fontFamily:'var(--font-mono)' }}>{fmtPct(pct)}</span>
                          <span style={{ fontSize:'11px', color:'var(--text-muted)', marginLeft:'8px', fontFamily:'var(--font-mono)' }}>({diff>0?'+':''}{fmt(diff,type)})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <MonthlyDateTable
                  rows={pr.rows}
                  keys={{ leftImps:'adServerImps', rightImps:'partnerImps', leftRev:'adServerRev', rightRev:'partnerRev' }}
                  columns={[
                    { header:`${pr.matchedAdvertiser} Imps`, mono:true, cell:r=>fmt(r.adServerImps,'integer'), subtotal:g=>fmt(g.sumLeftImps,'integer') },
                    { header:`${pr.partnerName} Imps`, mono:true, cell:r=>fmt(r.partnerImps,'integer'), subtotal:g=>fmt(g.sumRightImps,'integer') },
                    { header:'Imp Δ', mono:true,
                      cell:r=><span style={{ color:(r.adServerImps-r.partnerImps)>0?'var(--amber)':'var(--green)' }}>{(r.adServerImps-r.partnerImps)>0?'+':''}{fmt(r.adServerImps-r.partnerImps,'integer')}</span>,
                      subtotal:g=><span style={{ color:g.impDiff>0?'var(--amber)':'var(--green)' }}>{g.impDiff>0?'+':''}{fmt(g.impDiff,'integer')}</span> },
                    { header:'Imp %', cell:(r,sv)=><span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.impPct)}</span>,
                      subtotal:(g,sv)=><span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.impPct)}</span> },
                    { header:`${pr.matchedAdvertiser} Rev`, mono:true, cell:r=>fmt(r.adServerRev,'currency'), subtotal:g=>fmt(g.sumLeftRev,'currency') },
                    { header:`${pr.partnerName} Rev`, mono:true, cell:r=>fmt(r.partnerRev,'currency'), subtotal:g=>fmt(g.sumRightRev,'currency') },
                    { header:'Rev Δ', mono:true,
                      cell:r=><span style={{ color:(r.adServerRev-r.partnerRev)>0?'var(--amber)':'var(--green)' }}>{(r.adServerRev-r.partnerRev)>0?'+':''}{fmt(r.adServerRev-r.partnerRev,'currency')}</span>,
                      subtotal:g=><span style={{ color:g.revDiff>0?'var(--amber)':'var(--green)' }}>{g.revDiff>0?'+':''}{fmt(g.revDiff,'currency')}</span> },
                    { header:'Rev %', cell:(r,sv)=><span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.revPct)}</span>,
                      subtotal:(g,sv)=><span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.revPct)}</span> },
                    { header:'Status', cell:(r,sv)=><span className={`badge ${sv.badge}`}>{sv.icon} {r.severity==='ok'?'OK < 5%':r.severity}</span>,
                      subtotal:(g,sv)=><span className={`badge ${sv.badge}`}>{sv.icon} {g.severity}</span> },
                  ]}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Edit Monitor Modal ───────────────────────────────────────────────────────
function EditMonitorModal({ monitor, onClose, onSaved }) {
  const isAuto = monitor.monitorType === 'auto_monitor';

  // Common state
  const [name, setName]               = useState(monitor.name || '');
  const [timezone, setTimezone]       = useState(monitor.timezone || 'UTC');
  const [adServerName, setAdServerName] = useState(monitor.adServer?.name || '');
  const [partners, setPartners]       = useState((monitor.partners || []).map(p => ({ ...p, savedConfigId: p.urlVal ? '__url__' : (p.savedConfigId || '') })));
  const [saving, setSaving]           = useState(false);
  const [showConfig, setShowConfig]   = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);

  // Auto-monitor specific
  const [adServerConfigId, setAdServerConfigId] = useState(monitor.adServer?.urlVal ? '__url__' : (monitor.adServer?.savedConfigId || ''));
  const [adServerUrl, setAdServerUrl] = useState(monitor.adServer?.urlVal || '');
  const [adServerUrlFmt, setAdServerUrlFmt] = useState(monitor.adServer?.urlDateFmt || 'YYYY-MM-DD');
  const [revenueMapping, setRevenueMapping] = useState(monitor.revenueMapping || { gross: '', net: '' });
  const [alertThreshold, setAlertThreshold]     = useState(String(monitor.alertThreshold || 20));
  const [runHourUtc, setRunHourUtc]             = useState(Number.isInteger(monitor.runHourUtc) ? monitor.runHourUtc : 8);
  const [notificationEmail, setNotificationEmail] = useState(monitor.notificationEmail || '');
  const [autoFilters, setAutoFilters]           = useState(monitor.autoFilters || { adserver: [] });

  const ROLE_COLORS = { supply:'var(--green)', demand:'var(--purple)', adserver:'var(--cyan)' };

  useEffect(() => {
    if (isAuto) {
      axios.get('/api/partner-configs').then(r => setSavedConfigs(r.data)).catch(() => {});
    }
  }, [isAuto]);

  async function removePartner(id) {
    const partner = partners.find(p => p.id === id);
    const isExisting = monitor.partners?.some(p => p.id === id);
    if (isExisting) {
      if (!window.confirm(`Remove "${partner?.name}" and permanently delete all their uploaded report data? This cannot be undone.`)) return;
      try {
        await axios.delete(`/api/disc-monitor-v2/${monitor.id}/partner/${id}`);
        toast.success(`Removed ${partner?.name} and deleted their data`);
      } catch (e) {
        toast.error(e.response?.data?.error || 'Failed to remove partner');
        return;
      }
    }
    setPartners(p => p.filter(x => x.id !== id));
  }

  async function handleSave() {
    if (!name.trim()) return toast.error('Monitor name required');
    if (!adServerName.trim()) return toast.error('Ad Server name required');
    if (partners.some(p => !p.name.trim())) return toast.error('All partners need a name');
    setSaving(true);
    try {
      const payload = {
        name, timezone,
        adServer: isAuto
          ? (adServerConfigId === '__url__'
              ? { ...monitor.adServer, name: adServerName, savedConfigId: '', inputType: 'url', urlVal: adServerUrl.trim(), urlDateFmt: adServerUrlFmt || 'YYYY-MM-DD' }
              : { ...monitor.adServer, name: adServerName, savedConfigId: adServerConfigId, urlVal: '', urlDateFmt: '', inputType: 'saved' })
          : { ...monitor.adServer, name: adServerName },
        partners: partners.map(p => isAuto ? normalizeAutoParty(p) : p),
      };
      if (isAuto) {
        payload.alertThreshold    = parseFloat(alertThreshold) || 20;
        payload.runHourUtc        = runHourUtc;
        payload.notificationEmail = notificationEmail;
        payload.autoFilters       = autoFilters;
        payload.revenueMapping    = revenueMapping;
      }
      await axios.patch(`/api/disc-monitor-v2/update/${monitor.id}`, payload);
      toast.success('Monitor updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  // Note: AutoMonitorFilterRows is used directly (top-level component) — NOT wrapped
  // in an inline component here, which would cause remount + focus loss on every keystroke.

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'800px', maxHeight:'95vh', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'8px' }}>
            <Edit2 size={15} style={{ color:'var(--cyan)' }} /> Edit Monitor: {monitor.name}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', padding:'20px', flex:1 }}>
          {/* Name + Timezone */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Monitor Name *</label>
              <input className="input-field w-full" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Timezone</label>
              <select className="select-field w-full" value={timezone} onChange={e => setTimezone(e.target.value)}>
                {['UTC','US/Eastern','US/Pacific','Europe/London','Europe/Berlin','Asia/Kolkata','Asia/Singapore','Asia/Tokyo'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>

          {/* Ad Server */}
          <div style={{ marginBottom:'14px', padding:'12px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
            <label style={{ fontSize:'10px', color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'8px', fontWeight:700 }}>● Ad Server (Baseline)</label>
            {isAuto ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Display Name</label>
                  <input className="input-field w-full" value={adServerName} onChange={e => setAdServerName(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Saved API Config</label>
                  <select className="select-field w-full" value={adServerConfigId} onChange={e => setAdServerConfigId(e.target.value)}>
                    <option value="">— Select config —</option>
                    {savedConfigs.filter(c => c.role === 'adserver').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__url__">Use API URL instead</option>
                  </select>
                </div>
                {adServerConfigId === '__url__' && (
                  <div style={{ gridColumn:'1 / -1' }}>
                    <UrlSourceInputs urlVal={adServerUrl} urlDateFmt={adServerUrlFmt} onUrl={setAdServerUrl} onFmt={setAdServerUrlFmt} />
                  </div>
                )}
                <div style={{ gridColumn:'1 / -1' }}>
                  <RevenueMetricPanel hasSupply={partners.some(p => p.role === 'supply')} hasDemand={partners.some(p => p.role === 'demand')} mapping={revenueMapping} onChange={setRevenueMapping} savedConfigId={adServerConfigId} savedConfigs={savedConfigs} filters={(autoFilters && autoFilters.adserver) || []} />
                </div>
              </div>
            ) : (
              <input className="input-field w-full" placeholder="Ad Server name" value={adServerName} onChange={e => setAdServerName(e.target.value)} />
            )}
          </div>

          {/* Partners */}
          <div style={{ marginBottom:'14px' }}>
            <label style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'8px', fontWeight:700 }}>
              Partners <span style={{ color:'var(--cyan)', fontWeight:400, textTransform:'none', letterSpacing:0 }}>— add or remove</span>
            </label>
            {partners.map((p, i) => (
              <div key={p.id} style={{ marginBottom:'8px' }}>
                <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                  {['freeform','auto_monitor'].includes(monitor.monitorType) && (
                    <select className="select-field" style={{ width:'100px', fontSize:'11px', flexShrink:0, color:ROLE_COLORS[p.role||'demand'] }}
                      value={p.role || 'demand'} onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,role:e.target.value} : x))}>
                      <option value="supply">Supply</option>
                      <option value="demand">Demand</option>
                    </select>
                  )}
                  <input className="input-field" style={{ flex:1 }} placeholder={`Partner ${i+1} name`}
                    value={p.name} onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,name:e.target.value} : x))} />
                  {isAuto && (
                    <select className="select-field" style={{ width:'160px', fontSize:'11px', flexShrink:0 }}
                      value={p.savedConfigId || ''} onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,savedConfigId:e.target.value} : x))}>
                      <option value="">— Saved config —</option>
                      {savedConfigs.filter(c => c.role !== 'adserver').map(c => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
                      <option value="__url__">Use API URL instead</option>
                    </select>
                  )}
                  <button className="btn btn-danger btn-sm" onClick={() => removePartner(p.id)}><Trash2 size={11}/></button>
                </div>
                {isAuto && p.savedConfigId === '__url__' && (
                  <UrlSourceInputs urlVal={p.urlVal} urlDateFmt={p.urlDateFmt}
                    onUrl={v => setPartners(prev => prev.map(x => x.id===p.id ? {...x,urlVal:v} : x))}
                    onFmt={v => setPartners(prev => prev.map(x => x.id===p.id ? {...x,urlDateFmt:v} : x))} />
                )}
              </div>
            ))}
            <div style={{ display:'flex', gap:'6px', marginTop:'6px' }}>
              {['freeform','auto_monitor'].includes(monitor.monitorType) ? (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPartners(p => [...p, { id:`partner_${Date.now()}`, name:'', role:'supply', inputType: isAuto ? 'saved' : 'file' }])} style={{ borderColor:'color-mix(in srgb, var(--green) 40%, transparent)', color:'var(--green)' }}><Plus size={12}/> Supply</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPartners(p => [...p, { id:`partner_${Date.now()}`, name:'', role:'demand', inputType: isAuto ? 'saved' : 'file' }])} style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}><Plus size={12}/> Demand</button>
                </>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={() => setPartners(p => [...p, { id:`partner_${Date.now()}`, name:'', inputType:'file' }])}><Plus size={12}/> Add Partner</button>
              )}
            </div>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'6px' }}>✦ Removing an existing partner permanently deletes their uploaded report data.</div>
          </div>

          {/* View config JSON — available for all types */}
          {!isAuto && (
            <div>
              <button onClick={() => setShowConfig(s => !s)} style={{ display:'flex', alignItems:'center', gap:'6px', background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 12px', cursor:'pointer', fontSize:'11px', color:'var(--text-muted)', width:'100%' }}>
                {showConfig ? <ChevronDown size={12}/> : <ChevronRight size={12}/>} View Full Config (JSON)
              </button>
              {showConfig && (
                <pre style={{ marginTop:'8px', padding:'12px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', fontSize:'10px', fontFamily:'var(--font-mono)', color:'var(--text-secondary)', overflow:'auto', maxHeight:'180px', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                  {JSON.stringify({ name:monitor.name, monitorType:monitor.monitorType, adServer:monitor.adServer, partners:monitor.partners, revenueMapping:monitor.revenueMapping, timezone:monitor.timezone }, null, 2)}
                </pre>
              )}
            </div>
          )}

          {/* Auto-monitor extra fields */}
          {isAuto && (
            <>
              {/* Alert settings */}
              <div style={{ marginBottom:'12px', padding:'12px', background:'color-mix(in srgb, var(--green-soft) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--green-soft) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                <div style={{ fontSize:'10px', color:'var(--green-soft)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:700, marginBottom:'10px', display:'flex', alignItems:'center', gap:'5px' }}>
                  <Bell size={11}/> Alert Settings
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Critical Alert Threshold (%)</label>
                    <input type="number" className="input-field w-full" min="1" max="100" value={alertThreshold} onChange={e => setAlertThreshold(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Daily Run Time (UTC)</label>
                    <select className="input-field w-full" value={runHourUtc} onChange={e => setRunHourUtc(parseInt(e.target.value, 10))}>
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC{h === 8 ? ' (default)' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Alert Email</label>
                    <input type="email" className="input-field w-full" placeholder="alerts@yourcompany.com" value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* API Filters */}
              <div style={{ marginBottom:'12px', padding:'12px', background:'color-mix(in srgb, var(--amber) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                <div style={{ fontSize:'10px', color:'var(--amber)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:700, marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                  <Filter size={11}/> API Filters (Optional)
                </div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'10px', lineHeight:1.5 }}>
                  Injected into each party's API request on every run. Comma-separate multiple values.
                </div>
                <AutoMonitorFilterRows
                  partyId="adserver" partyLabel="Ad Server" partyColor="var(--cyan)"
                  autoFilters={autoFilters} setAutoFilters={setAutoFilters}
                  savedConfigs={savedConfigs} configId={adServerConfigId}
                />
                {partners.filter(p=>p.name.trim()).map(p => (
                  <AutoMonitorFilterRows
                    key={p.id}
                    partyId={p.id} partyLabel={p.name} partyColor={p.role==='supply' ? 'var(--green)' : 'var(--purple)'}
                    autoFilters={autoFilters} setAutoFilters={setAutoFilters}
                    savedConfigs={savedConfigs} configId={p.savedConfigId}
                  />
                ))}
              </div>

              {/* Config inspector */}
              <div>
                <button onClick={() => setShowConfig(s => !s)} style={{ display:'flex', alignItems:'center', gap:'6px', background:'none', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 12px', cursor:'pointer', fontSize:'11px', color:'var(--text-muted)', width:'100%' }}>
                  {showConfig ? <ChevronDown size={12}/> : <ChevronRight size={12}/>} View Full Config (JSON)
                </button>
                {showConfig && (
                  <pre style={{ marginTop:'8px', padding:'12px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', fontSize:'10px', fontFamily:'var(--font-mono)', color:'var(--text-secondary)', overflow:'auto', maxHeight:'180px', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                    {JSON.stringify({ name:monitor.name, monitorType:monitor.monitorType, adServer:monitor.adServer, partners:monitor.partners, scheduleEnabled:monitor.scheduleEnabled, alertThreshold:monitor.alertThreshold, notificationEmail:monitor.notificationEmail, autoFilters:monitor.autoFilters, lastRun:monitor.lastRun }, null, 2)}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:'8px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : <><Save size={13}/> Save Changes</>}</button>
        </div>
      </div>
    </div>
  );
}
// ─── Bundle File Reader ───────────────────────────────────────────────────────
function BundleUploadSlot({ label, color, onChange }) {
  const fileRef = React.useRef(null);
  const [fileName, setFileName] = React.useState(null);
  async function handleFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    let inputType, data;
    if (ext === 'xlsx' || ext === 'xls') { inputType='xlsx'; data=await readAsBase64(file); }
    else { inputType='csv'; data=await readAsText(file); }
    setFileName(file.name);
    onChange({ inputType, data });
    toast.success(`Loaded: ${file.name}`);
  }
  return (
    <div style={{ background:'var(--bg-elevated)', border:`1px solid ${color}`, borderRadius:'var(--radius-sm)', padding:'10px' }}>
      <div style={{ fontSize:'11px', fontWeight:700, color, marginBottom:'8px' }}>{label}</div>
      <div onClick={() => fileRef.current.click()} style={{ border:'1px dashed var(--border)', borderRadius:'var(--radius-sm)', padding:'10px', textAlign:'center', cursor:'pointer', fontSize:'11px', color: fileName ? 'var(--green)' : 'var(--text-muted)' }}
        onMouseEnter={e=>e.currentTarget.style.borderColor=color}
        onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}
      >
        {fileName ? `✓ ${fileName}` : '+ Upload CSV or XLSX'}
        <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px' }}>Needs: Bundle ID, Impressions, Revenue</div>
      </div>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }} onChange={handleFile} />
    </div>
  );
}

// ─── Bundle Results Table ─────────────────────────────────────────────────────
function BundleResults({ result, adServerName, partnerName }) {
  const { rows: rawRows, summary } = result;
  const DISPLAY_LIMIT = 50;

  // Sort by absolute revenue diff descending — Revenue is what everyone chases
  const rows = [...rawRows].sort((a, b) => Math.abs(b.revDiff) - Math.abs(a.revDiff));
  const tooManyRows = rows.length > DISPLAY_LIMIT;

  function exportCSV() {
    const csvRows = [['Bundle ID','Ad Server Imps','Partner Imps','Imp Diff','Imp %','Ad Server Rev','Partner Rev','Rev Diff','Rev %','Status']];
    rows.forEach(r => csvRows.push([r.bundle,r.adServerImps,r.partnerImps,r.impDiff,r.impPct,r.adServerRev,r.partnerRev,r.revDiff,r.revPct,r.severity]));
    const csv = csvRows.map(r=>r.map(c=>`"${String(c??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download=`bundle-discrepancy-${partnerName}-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  return (
    <div style={{ marginTop:'12px' }}>
      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', marginBottom:'12px' }}>
        {[
          {label:'Total Bundles', value:summary.totalBundles, color:'var(--cyan)'},
          {label:'Critical', value:summary.critical, color:'var(--red)'},
          {label:'Warning', value:summary.warning, color:'var(--amber)'},
          {label:'OK < 5%', value:summary.ok, color:'var(--green)'},
        ].map(({label,value,color}) => (
          <div key={label} style={{ padding:'10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', textAlign:'center' }}>
            <div style={{ fontSize:'20px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{value}</div>
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* > 50 rows: prompt download instead of rendering table */}
      {tooManyRows ? (
        <div style={{ padding:'20px', background:'color-mix(in srgb, var(--cyan) 5%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius)', textAlign:'center' }}>
          <Download size={28} style={{ color:'var(--cyan)', marginBottom:'10px' }} />
          <div style={{ fontWeight:700, fontSize:'14px', color:'var(--text-primary)', marginBottom:'6px' }}>
            {rows.length} bundles analysed — too many to display inline
          </div>
          <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'16px', lineHeight:1.6 }}>
            Displaying {rows.length} rows would slow the dashboard. Download the full report as CSV — sorted by highest revenue discrepancy.
          </div>
          <button className="btn btn-primary" onClick={exportCSV} style={{ fontSize:'14px', padding:'10px 28px' }}>
            <Download size={14}/> Download Full Bundle Report ({rows.length} bundles)
          </button>
          <div style={{ marginTop:'10px', fontSize:'11px', color:'var(--text-muted)' }}>
            {summary.critical} critical · {summary.warning} warnings · sorted by highest revenue discrepancy
          </div>
        </div>
      ) : (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>Sorted by highest revenue discrepancy (descending)</div>
            <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={11}/> CSV</button>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table className="data-table">
              <thead><tr>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>Bundle ID</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>{adServerName} Imps</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>{partnerName} Imps</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>Imp Diff</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>Imp %</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>{adServerName} Rev</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>{partnerName} Rev</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>Rev %</th>
                <th style={{ color:'var(--text-primary)', fontWeight:800 }}>Status</th>
              </tr></thead>
              <tbody>
                {rows.map((r,i) => {
                  const sv = SEV[r.severity]||SEV.ok;
                  return (
                    <tr key={i} style={{ background: r.severity!=='ok'?sv.bg:undefined }}>
                      <td style={{ fontFamily:'var(--font-mono)', fontSize:'12px', fontWeight:600 }}>
                        {r.bundle}
                        {r.onlyInAdServer && <span className="badge badge-amber" style={{ marginLeft:6, fontSize:9 }}>Ad Server only</span>}
                        {r.onlyInPartner  && <span className="badge badge-purple" style={{ marginLeft:6, fontSize:9 }}>Partner only</span>}
                      </td>
                      <td className="mono">{fmt(r.adServerImps,'integer')}</td>
                      <td className="mono">{fmt(r.partnerImps,'integer')}</td>
                      <td className="mono" style={{ color:r.impDiff>0?'var(--amber)':'var(--green)', fontSize:'12px' }}>{r.impDiff>0?'+':''}{fmt(r.impDiff,'integer')}</td>
                      <td><span style={{ fontWeight:800, color:sv.text, fontSize:'13px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.impPct)}</span></td>
                      <td className="mono">{fmt(r.adServerRev,'currency')}</td>
                      <td className="mono">{fmt(r.partnerRev,'currency')}</td>
                      <td><span style={{ fontWeight:800, color:sv.text, fontSize:'13px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.revPct)}</span></td>
                      <td><span className={`badge ${sv.badge}`}>{sv.icon} {r.severity==='ok'?'OK < 5%':r.severity}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Partner Quick Panel — searchable, grouped, accordion partner list ────────
function PartnerQuickPanel({ monitor, dataStatus, onUploaded, onRefreshMonitor }) {
  const [search, setSearch] = useState('');
  const [activePartyId, setActivePartyId] = useState(null);

  // Determine role of each partner based on monitorType
  function getPartnerRole(p) {
    if (p.role) return p.role; // explicit role (freeform, auto_monitor)
    if (monitor.monitorType === 'supply') return 'supply';
    return 'demand';
  }

  const allParties = [
    { id: 'adserver', name: monitor.adServer?.name || 'Ad Server', role: 'adserver', partyType: 'adserver' },
    ...(monitor.partners || []).map(p => ({
      id: p.id,
      name: p.name,
      role: getPartnerRole(p),
      partyType: 'partner',
    })),
  ];

  const filtered = allParties.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Group by role
  const GROUP_ORDER = { adserver: 0, supply: 1, demand: 2 };
  const groupMap = {};
  filtered.forEach(p => {
    const g = p.role === 'adserver' ? 'Ad Server'
      : p.role === 'supply' ? 'Supply Partners'
      : 'Demand Partners';
    if (!groupMap[g]) groupMap[g] = { parties: [], role: p.role };
    groupMap[g].parties.push(p);
  });
  const groups = Object.entries(groupMap).sort((a, b) => {
    const ra = GROUP_ORDER[a[1].role] ?? 99;
    const rb = GROUP_ORDER[b[1].role] ?? 99;
    return ra - rb;
  });

  const roleColor = r => r === 'adserver' ? 'var(--cyan)' : r === 'supply' ? 'var(--green)' : 'var(--purple)';

  return (
    <div>
      {/* Search bar */}
      <div style={{ position:'relative', marginBottom:'10px' }}>
        <Search size={13} style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', pointerEvents:'none' }} />
        <input
          className="input-field w-full"
          style={{ paddingLeft:'30px', fontSize:'12px' }}
          placeholder="Search partners..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', padding:'2px' }}>
            <X size={12}/>
          </button>
        )}
      </div>

      {groups.map(([groupName, { parties, role }]) => (
        <div key={groupName} style={{ marginBottom:'10px' }}>
          {/* Group header */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', paddingLeft:'2px',
            borderLeft:`3px solid ${roleColor(role)}`, paddingLeft:'8px' }}>
            <span style={{ fontSize:'11px', fontWeight:800, color:roleColor(role), textTransform:'uppercase', letterSpacing:'1.5px' }}>
              {groupName}
            </span>
            <span style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>
              {parties.filter(p => dataStatus[p.id]).length}/{parties.length} with data
            </span>
          </div>

          {/* Party rows */}
          {parties.map(party => {
            const status = dataStatus[party.id];
            const isActive = activePartyId === party.id;
            const color = roleColor(party.role);

            return (
              <div key={party.id} style={{ marginBottom:'4px' }}>
                {/* Compact row */}
                <div
                  onClick={() => setActivePartyId(isActive ? null : party.id)}
                  style={{
                    display:'flex', alignItems:'center', gap:'8px',
                    padding:'8px 12px',
                    background: isActive ? 'var(--bg-elevated)' : 'var(--bg-surface)',
                    border:`1px solid ${isActive ? color : status ? 'color-mix(in srgb, var(--green) 20%, transparent)' : 'var(--border)'}`,
                    borderRadius:'var(--radius-sm)', cursor:'pointer',
                    transition:'all 0.12s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = color; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = status ? 'color-mix(in srgb, var(--green) 20%, transparent)' : 'var(--border)'; }}
                >
                  {/* Status dot */}
                  <div style={{
                    width:8, height:8, borderRadius:'50%', flexShrink:0,
                    background: status ? 'var(--green)' : 'var(--border)',
                    boxShadow: status ? '0 0 5px color-mix(in srgb, var(--green) 50%, transparent)' : 'none',
                  }} />

                  {/* Name */}
                  <span style={{ flex:1, fontSize:'13px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {party.name}
                  </span>

                  {/* Status info */}
                  {status ? (
                    <span style={{ fontSize:'10px', color:'var(--green)', background:'color-mix(in srgb, var(--green) 8%, transparent)', border:'1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius:'4px', padding:'2px 7px', whiteSpace:'nowrap', flexShrink:0 }}>
                      ✓ {Number(status.rowCount).toLocaleString()} rows · {status.dateFrom?.slice?.(0,10)} → {status.dateTo?.slice?.(0,10)}
                    </span>
                  ) : (
                    <span style={{ fontSize:'10px', color:'var(--text-muted)', flexShrink:0 }}>No data</span>
                  )}

                  {/* Toggle arrow */}
                  <span style={{ fontSize:'12px', color: isActive ? color : 'var(--text-muted)', flexShrink:0, fontWeight:700 }}>
                    {isActive ? '↑' : '↓'}
                  </span>
                </div>

                {/* Expanded upload panel — only one open at a time */}
                {isActive && (
                  <div style={{ marginTop:'3px', marginBottom:'6px', borderLeft:`2px solid ${color}`, paddingLeft:'8px' }}>
                    <UploadPanel
                      label={party.name}
                      color={color}
                      partyId={party.id}
                      monitorId={monitor.id}
                      partyType={party.partyType}
                      partyRole={party.role}
                      existingStatus={status}
                      onUploaded={onUploaded}
                      monitor={monitor}
                      onRefreshMonitor={onRefreshMonitor}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {filtered.length === 0 && (
        <div style={{ textAlign:'center', padding:'20px', fontSize:'12px', color:'var(--text-muted)' }}>
          No partners match "{search}"
        </div>
      )}
    </div>
  );
}

// ─── Free Form Results (Supply → AdServer → Demand) ───────────────────────────
// ─── Deductions ───────────────────────────────────────────────────────────────
// Per party, per day, with a reason. Fetched from their own authenticated
// endpoint and applied over the top of the results — never merged into the
// computed result, which is what the public share route copies.
function DeductionsPanel({ monitorId, parties, dates, deductions, onChanged, onClose }) {
  const [date, setDate] = useState(dates?.[0] || '');
  const [partyId, setPartyId] = useState(parties?.[0]?.id || '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const nameOf = (id) => (parties || []).find(p => String(p.id) === String(id))?.name || id;

  async function save() {
    setBusy(true); setErr(null);
    try {
      await axios.put(`/api/disc-monitor-v2/monitors/${monitorId}/deductions`,
        { date, partyId, amount: Number(amount), reason });
      setAmount(''); setReason('');
      await onChanged();
      toast.success('Deduction saved');
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); }
    setBusy(false);
  }

  async function edit(d) {
    setDate(d.report_date); setPartyId(d.party_id);
    setAmount(String(d.amount)); setReason(d.reason || '');
  }

  async function remove(id) {
    try {
      await axios.delete(`/api/disc-monitor-v2/monitors/${monitorId}/deductions/${id}`);
      await onChanged();
    } catch (e) { setErr(e.response?.data?.error || 'Could not delete'); }
  }

  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)',
                  padding:'12px', marginBottom:'12px', background:'var(--bg-card)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
        <span style={{ fontSize:'12px', fontWeight:700 }}>Revenue deductions</span>
        <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
          Visible to your team only — never included in a shared link
        </span>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft:'auto' }} onClick={onClose}>Close</button>
      </div>

      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'flex-end', marginBottom:'10px' }}>
        <div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'3px' }}>Date</div>
          <select className="select-field" value={date} onChange={e => setDate(e.target.value)}
                  style={{ fontSize:'11px', padding:'4px 6px' }}>
            {(dates || []).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'3px' }}>Party</div>
          <select className="select-field" value={partyId} onChange={e => setPartyId(e.target.value)}
                  style={{ fontSize:'11px', padding:'4px 6px' }}>
            {(parties || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'3px' }}>Amount</div>
          <input className="input-field" value={amount} onChange={e => setAmount(e.target.value)}
                 placeholder="0.00" inputMode="decimal"
                 style={{ fontSize:'11px', padding:'4px 6px', width:'90px' }} />
        </div>
        <div style={{ flex:'1 1 180px' }}>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginBottom:'3px' }}>Reason</div>
          <input className="input-field" value={reason} onChange={e => setReason(e.target.value)}
                 placeholder="Make-good, invalid traffic credit…"
                 style={{ fontSize:'11px', padding:'4px 6px', width:'100%', boxSizing:'border-box' }} />
        </div>
        <button className="btn btn-primary btn-sm" disabled={busy || !date || !partyId} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      {err && <div style={{ fontSize:'11px', color:'var(--amber)', marginBottom:'8px' }}>{err}</div>}

      {(deductions || []).length === 0
        ? <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>No deductions recorded.</div>
        : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
            <thead>
              <tr style={{ textAlign:'left', color:'var(--text-muted)', borderBottom:'1px solid var(--border)' }}>
                <th style={{ padding:'4px 6px' }}>Date</th>
                <th style={{ padding:'4px 6px' }}>Party</th>
                <th style={{ padding:'4px 6px', textAlign:'right' }}>Amount</th>
                <th style={{ padding:'4px 6px' }}>Reason</th>
                <th style={{ padding:'4px 6px' }}>Updated</th>
                <th style={{ padding:'4px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {deductions.map(d => (
                <tr key={d.id} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:'4px 6px', fontFamily:'var(--font-mono)' }}>{d.report_date}</td>
                  <td style={{ padding:'4px 6px' }}>{nameOf(d.party_id)}</td>
                  <td style={{ padding:'4px 6px', textAlign:'right', fontFamily:'var(--font-mono)', color:'var(--amber)' }}>
                    −${Number(d.amount).toFixed(2)}
                  </td>
                  <td style={{ padding:'4px 6px', maxWidth:'240px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                      title={d.reason || ''}>{d.reason || '—'}</td>
                  <td style={{ padding:'4px 6px', color:'var(--text-muted)' }}>{d.updated_at}</td>
                  <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize:'9px' }} onClick={() => edit(d)}>Edit</button>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize:'9px', marginLeft:'4px' }} onClick={() => remove(d.id)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

function FreeFormResults({ result, monitorName, monitorId, partyNames }) {
  // party_id -> the name the user typed. The breakdown endpoint reads stored rows
  // and only knows ids, but the monitor object already carries {id, name, role} —
  // so the mapping lives here rather than needing a backend lookup.
  // Deductions live outside the computed result, so they are fetched separately
  // and applied over the top. The share route never sees them.
  const [deductions, setDeductions] = useState([]);
  const [showDeductions, setShowDeductions] = useState(false);
  const [applyDeductions, setApplyDeductions] = useState(true);
  const loadDeductions = useCallback(async () => {
    if (!monitorId) return;
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/monitors/${monitorId}/deductions`);
      setDeductions(data.deductions || []);
    } catch { setDeductions([]); }
  }, [monitorId]);
  useEffect(() => { loadDeductions(); }, [loadDeductions]);

  // (date, role) -> total deducted. Role decides WHICH COLUMN it adjusts: supply
  // is the left of Supply-vs-Ad-Server, demand the right of Ad-Server-vs-Demand.
  // monitor.partners holds only supply/demand partners, so the ad server could
  // never be deducted against. 'adserver' is the party_id monitor_report_data
  // already uses for it, so the two agree without a translation step.
  // LAZY, not a const. adServerName is destructured from `result` further down,
  // and `const` is not hoisted — building this eagerly here threw "Cannot access
  // 'adServerName' before initialization" and blanked the page. A function defers
  // the read to call time, by which point the destructure has run.
  const buildDeductionParties = () => [
    { id: 'adserver', name: adServerName || 'Ad Server', role: 'adserver' },
    ...(partyNames || []).map(p => ({ id: p.id, name: p.name, role: p.role })),
  ];
  // roleOf is only ever called during render, so it is safe to resolve lazily too.
  const roleOf = (pid) => {
    if (String(pid) === 'adserver') return 'adserver';
    return (partyNames || []).find(p => String(p.id) === String(pid))?.role || null;
  };
  const deductionBy = {};
  deductions.forEach(d => {
    const role = roleOf(d.party_id);
    if (!role) return;
    const k = `${d.report_date}|${role}`;
    deductionBy[k] = (deductionBy[k] || 0) + (Number(d.amount) || 0);
  });
  const deductionFor = (date, role) => (applyDeductions ? (deductionBy[`${date}|${role}`] || 0) : 0);
  const deductionTotal = deductions.reduce((a, d) => a + (Number(d.amount) || 0), 0);

  const partyNameOf = (partyId, adServerNm) => {
    if (partyId === 'adserver') return adServerNm || 'Ad Server';
    const hit = (partyNames || []).find(p => String(p.id) === String(partyId));
    return (hit && hit.name) || partyId;
  };
  if (!result?.supplyVsAdServer && !result?.adServerVsDemand) return (
    <div style={{ padding:'16px', textAlign:'center', color:'var(--text-muted)', fontSize:'12px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
      No detailed data for this run — click <strong>Run Check</strong> to fetch fresh results.
    </div>
  );
  const { supplyVsAdServer, adServerVsDemand, adServerName, supplyPartnerNames, demandPartnerNames } = result;

  // Per-bundle rows for one date, across every party. Rendered from stored upload
  // rows, so it works for free-form monitors and for any number of partners.
  function renderBundleBreakdown(date, bb, onRetry) {
    const d = bb.data;
    const fmtN = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (bb.loading) return (
      <div style={{ padding:'12px 16px', fontSize:'12px', color:'var(--text-muted)' }}>
        Loading bundle rows for {date}{'\u2026'}
      </div>
    );
    if (bb.error) return (
      <div style={{ padding:'12px 16px', fontSize:'12px', color:'var(--amber)', display:'flex', gap:'10px', alignItems:'center' }}>
        <span>{'\u26A0'} {bb.error}</span>
        <button className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>
      </div>
    );
    // Everything that is not the ad server, in response order.
    const partnerIds = d && d.parties ? d.parties.filter(p => p.partyId !== d.adServerId).map(p => p.partyId) : [];
    if (!d) return null;
    if (!d.hasData) return (
      <div style={{ padding:'12px 16px', fontSize:'12px', color:'var(--text-muted)' }}>
        {d.note || 'No bundle rows stored for this date.'}
      </div>
    );
    return (
      <div style={{ padding:'10px 16px 14px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'8px' }}>
          <span style={{ fontSize:'10px', letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--text-muted)' }}>
            Bundle breakdown
          </span>
          <span style={{ fontSize:'11px', color:'var(--text-secondary)' }}>
            {fmtN(d.summary.bundles)} apps {'\u00b7'} {fmtN(d.summary.inAll)} reported by everyone
            {d.summary.partial ? ` \u00b7 ${fmtN(d.summary.partial)} missing from at least one party` : ''}
          </span>
        </div>
        <div style={{ overflowX:'auto', maxHeight:'420px', overflowY:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)', textAlign:'left' }}>
                <th style={{ padding:'5px 10px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>App bundle</th>
                <th colSpan={2} style={{ padding:'5px 10px', position:'sticky', top:0, background:'var(--bg-elevated)' }}>
                  {adServerName || 'Ad Server'}
                </th>
                {partnerIds.map(pid => (
                  <th key={pid} colSpan={2} style={{ padding:'5px 10px', position:'sticky', top:0, background:'var(--bg-elevated)', color:'var(--purple)' }}>
                    {partyNameOf(pid, adServerName)}
                  </th>
                ))}
                {/* The discrepancy is the partners COMBINED against the ad server —
                    the same comparison the panel above this table reports, and it
                    reports BOTH metrics. Revenue is usually the larger gap. */}
                <th colSpan={4} style={{ padding:'5px 10px', position:'sticky', top:0, background:'var(--bg-elevated)', color:'var(--green)' }}>
                  All partners ({partnerIds.length}) vs {adServerName || 'Ad Server'}
                </th>
              </tr>
              <tr style={{ borderBottom:'1px solid var(--border)', textAlign:'left', color:'var(--text-muted)' }}>
                <th style={{ padding:'3px 10px' }} />
                <th style={{ padding:'3px 10px', fontWeight:400 }}>Imps</th>
                <th style={{ padding:'3px 10px', fontWeight:400 }}>Revenue</th>
                {partnerIds.map(pid => (
                  <React.Fragment key={pid}>
                    <th style={{ padding:'3px 10px', fontWeight:400 }}>Imps</th>
                    <th style={{ padding:'3px 10px', fontWeight:400 }}>Revenue</th>
                  </React.Fragment>
                ))}
                <th style={{ padding:'3px 10px', fontWeight:400 }}>Total imps</th>
                <th style={{ padding:'3px 10px', fontWeight:400 }}>{'\u0394'} imps</th>
                <th style={{ padding:'3px 10px', fontWeight:400 }}>Total rev</th>
                <th style={{ padding:'3px 10px', fontWeight:400 }}>{'\u0394'} rev</th>
              </tr>
            </thead>
            <tbody>
              {d.bundles.map(b => {
                const ad = b.parties[d.adServerId];
                // Partners SUMMED. A bundle split across two partners reconciles
                // only in aggregate; comparing each separately reported two
                // alarming halves for inventory that actually adds up.
                let partnerTotal = 0, partnerRev = 0, anyPartner = false;
                partnerIds.forEach(pid => {
                  const v = b.parties[pid];
                  if (v) {
                    partnerTotal += Number(v.impressions) || 0;
                    partnerRev   += Number(v.grossRevenue) || 0;
                    anyPartner = true;
                  }
                });
                const adImps = ad ? Number(ad.impressions) || 0 : null;
                const adRev  = ad ? Number(ad.grossRevenue) || 0 : null;
                const delta = (anyPartner && adImps !== null) ? partnerTotal - adImps : null;
                const pct = (delta !== null && adImps > 0) ? Math.round((delta / adImps) * 1000) / 10 : null;
                const revDelta = (anyPartner && adRev !== null) ? Math.round((partnerRev - adRev) * 100) / 100 : null;
                // Guard the divide: a bundle with impressions but no ad-server
                // revenue is real, and a percentage there would be meaningless.
                const revPct = (revDelta !== null && adRev > 0) ? Math.round((revDelta / adRev) * 1000) / 10 : null;
                return (
                <tr key={b.bundle} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)', maxWidth:'260px',
                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={b.bundle}>
                    {b.bundle}
                    {b.missingFrom.length > 0 && (
                      <span title={`Not reported by: ${b.missingFrom.map(id => partyNameOf(id, adServerName)).join(', ')}`}
                            style={{ marginLeft:'6px', color:'var(--amber)' }}>{'\u26A0'}</span>
                    )}
                  </td>
                  {/* Em dash, never 0 — a party that never reported this app is not a
                      party reporting nothing, and 0 would read as a 100% discrepancy
                      that does not exist. */}
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)',
                               color: ad ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {ad ? fmtN(ad.impressions) : '\u2014'}
                  </td>
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)' }}>
                    {ad ? `$${Number(ad.grossRevenue).toFixed(2)}` : '\u2014'}
                  </td>
                  {partnerIds.map(pid => {
                    const v = b.parties[pid];
                    return (
                      <React.Fragment key={pid}>
                        <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)',
                                     color: v ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {v ? fmtN(v.impressions) : '\u2014'}
                        </td>
                        <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)',
                                     color: v ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {v ? `$${Number(v.grossRevenue).toFixed(2)}` : '\u2014'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)', fontWeight:600,
                               color: anyPartner ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {anyPartner ? fmtN(partnerTotal) : '\u2014'}
                  </td>
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)' }}>
                    {delta === null
                      ? <span style={{ color:'var(--text-muted)' }}
                              title={!anyPartner ? 'No partner reported this app' : 'The ad server did not report this app'}>{'\u2014'}</span>
                      : <span style={{ color: Math.abs(pct || 0) >= 20 ? 'var(--red)' : Math.abs(pct || 0) >= 5 ? 'var(--amber)' : 'var(--green)' }}>
                          {delta > 0 ? '+' : ''}{fmtN(delta)}
                          {pct !== null ? ` (${pct > 0 ? '+' : ''}${pct}%)` : ''}
                        </span>}
                  </td>
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)', fontWeight:600,
                               color: anyPartner ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {anyPartner ? `$${partnerRev.toFixed(2)}` : '\u2014'}
                  </td>
                  <td style={{ padding:'5px 10px', fontFamily:'var(--font-mono)' }}>
                    {revDelta === null
                      ? <span style={{ color:'var(--text-muted)' }}
                              title={!anyPartner ? 'No partner reported this app' : 'The ad server did not report this app'}>{'\u2014'}</span>
                      : <span style={{ color: Math.abs(revPct || 0) >= 20 ? 'var(--red)' : Math.abs(revPct || 0) >= 5 ? 'var(--amber)' : 'var(--green)' }}>
                          {revDelta > 0 ? '+' : ''}${Math.abs(revDelta).toFixed(2)}
                          {revPct !== null ? ` (${revPct > 0 ? '+' : ''}${revPct}%)` : ''}
                        </span>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function ComparisonPanel({ data, leftLabel, rightLabel, leftColor, rightColor, title, icon,
                            leftRole = null, rightRole = null }) {
    // BOTH sides carry a role now. The ad server is the RIGHT column of
    // Supply-vs-Ad-Server and the LEFT of Ad-Server-vs-Demand, so a single role
    // per panel would have silently dropped every ad-server deduction.
    const adj = (r, which) => {
      const role = which === 'left' ? leftRole : rightRole;
      return role ? deductionFor(r.date, role) : 0;
    };
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    // Per-bundle rows for an expanded day, keyed by date. MonthlyDateTable already
    // supports row expansion — this panel simply never passed the props, so its
    // day rows were plain.
    const [openDate, setOpenDate] = useState(null);
    const [bundleBreak, setBundleBreak] = useState({});
    async function loadBundles(date) {
      if (bundleBreak[date]) return;
      setBundleBreak(prev => ({ ...prev, [date]: { loading: true } }));
      try {
        const { data: d } = await axios.get(
          `/api/disc-monitor-v2/monitors/${monitorId}/bundle-breakdown?date=${encodeURIComponent(date)}`);
        setBundleBreak(prev => ({ ...prev, [date]: { loading: false, data: d } }));
      } catch (e) {
        setBundleBreak(prev => ({ ...prev, [date]: { loading: false,
          error: e.response?.data?.error || 'Could not load the bundle breakdown' } }));
      }
    }
    if (!data) return null;
    const s = data.summary;
    const overallSev = SEV[s.severity] || SEV.ok;

    const leftImpsKey = Object.keys(s).find(k => k.includes('Imps') && !k.toLowerCase().includes('diff'));
    const rightImpsKey = Object.keys(s).filter(k => k.includes('Imps') && !k.toLowerCase().includes('diff'))[1];
    const leftRevKey = Object.keys(s).find(k => k.includes('Rev') && !k.toLowerCase().includes('diff'));
    const rightRevKey = Object.keys(s).filter(k => k.includes('Rev') && !k.toLowerCase().includes('diff'))[1];

    // Key names in row objects
    const rowLeftImps = Object.keys(data.rows[0] || {}).find(k => k.includes('Imps') && !k.includes('diff') && !k.includes('Pct'));
    const rowRightImps = Object.keys(data.rows[0] || {}).filter(k => k.includes('Imps') && !k.includes('diff') && !k.includes('Pct'))[1];
    const rowLeftRev = Object.keys(data.rows[0] || {}).find(k => k.includes('Rev') && !k.includes('diff') && !k.includes('Pct'));
    const rowRightRev = Object.keys(data.rows[0] || {}).filter(k => k.includes('Rev') && !k.includes('diff') && !k.includes('Pct'))[1];

    return (
      <div style={{ background:'var(--bg-elevated)', border:`1px solid ${overallSev.border}`, borderRadius:'var(--radius)', marginBottom:'14px', overflow:'hidden' }}>
        {/* Panel header */}
        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
            {icon}
            <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>{title}</span>
            <span style={{ fontSize:'11px', color:leftColor, fontWeight:700 }}>{leftLabel}</span>
            <ArrowRight size={12} style={{ color:'var(--text-muted)' }}/>
            <span style={{ fontSize:'11px', color:rightColor, fontWeight:700 }}>{rightLabel}</span>
          </div>
          <span className={`badge ${overallSev.badge}`}>{overallSev.icon} {s.severity}</span>
        </div>

        {/* Summary cards */}
        <div style={{ padding:'12px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
            {[
              { label:'Impressions', leftVal: s[leftImpsKey], rightVal: s[rightImpsKey], pct: s.impPct, diff: s.impDiff, type:'integer' },
              { label:'Gross Revenue', leftVal: s[leftRevKey], rightVal: s[rightRevKey], pct: s.revPct, diff: s.revDiff, type:'currency' },
            ].map(({ label, leftVal, rightVal, pct, diff, type }) => {
              const sv2 = getSevStyle(pct);
              return (
                <div key={label} style={{ padding:'12px', background:'var(--bg-base)', borderRadius:'var(--radius-sm)', border:`1px solid ${sv2.border}` }}>
                  <div style={{ fontSize:'10px', fontWeight:800, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>{label}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px', marginBottom:'8px' }}>
                    <div><div style={{ fontSize:'10px', color:leftColor }}>{leftLabel}</div><div style={{ fontSize:'13px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(leftVal, type)}</div></div>
                    <div style={{ textAlign:'right' }}><div style={{ fontSize:'10px', color:rightColor }}>{rightLabel}</div><div style={{ fontSize:'13px', fontWeight:800, fontFamily:'var(--font-mono)' }}>{fmt(rightVal, type)}</div></div>
                  </div>
                  <div style={{ borderTop:'1px solid var(--border)', paddingTop:'8px', textAlign:'center' }}>
                    <span style={{ fontSize:'22px', fontWeight:900, color:sv2.text, fontFamily:'var(--font-mono)' }}>{fmtPct(pct)}</span>
                    <span style={{ fontSize:'10px', color:'var(--text-muted)', marginLeft:'8px', fontFamily:'var(--font-mono)' }}>({diff>0?'+':''}{fmt(diff, type)})</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats row */}
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'10px' }}>
            {[
              { label:'Critical Dates', v:s.critical, color:'var(--red)' },
              { label:'Warning Dates', v:s.warning, color:'var(--amber)' },
              { label:'OK Dates', v:s.ok, color:'var(--cyan)' },
            ].map(({label,v,color}) => (
              <div key={label} style={{ flex:'1 1 60px', padding:'6px 10px', background:'var(--bg-base)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', textAlign:'center' }}>
                <div style={{ fontSize:'16px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{v}</div>
                <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase', marginTop:'2px' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Date filter + monthly-grouped table (Δ columns + month subtotals) */}
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
          <MonthlyDateTable
            rows={data.rows}
            keys={{ leftImps: rowLeftImps, rightImps: rowRightImps, leftRev: rowLeftRev, rightRev: rowRightRev }}
            dateFrom={dateFrom}
            dateTo={dateTo}
            columns={[
              { header: `${leftLabel} Imps`, mono:true, thStyle:{ color:leftColor },
                cell: r => fmt(r[rowLeftImps],'integer'),
                subtotal: g => fmt(g.sumLeftImps,'integer') },
              { header: `${rightLabel} Imps`, mono:true, thStyle:{ color:rightColor },
                cell: r => fmt(r[rowRightImps],'integer'),
                subtotal: g => fmt(g.sumRightImps,'integer') },
              { header: 'Imp Δ', mono:true,
                cell: r => <span style={{ color:(r[rowLeftImps]-r[rowRightImps])>0?'var(--amber)':'var(--green)' }}>{(r[rowLeftImps]-r[rowRightImps])>0?'+':''}{fmt(r[rowLeftImps]-r[rowRightImps],'integer')}</span>,
                subtotal: g => <span style={{ color:g.impDiff>0?'var(--amber)':'var(--green)' }}>{g.impDiff>0?'+':''}{fmt(g.impDiff,'integer')}</span> },
              { header: 'Imp %',
                cell: (r,sv) => <span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.impPct)}</span>,
                subtotal: (g,sv) => <span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.impPct)}</span> },
              { header: `${leftLabel} Rev`, mono:true, thStyle:{ color:leftColor },
                cell: r => {
                  const d = adj(r, 'left');
                  return d
                    ? <span title={`Reported ${fmt(r[rowLeftRev],'currency')} · deducted ${fmt(d,'currency')}`}>
                        {fmt(r[rowLeftRev] - d,'currency')}
                        <span style={{ color:'var(--amber)', fontSize:'9px', marginLeft:'3px' }}>adj</span>
                      </span>
                    : fmt(r[rowLeftRev],'currency');
                },
                subtotal: g => fmt(g.sumLeftRev,'currency') },
              { header: `${rightLabel} Rev`, mono:true, thStyle:{ color:rightColor },
                cell: r => {
                  const d = adj(r, 'right');
                  return d
                    ? <span title={`Reported ${fmt(r[rowRightRev],'currency')} · deducted ${fmt(d,'currency')}`}>
                        {fmt(r[rowRightRev] - d,'currency')}
                        <span style={{ color:'var(--amber)', fontSize:'9px', marginLeft:'3px' }}>adj</span>
                      </span>
                    : fmt(r[rowRightRev],'currency');
                },
                subtotal: g => fmt(g.sumRightRev,'currency') },
              { header: 'Rev Δ', mono:true,
                cell: r => {
                  // The delta must use the SAME figures as the two columns beside
                  // it, or the row stops adding up.
                  const dv = (r[rowLeftRev] - adj(r,'left')) - (r[rowRightRev] - adj(r,'right'));
                  return <span style={{ color: dv > 0 ? 'var(--amber)' : 'var(--green)' }}>{dv > 0 ? '+' : ''}{fmt(dv,'currency')}</span>;
                },
                subtotal: g => <span style={{ color:g.revDiff>0?'var(--amber)':'var(--green)' }}>{g.revDiff>0?'+':''}{fmt(g.revDiff,'currency')}</span> },
              { header: 'Rev %',
                cell: (r,sv) => <span style={{ fontWeight:800, color:sv.text, fontSize:'12px', fontFamily:'var(--font-mono)' }}>{fmtPct(r.revPct)}</span>,
                subtotal: (g,sv) => <span style={{ color:sv.text, fontFamily:'var(--font-mono)' }}>{fmtPct(g.revPct)}</span> },
              { header: 'Status',
                cell: (r,sv) => <span className={`badge ${sv.badge}`}>{sv.icon} {r.severity}</span>,
                subtotal: (g,sv) => <span className={`badge ${sv.badge}`}>{sv.icon} {g.severity}</span> },
            ]}
            rowExpandable={() => !!monitorId}
            isRowExpanded={(r) => openDate === r.date}
            rowLoading={(r) => !!(bundleBreak[r.date] && bundleBreak[r.date].loading)}
            onRowClick={(r) => {
              if (openDate === r.date) { setOpenDate(null); return; }
              setOpenDate(r.date);
              loadBundles(r.date);
            }}
            renderRowExpansion={(r) => renderBundleBreakdown(r.date, bundleBreak[r.date] || {}, () => {
              setBundleBreak(prev => { const n = { ...prev }; delete n[r.date]; return n; });
              loadBundles(r.date);
            })}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Chain header */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
        <div style={{ fontSize:'11px', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
          {supplyPartnerNames?.length > 0 && (
            <>
              <span style={{ color:'var(--green)', fontWeight:700 }}>{supplyPartnerNames.length} Supply Partner{supplyPartnerNames.length!==1?'s':''}</span>
              <ArrowRight size={12}/>
            </>
          )}
          <span style={{ color:'var(--cyan)', fontWeight:700 }}>{adServerName}</span>
          {demandPartnerNames?.length > 0 && (
            <>
              <ArrowRight size={12}/>
              <span style={{ color:'var(--purple)', fontWeight:700 }}>{demandPartnerNames.length} Demand Partner{demandPartnerNames.length!==1?'s':''}</span>
            </>
          )}
        </div>
      </div>

      {!supplyVsAdServer && !adServerVsDemand && (
        <div className="alert alert-warning">
          <AlertTriangle size={13}/>
          <span>No partner data found. Upload data for at least one Supply or Demand partner to see results.</span>
        </div>
      )}

      {/* Deductions — company-facing only. The banner states that plainly, so
          nobody assumes a shared link shows the same numbers. */}
      {monitorId && (
        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap', marginBottom:'12px',
                      padding:'8px 12px', borderRadius:'var(--radius-sm)',
                      border:'1px solid var(--border)', background:'var(--bg-card)' }}>
          <span style={{ fontSize:'10px', letterSpacing:'1px', textTransform:'uppercase', color:'var(--text-muted)' }}>
            Revenue adjustments
          </span>
          <button className="btn btn-sm" onClick={() => setShowDeductions(v => !v)}
                  style={{ background:'color-mix(in srgb, var(--amber) 16%, transparent)',
                           border:'1px solid color-mix(in srgb, var(--amber) 45%, transparent)',
                           color:'var(--amber)', fontWeight:600, display:'inline-flex', alignItems:'center', gap:'6px' }}>
            <DollarSign size={13}/> Deductions
            {deductions.length > 0 && (
              <span style={{ background:'var(--amber)', color:'var(--on-accent)', borderRadius:'10px',
                             padding:'0 6px', fontSize:'10px', fontWeight:700 }}>{deductions.length}</span>
            )}
          </button>
          {deductionTotal > 0 && (
            <>
              <label style={{ display:'flex', gap:'5px', alignItems:'center', fontSize:'11px', cursor:'pointer' }}>
                <input type="checkbox" checked={applyDeductions} onChange={() => setApplyDeductions(v => !v)} />
                Apply deductions
              </label>
              <span style={{ fontSize:'11px', color: applyDeductions ? 'var(--amber)' : 'var(--text-muted)' }}>
                {applyDeductions
                  ? `Showing adjusted — −$${deductionTotal.toFixed(2)} applied`
                  : `Showing reported — $${deductionTotal.toFixed(2)} not applied`}
              </span>
            </>
          )}
        </div>
      )}
      {showDeductions && monitorId && (
        <DeductionsPanel
          monitorId={monitorId}
          parties={buildDeductionParties()}
          dates={[...new Set([
            ...((supplyVsAdServer?.rows) || []).map(r => r.date),
            ...((adServerVsDemand?.rows) || []).map(r => r.date),
          ])].sort().reverse()}
          deductions={deductions}
          onChanged={loadDeductions}
          onClose={() => setShowDeductions(false)}
        />
      )}

      <ComparisonPanel
        data={supplyVsAdServer}
        leftRole="supply"
        rightRole="adserver"
        leftLabel={supplyPartnerNames?.length > 1 ? `Supply (${supplyPartnerNames.length})` : supplyPartnerNames?.[0] || 'Supply'}
        rightLabel={adServerName}
        leftColor="var(--green)"
        rightColor="var(--cyan)"
        title="Supply vs Ad Server"
        icon={<Activity size={14} style={{ color:'var(--green)' }}/>}
      />

      <ComparisonPanel
        data={adServerVsDemand}
        leftRole="adserver"
        rightRole="demand"
        leftLabel={adServerName}
        rightLabel={demandPartnerNames?.length > 1 ? `Demand (${demandPartnerNames.length})` : demandPartnerNames?.[0] || 'Demand'}
        leftColor="var(--cyan)"
        rightColor="var(--purple)"
        title="Ad Server vs Demand"
        icon={<Zap size={14} style={{ color:'var(--purple)' }}/>}
      />
    </div>
  );
}

// ─── Auto Monitor Schedule Panel ──────────────────────────────────────────────
function AutoMonitorSchedulePanel({ monitor, onToggle, onResultLoaded }) {
  const [toggling, setToggling] = useState(false);
  const [runs, setRuns] = useState([]);
  const [showRuns, setShowRuns] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // Load runs on mount for auto monitors — so latest result is available immediately
  useEffect(() => {
    loadRuns();
  }, [monitor.id]);

  async function loadRuns() {
    setLoadingRuns(true);
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/auto-monitors/runs/${monitor.id}`);
      setRuns(data);
      // Auto-load the latest successful run's full result into the card
      const latest = data.find(r => r.status !== 'error' && r.result_snapshot);
      if (latest?.result_snapshot && onResultLoaded) {
        // result_snapshot now stores the full result object
        const snap = latest.result_snapshot;
      // Only restore if it's a full result with rows — old snapshots only had {type, dateRange}
      if (snap?.rows?.length > 0) {
        onResultLoaded(snap);
      }
      }
    } catch {}
    finally { setLoadingRuns(false); }
  }

  async function handleToggle() {
    setToggling(true);
    try {
      const { data } = await axios.patch(`/api/disc-monitor-v2/auto-monitors/toggle/${monitor.id}`, {
        enabled: !monitor.scheduleEnabled,
      });
      onToggle(data.scheduleEnabled);
      toast.success(data.scheduleEnabled ? 'Auto Monitor enabled — runs once daily for yesterday\'s data' : 'Auto Monitor paused');
    } catch { toast.error('Failed to update schedule'); }
    finally { setToggling(false); }
  }

  const SEV_COLORS = { ok:'var(--green)', warning:'var(--amber)', critical:'var(--red)', error:'var(--text-muted)' };

  return (
    <div style={{ padding:'12px', background:'color-mix(in srgb, var(--green-soft) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--green-soft) 20%, transparent)', borderRadius:'var(--radius)', marginBottom:'14px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
        <Cpu size={14} style={{ color:'var(--green-soft)' }}/>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:'12px', color:'var(--text-primary)' }}>Automated Schedule</div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'1px' }}>
            Runs <strong>once daily at {String(Number.isInteger(monitor.runHourUtc) ? monitor.runHourUtc : 8).padStart(2,'0')}:00 UTC</strong> (fetches yesterday's data) · Alert threshold: <strong>{monitor.alertThreshold || 20}%</strong>
            {monitor.notificationEmail && <span> · Alerts to: <strong>{monitor.notificationEmail}</strong></span>}
            {monitor.lastRunDate && <span> · Last daily run: <strong>{monitor.lastRunDate}</strong></span>}
          </div>
        </div>
        <button
          onClick={handleToggle}
          disabled={toggling}
          style={{
            display:'flex', alignItems:'center', gap:'6px',
            padding:'6px 14px', borderRadius:'20px', border:'none', cursor:'pointer', fontWeight:700, fontSize:'12px',
            background: monitor.scheduleEnabled ? 'color-mix(in srgb, var(--green-soft) 15%, transparent)' : 'var(--bg-elevated)',
            color: monitor.scheduleEnabled ? 'var(--green-soft)' : 'var(--text-muted)',
            border: `1px solid ${monitor.scheduleEnabled ? 'color-mix(in srgb, var(--green-soft) 40%, transparent)' : 'var(--border)'}`,
          }}
        >
          {monitor.scheduleEnabled ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
          {monitor.scheduleEnabled ? 'Active' : 'Paused'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => { setShowRuns(s=>!s); if (!showRuns) loadRuns(); }}>
          <Clock size={11}/> Run History {runs.length > 0 && `(${runs.length})`}
        </button>
      </div>

      {showRuns && (
        <div style={{ marginTop:'10px', borderTop:'1px solid color-mix(in srgb, var(--green-soft) 15%, transparent)', paddingTop:'10px' }}>
          {loadingRuns ? (
            <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-muted)' }}><span className="spinner" /></div>
          ) : runs.length === 0 ? (
            <div style={{ fontSize:'11px', color:'var(--text-muted)', textAlign:'center' }}>No runs yet — the monitor will run automatically once daily when enabled.</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table className="data-table" style={{ fontSize:'11px' }}>
                <thead><tr><th>Run At</th><th>Date</th><th>Status</th><th>Imp %</th><th>Rev %</th><th>Details</th><th></th></tr></thead>
                <tbody>
                  {runs.map((r,i) => {
                    const color = SEV_COLORS[r.status] || 'var(--text-muted)';
                    const hasError = r.status === 'error';
                    const hasWarnings = r.summary?.fetchWarnings?.length > 0;
                    const dateRange = r.result_snapshot?.dateRange;
                    return (
                      <tr key={i} style={{ background: hasError ? 'color-mix(in srgb, var(--red) 4%, transparent)' : undefined }}>
                        <td className="mono" style={{ whiteSpace:'nowrap', color:'var(--cyan)', fontSize:'13px', fontWeight:600 }}>{new Date(r.run_at).toLocaleString()}</td>
                        <td className="mono" style={{ fontSize:'10px', color:'var(--text-muted)' }}>
                          {dateRange ? `${dateRange.from}` : '—'}
                        </td>
                        <td><span style={{ color, fontWeight:700, textTransform:'uppercase', fontSize:'10px' }}>{r.status}</span></td>
                        <td className="mono">{r.summary?.impPct != null ? `${r.summary.impPct.toFixed(2)}%` : '—'}</td>
                        <td className="mono">{r.summary?.revPct != null ? `${r.summary.revPct.toFixed(2)}%` : '—'}</td>
                        <td style={{ maxWidth:'200px' }}>
                          {hasError && r.summary?.error && (
                            <span style={{ fontSize:'10px', color:'var(--red)', display:'block', wordBreak:'break-word' }} title={r.summary.error}>
                              {r.summary.error.slice(0, 100)}{r.summary.error.length > 100 ? '…' : ''}
                            </span>
                          )}
                          {hasWarnings && (
                            <span style={{ fontSize:'10px', color:'var(--amber)', display:'block' }}>
                              ⚠ {r.summary.fetchWarnings.length} fetch warning{r.summary.fetchWarnings.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </td>
                        <td>
                          {r.result_snapshot?.rows?.length > 0 && !hasError && onResultLoaded && (
                            <button className="btn btn-secondary btn-sm" style={{ fontSize:'9px', whiteSpace:'nowrap' }}
                              onClick={() => { onResultLoaded(r.result_snapshot); setShowRuns(false); }}>
                              View Results
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Monitor Card ─────────────────────────────────────────────────────────────
// Color palette for monitor cards — cycles through if more than 8
const CARD_COLORS = [
  { accent:'var(--cyan)', bg:'color-mix(in srgb, var(--cyan) 6%, transparent)', border:'color-mix(in srgb, var(--cyan) 25%, transparent)', label:'Flame' },
  { accent:'var(--purple)', bg:'color-mix(in srgb, var(--purple) 6%, transparent)', border:'color-mix(in srgb, var(--purple) 25%, transparent)', label:'Purple' },
  { accent:'var(--green)', bg:'color-mix(in srgb, var(--green) 6%, transparent)', border:'color-mix(in srgb, var(--green) 25%, transparent)', label:'Green' },
  { accent:'var(--amber)', bg:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'color-mix(in srgb, var(--amber) 25%, transparent)', label:'Amber' },
  { accent:'#FF6B6B', bg:'rgba(255,107,107,0.06)', border:'rgba(255,107,107,0.25)', label:'Rose' },
  { accent:'#58A6FF', bg:'rgba(88,166,255,0.06)', border:'rgba(88,166,255,0.25)', label:'Blue' },
  { accent:'var(--green-soft)', bg:'color-mix(in srgb, var(--green-soft) 6%, transparent)', border:'color-mix(in srgb, var(--green-soft) 25%, transparent)', label:'Lime' },
  { accent:'#F472B6', bg:'rgba(244,114,182,0.06)', border:'rgba(244,114,182,0.25)', label:'Pink' },
];

function MonitorCard({ monitor, onDelete, onRefresh, cardIndex = 0, detailMode = false, hidden = false, onViewResults, onBackToList }) {
  const cardColor = CARD_COLORS[cardIndex % CARD_COLORS.length];
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [lastRun, setLastRun] = useState(monitor.lastRun || null);
  const [dataStatus, setDataStatus] = useState({});
  const [missingModal, setMissingModal] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [deepDiveResult, setDeepDiveResult] = useState(null);      // Advanced result
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [deepDiveNeedsData, setDeepDiveNeedsData] = useState(false); // no advertiser column
  const [bundleState, setBundleState] = useState(null);             // { partnerId, partnerName, adServerData, partnerData, result }
  const [bundleLoading, setBundleLoading] = useState(false);
  const [fetchingYesterday, setFetchingYesterday] = useState(false);
  // Custom date range for auto monitors — lets users backfill missed dates
  const [showDatePicker, setShowDatePicker]   = useState(false);
  const [customDateFrom, setCustomDateFrom]   = useState('');
  const [customDateTo, setCustomDateTo]       = useState('');
  // Briefly pulses the "View Results" button after a check completes so the
  // user notices the new CTA. Cleared after 4s.
  const [resultJustReady, setResultJustReady] = useState(false);
  useEffect(() => {
    if (!result) return;
    setResultJustReady(true);
    const t = setTimeout(() => setResultJustReady(false), 4000);
    return () => clearTimeout(t);
  }, [result]);

  // In detail mode, auto-expand the card body (otherwise nothing would render)
  useEffect(() => { if (detailMode) setExpanded(true); }, [detailMode]);

  // Check if this monitor has at least one party with a saved API config
  const hasAnySavedConfig = (() => {
    const pss = monitor.partySavedSettings || {};
    return Object.values(pss).some(s => s?.configId);
  })();

  async function runFetchYesterday() {
    setFetchingYesterday(true);
    try {
      const { data } = await axios.post(`/api/disc-monitor-v2/fetch-yesterday/${monitor.id}`);
      if (data.fetchErrors?.length) {
        toast(`⚠ Fetch warnings: ${data.fetchErrors.join(' | ')}`, { duration: 8000, icon: '⚠️' });
      }
      if (data.result) {
        setResult(data.result);
        setLastRun(new Date().toISOString());
        setExpanded(true);
        const yd = localYesterday();
        const parties = data.fetchedParties?.map(p => `${p.name} (${p.rows} rows)`).join(', ') || '';
        toast.success(`Fetched yesterday (${yd}) — ${parties}`);
      }
    } catch (e) {
      const err = e.response?.data;
      toast.error(err?.error || 'Fetch yesterday failed', { duration: 6000 });
      if (err?.fetchErrors?.length) toast(`Errors: ${err.fetchErrors.join(' | ')}`, { icon: '⚠️', duration: 8000 });
    } finally { setFetchingYesterday(false); }
  }

  const typeInfo = MONITOR_TYPES.find(t => t.id === monitor.monitorType) || MONITOR_TYPES[0];
  const TypeIcon = typeInfo.icon;

  useEffect(() => {
    if (expanded) {
      loadDataStatus();
      // Auto-load last successful run result for auto monitors
      if (monitor.monitorType === 'auto_monitor' && !result) {
        axios.get(`/api/disc-monitor-v2/auto-monitors/latest-result/${monitor.id}`)
          .then(({ data }) => {
            // Only restore if it's a full result with rows
            if (data.found && data.result?.rows?.length > 0) {
              setResult(data.result);
              setLastRun(data.run?.run_at || monitor.lastRun || null);
            }
          })
          .catch(() => {});
      }
    }
  }, [expanded]);

  async function loadDataStatus() {
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/data-status/${monitor.id}`);
      const map = {};
      data.forEach(d => { map[d.party_id] = d; });
      setDataStatus(map);
    } catch {}
  }

  async function runDeepDive() {
    setDeepDiveLoading(true);
    setDeepDiveResult(null);
    setDeepDiveNeedsData(false);
    setBundleState(null);
    try {
      const { data } = await axios.post(`/api/disc-monitor-v2/deep-dive/${monitor.id}`);
      if (!data.success && data.needsAdvertiserData) {
        setDeepDiveNeedsData(true);
        return;
      }
      setDeepDiveResult(data.result);
      toast.success('Deep dive complete');
    } catch (e) { toast.error(e.response?.data?.error || 'Deep dive failed'); }
    finally { setDeepDiveLoading(false); }
  }

  async function runBundleDeepDive(partnerId, partnerName) {
    if (!bundleState?.adServerData || !bundleState?.partnerData) {
      toast.error('Please upload both Ad Server and Partner bundle reports first');
      return;
    }
    setBundleLoading(true);
    try {
      const { data } = await axios.post(`/api/disc-monitor-v2/deep-dive-bundles/${monitor.id}`, {
        partnerId, partnerName,
        adServerData: bundleState.adServerData,
        partnerData: bundleState.partnerData,
      });
      setBundleState(prev => ({ ...prev, result: data }));
      toast.success('Bundle analysis complete');
    } catch (e) { toast.error(e.response?.data?.error || 'Bundle analysis failed'); }
    finally { setBundleLoading(false); }
  }

  async function handleRun() {
    if (monitor.monitorType === 'auto_monitor') {
      // Auto monitor uses its own endpoint that fetches data inline first
      runAutoCheck();
      return;
    }
    try {
      const { data: check } = await axios.post(`/api/disc-monitor-v2/check-missing/${monitor.id}`);
      if (check.hasMissing) { setMissingModal(check.missing); return; }
    } catch {}
    runCheck();
  }

  async function runAutoCheck(opts = {}) {
    setRunning(true);
    setLastRun(new Date().toISOString());
    setShowDatePicker(false);
    try {
      const body = {};
      if (opts.dateFrom) body.dateFrom = opts.dateFrom;
      if (opts.dateTo)   body.dateTo   = opts.dateTo;
      const { data } = await axios.post(`/api/disc-monitor-v2/auto-monitors/run/${monitor.id}`, body);
      if (data.fetchErrors?.length) {
        toast(`⚠️ Fetch warnings: ${data.fetchErrors.join(' | ')}`, { duration: 8000, icon: '⚠️' });
      }
      if (data.result) {
        setResult(data.result);
        setExpanded(true);
        const dateLabel = data.dateFrom === data.dateTo
          ? data.dateFrom
          : `${data.dateFrom} → ${data.dateTo}`;
        toast.success(`Check complete (${dateLabel}) — ${data.severity || 'ok'}`);
      } else {
        toast.error(data.error || 'Run failed — check fetch warnings above');
      }
    } catch (e) {
      const errData = e.response?.data;
      toast.error(errData?.error || 'Auto monitor run failed');
      if (errData?.fetchErrors?.length) {
        toast(`Fetch errors: ${errData.fetchErrors.join(' | ')}`, { duration: 8000, icon: '⚠️' });
      }
    } finally { setRunning(false); }
  }

  async function runCheck() {
    setMissingModal(null);
    setRunning(true);
    setLastRun(new Date().toISOString());
    try {
      const { data } = await axios.post(`/api/disc-monitor-v2/run/${monitor.id}`);
      if (!data.result) {
        toast.error('Run returned no results — check that data has been uploaded for all parties');
        return;
      }
      setResult(data.result);
      setExpanded(true);
      toast.success('Check complete');
    } catch (e) {
      const errData = e.response?.data;
      toast.error(errData?.error || 'Run failed');
    } finally { setRunning(false); }
  }

  // ── Bundle export ──────────────────────────────────────────────────────────
  // Available dates come from the SERVER: "which dates have stored bundle rows"
  // is a different question from "which dates the monitor covers", and offering a
  // date with nothing behind it just produces an empty file.
  const [expOpen, setExpOpen] = useState(false);
  const [expDates, setExpDates] = useState(null);      // null = not loaded yet
  const [expSel, setExpSel] = useState([]);            // [] = all
  const [expFmt, setExpFmt] = useState('xlsx');
  const [expBusy, setExpBusy] = useState(false);
  const [expErr, setExpErr] = useState(null);

  async function openExport() {
    const next = !expOpen;
    setExpOpen(next);
    if (!next || expDates) return;
    setExpErr(null);
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/monitors/${monitor.id}/bundle-export/dates`);
      setExpDates(data.dates || []);
    } catch (e) {
      setExpDates([]);
      setExpErr(e.response?.data?.error || 'Could not load the available dates');
    }
  }

  async function runExport() {
    setExpBusy(true); setExpErr(null);
    try {
      const res = await axios.post(
        `/api/disc-monitor-v2/monitors/${monitor.id}/bundle-export`,
        {
          dates: expSel.length ? expSel : 'all',
          format: expFmt,
          monitorName: monitor.name,
          adServerName: monitor.adServer?.name || 'Ad Server',
          // Stored rows only know party_id; the monitor object carries the names.
          partyNames: (monitor.partners || []).map(p => ({ id: p.id, name: p.name })),
        },
        { responseType: 'blob' });
      // axios + blob, never window.open — a plain navigation carries no
      // Authorization header and the request would come back unauthenticated.
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = m ? m[1] : `bundles.${expFmt}`;
      a.click(); URL.revokeObjectURL(a.href);
      setExpOpen(false);
    } catch (e) {
      // An error body arrives as a Blob here, so the usual e.response.data.error
      // is an object — read it as text before showing anything.
      let msg = 'Export failed';
      try { if (e.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg; }
      catch { /* keep the generic message */ }
      setExpErr(msg);
    }
    setExpBusy(false);
  }

  function exportCSV() {
    if (!result) return;
    // Basic type export
    if (result.type === 'basic') {
      const rows = [['Date','Ad Server Imps','Partners Total Imps','Imp Diff','Imp %','Ad Server Rev','Partners Total Rev','Rev Diff','Rev %','Status']];
      result.rows.forEach(r => rows.push([r.date,r.adServerImps,r.partnerImps,(Number(r.adServerImps)-Number(r.partnerImps)),r.impPct,r.adServerRev,r.partnerRev,(Number(r.adServerRev)-Number(r.partnerRev)),r.revPct,r.severity]));
      const csv = rows.map(r => r.map(c=>`"${String(c??'').replace(/"/g,'""')}"`).join(',')).join('\n');
      const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
      a.download = `${monitor.name.replace(/\s+/g,'-')}-${new Date().toISOString().split('T')[0]}.csv`; a.click();
      return;
    }

    // ── Everything that is not 'basic' ────────────────────────────────────
    // This branch did not exist: on 'freeform' and 'advanced' the button was
    // clickable and did nothing at all. Columns are DERIVED from the rows rather
    // than hardcoded, because the key set differs per monitor — the freeform
    // panel discovers its own columns the same way.
    const sections = [];
    if (result.supplyVsAdServer?.rows?.length) sections.push(['Supply vs Ad Server', result.supplyVsAdServer.rows]);
    if (result.adServerVsDemand?.rows?.length) sections.push(['Ad Server vs Demand', result.adServerVsDemand.rows]);
    if (!sections.length && Array.isArray(result.rows) && result.rows.length) sections.push(['Results', result.rows]);

    if (!sections.length) {
      // Say so. Silence is what made this button feel broken.
      toast.error('Nothing to export yet — run the monitor first');
      return;
    }

    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [];
    sections.forEach(([title, rows], i) => {
      if (sections.length > 1) {
        if (i) lines.push('');
        lines.push(esc(title));
      }
      // Union of keys across ALL rows, not just the first — a row missing a key
      // would otherwise drop that column for everyone.
      const cols = [];
      rows.forEach(r => Object.keys(r || {}).forEach(k => { if (!cols.includes(k)) cols.push(k); }));
      lines.push(cols.map(esc).join(','));
      rows.forEach(r => lines.push(cols.map(c => esc(r?.[c])).join(',')));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a2 = document.createElement('a');
    a2.href = URL.createObjectURL(blob);
    a2.download = `${monitor.name.replace(/\s+/g,'-')}-${new Date().toISOString().split('T')[0]}.csv`;
    a2.click(); URL.revokeObjectURL(a2.href);
  }

  const overallSev = result
    ? SEV[result.summary?.severity || result.summary?.overallSeverity] || SEV.ok
    : null;

  return (
    <div style={{ background:'var(--bg-card)', border:`2px solid ${overallSev ? overallSev.border : cardColor.border}`, borderRadius:'var(--radius)', marginBottom:'12px', overflow:'hidden', display: hidden ? 'none' : 'block' }}>
      {/* Header */}
      <div style={{ padding:'14px 16px', display:'flex', gap:'12px', alignItems:'center', background:cardColor.bg, borderBottom:expanded?`1px solid ${cardColor.border}`:'none', flexWrap:'wrap', borderLeft:`4px solid ${cardColor.accent}` }}>
        <div onClick={() => setExpanded(e=>!e)} style={{ cursor:'pointer', display:'flex', gap:'8px', alignItems:'center', flex:1, minWidth:0 }}>
          {expanded ? <ChevronDown size={13} style={{ color:'var(--text-muted)' }}/> : <ChevronRight size={13} style={{ color:'var(--text-muted)'}}/>}
          <TypeIcon size={16} style={{ color:cardColor.accent, flexShrink:0 }} />
          <div style={{ minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:'14px', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{monitor.name}</div>
            <div style={{ fontSize:'10px', color:cardColor.accent, marginTop:'1px', fontWeight:600 }}>{typeInfo.label}</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap' }}>
          {overallSev && <span className={`badge ${overallSev.badge}`}>{overallSev.icon} {result.summary?.severity || result.summary?.overallSeverity}</span>}
          {lastRun && <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{timeAgo(lastRun)}</span>}
          {result && <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={11}/> CSV</button>}
          {result && (
            <div style={{ position:'relative' }}>
              <button className="btn btn-secondary btn-sm" onClick={openExport}
                      title="Export per-app detail across one or more dates">
                <Download size={11}/> Bundles
              </button>
              {expOpen && (
                <div style={{ position:'absolute', right:0, top:'calc(100% + 6px)', zIndex:40, width:'260px',
                              background:'var(--bg-card)', border:'1px solid var(--border)',
                              borderRadius:'var(--radius-sm)', padding:'10px', boxShadow:'0 8px 24px rgba(0,0,0,.35)' }}>
                  <div style={{ fontSize:'11px', fontWeight:700, marginBottom:'6px' }}>Export bundle detail</div>
                  {expDates === null && <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>Loading dates…</div>}
                  {expDates && expDates.length === 0 && (
                    <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                      No stored bundle rows yet. Upload a report that includes a bundle column.
                    </div>
                  )}
                  {expDates && expDates.length > 0 && (
                    <>
                      <label style={{ display:'flex', gap:'6px', alignItems:'center', fontSize:'11px', marginBottom:'4px', cursor:'pointer' }}>
                        <input type="checkbox" checked={expSel.length === 0}
                               onChange={() => setExpSel([])} />
                        <strong>All dates ({expDates.length})</strong>
                      </label>
                      <div style={{ maxHeight:'150px', overflowY:'auto', borderTop:'1px solid var(--border)', paddingTop:'4px' }}>
                        {expDates.map(d => (
                          <label key={d.d} style={{ display:'flex', gap:'6px', alignItems:'center', fontSize:'11px', padding:'2px 0', cursor:'pointer' }}>
                            <input type="checkbox" checked={expSel.includes(d.d)}
                                   onChange={() => setExpSel(prev => prev.includes(d.d)
                                     ? prev.filter(x => x !== d.d) : [...prev, d.d])} />
                            <span style={{ fontFamily:'var(--font-mono)' }}>{d.d}</span>
                            <span style={{ color:'var(--text-muted)', marginLeft:'auto' }}>{d.rows}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ display:'flex', gap:'6px', alignItems:'center', marginTop:'8px' }}>
                        <select className="select-field" value={expFmt} onChange={e => setExpFmt(e.target.value)}
                                style={{ fontSize:'11px', padding:'3px 6px', flex:1 }}>
                          <option value="xlsx">Excel (.xlsx)</option>
                          <option value="csv">CSV</option>
                        </select>
                        <button className="btn btn-primary btn-sm" disabled={expBusy} onClick={runExport}>
                          {expBusy ? 'Building…' : 'Download'}
                        </button>
                      </div>
                      <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'5px' }}>
                        {expSel.length === 0 ? `All ${expDates.length} dates` : `${expSel.length} date${expSel.length === 1 ? '' : 's'} selected`}
                      </div>
                    </>
                  )}
                  {expErr && <div style={{ fontSize:'11px', color:'var(--amber)', marginTop:'6px' }}>{expErr}</div>}
                </div>
              )}
            </div>
          )}
          {/* Action buttons — only shown in list view. In detail view we keep
              the header read-only so the user focuses on results; all
              operational actions (Run, Edit, Share, Delete, etc.) live on
              the list-card expanded body. */}
          {!detailMode && <>
          {/* Fetch Yesterday's Data — for non-auto monitors with at least one saved config */}
          {monitor.monitorType !== 'auto_monitor' && hasAnySavedConfig && (
            <button className="btn btn-secondary btn-sm" onClick={runFetchYesterday}
              disabled={fetchingYesterday || running}
              title="Fetch yesterday's data for all parties with saved configs, then run discrepancy check"
              style={{ borderColor:'color-mix(in srgb, var(--cyan) 40%, transparent)', color:'var(--cyan)' }}>
              {fetchingYesterday
                ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Fetching...</>
                : <><RefreshCw size={11}/> Fetch Yesterday</>}
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={handleRun} disabled={running}>
            {running ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Running...</> : <><Play size={11}/> Run Check</>}
          </button>
          {/* View Results — opens the detail view (results-only) for this monitor.
              Disabled until a completed result exists. Briefly highlighted with
              a cyan glow when a new result lands so the user notices the CTA. */}
          <button
            className={`btn btn-sm ${result ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => result && onViewResults && onViewResults(monitor.id)}
            disabled={!result}
            title={result ? 'Open detailed results view' : 'Run a check first — View Results becomes available once results are ready'}
            style={{
              ...(resultJustReady && result ? {
                boxShadow: '0 0 0 3px color-mix(in srgb, var(--cyan) 55%, transparent)',
                transition: 'box-shadow 0.3s',
              } : { transition: 'box-shadow 0.3s' }),
              ...(!result ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
            }}>
            <Eye size={11}/> View Results
          </button>
          {/* Custom date range — for auto monitors to backfill missed dates */}
          {monitor.monitorType === 'auto_monitor' && (
            <button
              className={`btn btn-sm ${showDatePicker ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => {
                setShowDatePicker(v => !v);
                setExpanded(true); // always expand to show the picker
                if (!customDateFrom) {
                  // Default: last 7 days (local tz)
                  const to   = new Date(); to.setDate(to.getDate() - 1);
                  const from = new Date(); from.setDate(from.getDate() - 7);
                  setCustomDateTo(localYMD(to));
                  setCustomDateFrom(localYMD(from));
                }
              }}
              title="Run check for a custom date range — use this to backfill missed dates"
              style={{ borderColor: showDatePicker ? undefined : 'color-mix(in srgb, var(--amber) 40%, transparent)', color: showDatePicker ? undefined : 'var(--amber)' }}>
              📅 Custom Dates
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setShowShare(true)} title="Share read-only link"
            style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}>
            🔗 Share
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowEdit(true)} title="Edit monitor"><Edit2 size={11}/></button>
          <button className="btn btn-danger btn-sm" onClick={() => onDelete(monitor.id)}><Trash2 size={11}/></button>
          </>}
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'14px 16px' }}>

          {/* Operational chrome — list view only. Detail view is read-only
              results, so all the chrome (Upload, custom dates, info banners)
              is gated. */}
          {!detailMode && <>

          {/* Custom date range picker panel — auto monitors only */}
          {monitor.monitorType === 'auto_monitor' && showDatePicker && (
            <div style={{ marginBottom:'14px', padding:'14px 16px', background:'color-mix(in srgb, var(--amber) 5%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 30%, transparent)', borderRadius:'var(--radius-sm)' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:'var(--amber)', marginBottom:'4px', display:'flex', alignItems:'center', gap:'6px' }}>
                📅 Run Check for Custom Date Range
              </div>
              <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'12px', lineHeight:1.6 }}>
                Use this to backfill missed dates — e.g. when a token expired and yesterday's run failed.
                Fetches fresh data from all API configs for the selected range and runs discrepancy check.
              </div>
              <div style={{ display:'flex', gap:'10px', alignItems:'flex-end', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'4px', fontWeight:700 }}>From</div>
                  <input type="date" className="input-field"
                    style={{ width:'160px', fontFamily:'var(--font-mono)', fontSize:'12px' }}
                    value={customDateFrom}
                    max={customDateTo || undefined}
                    onChange={e => setCustomDateFrom(e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'4px', fontWeight:700 }}>To</div>
                  <input type="date" className="input-field"
                    style={{ width:'160px', fontFamily:'var(--font-mono)', fontSize:'12px' }}
                    value={customDateTo}
                    min={customDateFrom || undefined}
                    max={localYesterday()}
                    onChange={e => setCustomDateTo(e.target.value)} />
                </div>
                <button
                  className="btn btn-primary"
                  disabled={running || !customDateFrom || !customDateTo}
                  style={{ padding:'8px 20px' }}
                  onClick={() => runAutoCheck({ dateFrom: customDateFrom, dateTo: customDateTo })}>
                  {running
                    ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Running...</>
                    : <><Play size={12}/> Run for {customDateFrom === customDateTo ? customDateFrom : `${customDateFrom} → ${customDateTo}`}</>}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowDatePicker(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Auto Monitor schedule panel */}
          {monitor.monitorType === 'auto_monitor' && (
            <AutoMonitorSchedulePanel
              monitor={monitor}
              onResultLoaded={loadedResult => {
                setResult(loadedResult);
                setExpanded(true);
              }}
              onToggle={enabled => {
                setTimeout(onRefresh, 300);
              }}
            />
          )}

          {/* Upload section — hidden for auto monitors (API-only) */}
          {monitor.monitorType !== 'auto_monitor' && (
            <div style={{ marginBottom:'16px' }}>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px', fontWeight:700 }}>
                Upload Reports
              </div>
              {monitor.monitorType === 'master_basic' && (
                <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--purple) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--purple) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'10px', fontSize:'12px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                  <strong style={{ color:'var(--purple)' }}>💡 Tip:</strong> Upload your Ad Server report with an <strong>Advertiser / Demand Partner name</strong> column to unlock <strong>Deep Dive</strong>.
                </div>
              )}
              {monitor.monitorType === 'freeform' && (
                <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'10px', fontSize:'12px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                  <strong style={{ color:'var(--amber)' }}>💡 Free Form:</strong> Upload data for any combination of Supply, Ad Server, and Demand partners below. Supply vs Ad Server and Ad Server vs Demand discrepancies are computed separately.
                </div>
              )}
              {/* NEW: Searchable partner quick-panel replaces the old flat list */}
              <PartnerQuickPanel
                monitor={monitor}
                dataStatus={dataStatus}
                onUploaded={loadDataStatus}
                onRefreshMonitor={onRefresh}
              />
            </div>
          )}

          {/* Auto monitor: info about data source */}
          {monitor.monitorType === 'auto_monitor' && (
            <div className="alert alert-info" style={{ marginBottom:'14px' }}>
              <Info size={12}/>
              <span style={{ fontSize:'12px' }}>
                This monitor fetches <strong>yesterday's data</strong> automatically <strong>once daily</strong>.
                Filters configured during setup are applied on every run.
                Use <strong>Run Check</strong> to trigger a manual fetch now.
              </span>
            </div>
          )}

          </>}
          {/* End operational chrome (list view only) */}

          {/* Results — DETAIL MODE ONLY. In list view, the user sees only the
              operational chrome (Upload Reports, Run Check, Edit/Share/Delete).
              Results live exclusively in the detail view to keep the list
              scannable. The "View Results" CTA in the card header is how
              users navigate from list → detail. */}
          {detailMode && result && (
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px', flexWrap:'wrap' }}>
                <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                  Last run: {lastRun ? new Date(lastRun).toLocaleString() : '—'}
                </span>
                {result.dateRange && (
                  <span style={{ fontSize:'11px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'2px 8px', fontFamily:'var(--font-mono)', color:'var(--cyan)' }}>
                    {result.dateRange.from}{result.dateRange.from !== result.dateRange.to ? ` → ${result.dateRange.to}` : ''}
                  </span>
                )}
                {monitor.monitorType === 'auto_monitor' && (
                  <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>
                    Data: yesterday's ({localYesterday()})
                  </span>
                )}
              </div>
              {result.type === 'basic' && <BasicResults result={result} adServerName={monitor.adServer?.name || 'Ad Server'} monitorId={monitor.id} monitorType={monitor.monitorType} />}
              {result.type === 'advanced' && <AdvancedResults result={result} adServerName={monitor.adServer?.name || 'Ad Server'} />}
              {result.type === 'freeform' && <FreeFormResults result={result} monitorName={monitor.name} monitorId={monitor.id} partyNames={monitor.partners} />}

              {/* ── Deep Dive Flow (Basic monitors only) ────────────────── */}
              {monitor.monitorType === 'master_basic' && result.type === 'basic' && (
                <div style={{ marginTop:'16px', padding:'16px', background:'color-mix(in srgb, var(--purple) 5%, transparent)', border:'1px solid color-mix(in srgb, var(--purple) 20%, transparent)', borderRadius:'var(--radius)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px', marginBottom: deepDiveResult || deepDiveNeedsData ? '14px' : 0 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>
                        <Zap size={14} style={{ color:'var(--purple)', verticalAlign:'middle', marginRight:'6px' }}/>
                        Want to know which Demand Partner is causing the discrepancy?
                      </div>
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'3px' }}>
                        Deep Dive runs an Advertiser-wise analysis to pinpoint the exact partner.
                      </div>
                    </div>
                    <button className="btn btn-sm" onClick={runDeepDive} disabled={deepDiveLoading}
                      style={{ background:'var(--purple)', color:'var(--on-purple)', border:'none', padding:'8px 18px', fontWeight:700, borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', opacity: deepDiveLoading?0.7:1 }}>
                      {deepDiveLoading
                        ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Analysing...</>
                        : <><Zap size={12}/> Deep Dive?</>}
                    </button>
                  </div>

                  {/* Needs advertiser data warning */}
                  {deepDiveNeedsData && (
                    <div className="alert alert-warning">
                      <AlertTriangle size={13}/>
                      <div style={{ fontSize:'12px' }}>
                        <strong>Advertiser column not found</strong> in your Ad Server report. To use Deep Dive, please upload a report that includes an <strong>Advertiser / Partner name</strong> column alongside Date, Impressions and Revenue. Then re-upload and run Deep Dive again.
                      </div>
                    </div>
                  )}

                  {/* Deep dive advanced result */}
                  {deepDiveResult && (
                    <div>
                      <div style={{ fontSize:'12px', fontWeight:700, color:'var(--purple)', marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
                        <Zap size={13}/> Advertiser-wise Discrepancy
                      </div>
                      <AdvancedResults result={deepDiveResult} adServerName={monitor.adServer?.name || 'Ad Server'} />

                      {/* Deep Dive More — bundle level */}
                      {deepDiveResult.partnerResults?.length > 0 && (
                        <div style={{ marginTop:'16px', padding:'14px', background:'color-mix(in srgb, var(--green) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius:'var(--radius)' }}>
                          <div style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)', marginBottom:'4px' }}>
                            <BarChart2 size={14} style={{ color:'var(--green)', verticalAlign:'middle', marginRight:'6px' }}/>
                            Want to go deeper? Find which Bundles are causing the discrepancy.
                          </div>
                          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'12px' }}>
                            Select a partner and upload bundle-level reports from both your Ad Server and the partner to see bundle-wise discrepancy.
                          </div>

                          {/* Partner selector + bundle uploads */}
                          <div style={{ marginBottom:'10px' }}>
                            <label style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px' }}>Select Demand Partner to analyse</label>
                            <select className="select-field" style={{ width:'100%', maxWidth:'320px' }}
                              value={bundleState?.partnerId || ''}
                              onChange={e => {
                                const p = deepDiveResult.partnerResults.find(x => x.partnerId === e.target.value);
                                setBundleState(p ? { partnerId:p.partnerId, partnerName:p.partnerName, adServerData:null, partnerData:null, result:null, selectedDateFrom:'', selectedDateTo:'' } : null);
                              }}>
                              <option value="">— Choose partner —</option>
                              {deepDiveResult.partnerResults.map(p => (
                                <option key={p.partnerId} value={p.partnerId}>
                                  {p.partnerName} ({p.summary?.severity?.toUpperCase()})
                                </option>
                              ))}
                            </select>
                          </div>

                          {bundleState?.partnerId && (() => {
                            // Get high-discrepancy dates for this partner from advanced result
                            const partnerResult = deepDiveResult.partnerResults.find(p => p.partnerId === bundleState.partnerId);
                            const highDiscDates = (partnerResult?.rows || [])
                              .filter(r => Math.abs(r.impPct) > 5 || Math.abs(r.revPct) > 5)
                              .sort((a,b) => Math.abs(b.impPct) - Math.abs(a.impPct))
                              .map(r => r.date);

                            return (
                              <div>
                                {/* Date range selector with high-discrepancy dates highlighted */}
                                {highDiscDates.length > 0 && (
                                  <div style={{ padding:'12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
                                    <div style={{ fontSize:'11px', fontWeight:700, color:'var(--amber)', marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                                      <AlertTriangle size={12}/> Dates with &gt;5% discrepancy — upload bundle reports for these dates
                                    </div>
                                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'10px' }}>
                                      {highDiscDates.map(d => {
                                        const row = partnerResult.rows.find(r => r.date === d);
                                        const sevColor = Math.abs(row?.impPct||0) > 20 ? 'var(--red)' : 'var(--amber)';
                                        const isSelected = bundleState.selectedDateFrom <= d && d <= bundleState.selectedDateTo;
                                        return (
                                          <div key={d} style={{ padding:'4px 10px', borderRadius:'4px', fontSize:'11px', fontFamily:'var(--font-mono)', fontWeight:600,
                                            background: isSelected ? 'color-mix(in srgb, var(--green) 15%, transparent)' : 'var(--bg-elevated)',
                                            border: `1px solid ${isSelected ? 'var(--green)' : sevColor}`,
                                            color: isSelected ? 'var(--green)' : sevColor }}>
                                            {d}
                                            <span style={{ marginLeft:'5px', fontSize:'10px', opacity:0.8 }}>
                                              {Math.abs(row?.impPct||0).toFixed(1)}%
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                                      <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>Select date range to upload reports for:</span>
                                      <input type="date" className="input-field" style={{ width:'150px', colorScheme:'dark' }}
                                        value={bundleState.selectedDateFrom || ''}
                                        onChange={e => setBundleState(prev => ({ ...prev, selectedDateFrom: e.target.value }))}
                                      />
                                      <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>→</span>
                                      <input type="date" className="input-field" style={{ width:'150px', colorScheme:'dark' }}
                                        value={bundleState.selectedDateTo || ''}
                                        onChange={e => setBundleState(prev => ({ ...prev, selectedDateTo: e.target.value }))}
                                      />
                                      {(bundleState.selectedDateFrom || bundleState.selectedDateTo) && (
                                        <button className="btn btn-secondary btn-sm" onClick={() => setBundleState(prev => ({ ...prev, selectedDateFrom:'', selectedDateTo:'' }))}>✕ Clear</button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Upload slots */}
                                <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px', fontWeight:700 }}>
                                  Upload Bundle Reports
                                  {bundleState.selectedDateFrom && bundleState.selectedDateTo && (
                                    <span style={{ color:'var(--cyan)', fontWeight:400, textTransform:'none', marginLeft:'8px', letterSpacing:0 }}>
                                      for {bundleState.selectedDateFrom} → {bundleState.selectedDateTo}
                                    </span>
                                  )}
                                  <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', marginLeft:'6px', letterSpacing:0 }}>
                                    (needs: Date, Bundle ID, Impressions, Revenue)
                                  </span>
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                                  <BundleUploadSlot
                                    label={`${monitor.adServer?.name || 'Ad Server'} — Bundle Report`}
                                    color="var(--cyan)"
                                    onChange={d => setBundleState(prev => ({ ...prev, adServerData:d, result:null }))}
                                  />
                                  <BundleUploadSlot
                                    label={`${bundleState.partnerName} — Bundle Report`}
                                    color="var(--purple)"
                                    onChange={d => setBundleState(prev => ({ ...prev, partnerData:d, result:null }))}
                                  />
                                </div>

                                {/* Action buttons */}
                                <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                                  <button className="btn btn-sm" disabled={bundleLoading || !bundleState?.adServerData || !bundleState?.partnerData}
                                    onClick={() => runBundleDeepDive(bundleState.partnerId, bundleState.partnerName)}
                                    style={{ background:'var(--green)', color:'var(--on-green)', border:'none', padding:'8px 18px', fontWeight:700, borderRadius:'var(--radius-sm)', display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', opacity:(bundleLoading||!bundleState?.adServerData||!bundleState?.partnerData)?0.5:1 }}>
                                    {bundleLoading
                                      ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Analysing bundles...</>
                                      : <><BarChart2 size={12}/> Deep Dive More?</>}
                                  </button>

                                  {/* Edit — re-select dates and re-upload */}
                                  {bundleState?.result && (
                                    <button className="btn btn-secondary btn-sm"
                                      onClick={() => setBundleState(prev => ({ ...prev, adServerData:null, partnerData:null, result:null, selectedDateFrom:'', selectedDateTo:'' }))}>
                                      <Edit2 size={11}/> Edit / Change Dates
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Bundle results */}
                          {bundleState?.result?.success && (
                            <BundleResults
                              result={bundleState.result}
                              adServerName={monitor.adServer?.name || 'Ad Server'}
                              partnerName={bundleState.partnerName}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Missing partners modal */}
      {missingModal && (
        <MissingPartnersModal
          missing={missingModal}
          monitorId={monitor.id}
          partners={monitor.partners}
          onConfirm={runCheck}
          onCancel={() => setMissingModal(null)}
        />
      )}

      {/* Edit monitor modal */}
      {showEdit && (
        <EditMonitorModal
          monitor={monitor}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onRefresh(); }}
        />
      )}

      {/* Share-link modal */}
      {showShare && (
        <ShareMonitorModal
          monitor={monitor}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}

// ─── Auto Monitor Filter Rows ─────────────────────────────────────────────────
// MUST be top-level — defining this inside a modal causes remount on every
// keystroke (new component type per render) which loses input focus.

// PAS_DIMS / CRITEO_DIMS / GENERIC_DIMS / getDimsForConfig removed in Sprint 2:
// dimensions now read directly from cfg.dimensions (populated by Sprint 1B backfill).
// See frontend/src/lib/filterShape.js + docs/ignitorads-architecture-roadmap.md

// ─── Share-link modal ─────────────────────────────────────────────────────────
// Creates, lists, and revokes public read-only share links for a monitor.
// The recipient has no IgnitorAds account — they consume `/api/public/share/monitor/:token`.
function ShareMonitorModal({ monitor, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiryDays, setExpiryDays] = useState('never');  // 'never' | '7' | '30' | '90'
  const [label, setLabel] = useState('');
  const [copiedToken, setCopiedToken] = useState(null);

  async function loadShares() {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/disc-monitor-v2/monitor-shares/${monitor.id}`);
      setShares(data.shares || []);
    } catch (e) { toast.error('Could not load shares: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadShares(); }, [monitor.id]);

  async function createShare() {
    // Confirm "never" because forever-links are a real liability
    if (expiryDays === 'never' && !window.confirm(
      'Create a share link that NEVER expires?\n\nAnyone with this link will be able to view this monitor indefinitely until you manually revoke it. Continue?'
    )) return;
    setCreating(true);
    try {
      const body = { label: label.trim() || null };
      if (expiryDays !== 'never') body.expiresInDays = parseInt(expiryDays, 10);
      const { data } = await axios.post(`/api/disc-monitor-v2/monitor-shares/${monitor.id}`, body);
      toast.success('Share link created');
      setLabel('');
      await loadShares();
      // Auto-copy the new token's URL for convenience
      if (data.share?.token) copyLink(data.share.token);
    } catch (e) { toast.error('Could not create share: ' + (e.response?.data?.error || e.message)); }
    finally { setCreating(false); }
  }

  async function revokeShare(shareId) {
    if (!window.confirm('Revoke this share link? Anyone using it will lose access immediately.')) return;
    try {
      await axios.delete(`/api/disc-monitor-v2/monitor-shares/${shareId}`);
      toast.success('Share link revoked');
      await loadShares();
    } catch (e) { toast.error('Could not revoke: ' + (e.response?.data?.error || e.message)); }
  }

  function shareUrl(token) { return `${window.location.origin}/share/m/${token}`; }
  function copyLink(token) {
    const url = shareUrl(token);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token);
      toast.success('Link copied');
      setTimeout(() => setCopiedToken(null), 2000);
    }).catch(() => toast.error('Could not copy — copy from the input manually'));
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'720px', maxHeight:'90vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'8px' }}>
            🔗 Share: {monitor.name}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px' }}>✕</button>
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY:'auto', padding:'20px', flex:1 }}>
          <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'14px', lineHeight:1.5 }}>
            Create a read-only link to share this monitor's results with partners or stakeholders.
            Recipients don't need an IgnitorAds account. They can see dates, impressions, revenue,
            differences, and status — nothing else. <strong style={{ color:'var(--amber)' }}>Anyone with the
            link can view the data</strong>, so treat it like a credential.
          </div>

          {/* Create new */}
          <div style={{ background:'var(--bg-elevated)', padding:'12px', borderRadius:'var(--radius-sm)', marginBottom:'16px', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:'var(--text-primary)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Create new link</div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr auto', gap:'8px', alignItems:'end' }}>
              <div>
                <label style={{ fontSize:'10px', color:'var(--text-muted)', display:'block', marginBottom:'3px' }}>Label (optional, helps you remember)</label>
                <input className="input-field" placeholder="e.g. Criteo team weekly" value={label} onChange={e => setLabel(e.target.value)} style={{ fontSize:'12px', width:'100%' }}/>
              </div>
              <div>
                <label style={{ fontSize:'10px', color:'var(--text-muted)', display:'block', marginBottom:'3px' }}>Expires</label>
                <select className="select-field" value={expiryDays} onChange={e => setExpiryDays(e.target.value)} style={{ fontSize:'12px', width:'100%' }}>
                  <option value="never">Never</option>
                  <option value="7">After 7 days</option>
                  <option value="30">After 30 days</option>
                  <option value="90">After 90 days</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={createShare} disabled={creating}>
                {creating ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Creating…</> : 'Create link'}
              </button>
            </div>
          </div>

          {/* Existing shares */}
          <div style={{ fontSize:'11px', fontWeight:700, color:'var(--text-primary)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Existing links</div>
          {loading ? (
            <div style={{ fontSize:'12px', color:'var(--text-muted)' }}>Loading…</div>
          ) : shares.length === 0 ? (
            <div style={{ fontSize:'12px', color:'var(--text-muted)', fontStyle:'italic', padding:'12px', textAlign:'center', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)' }}>No share links yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {shares.map(s => {
                const isRevoked = !!s.revoked_at;
                const isExpired = s.expires_at && new Date(s.expires_at) < new Date();
                const isActive = !isRevoked && !isExpired;
                return (
                  <div key={s.id} style={{ padding:'10px', background:'var(--bg-elevated)', border:`1px solid ${isActive ? 'var(--border)' : 'color-mix(in srgb, var(--red) 30%, transparent)'}`, borderRadius:'var(--radius-sm)', opacity: isActive ? 1 : 0.6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)' }}>{s.label || '(unnamed)'}</span>
                      {isRevoked && <span style={{ fontSize:'10px', color:'var(--red)' }}>● revoked</span>}
                      {isExpired && !isRevoked && <span style={{ fontSize:'10px', color:'var(--amber)' }}>● expired</span>}
                      {isActive && <span style={{ fontSize:'10px', color:'var(--green)' }}>● active</span>}
                      <span style={{ marginLeft:'auto', fontSize:'10px', color:'var(--text-muted)' }}>
                        Views: {s.view_count} · {s.expires_at ? `Expires ${new Date(s.expires_at).toLocaleDateString()}` : 'Never expires'}
                      </span>
                    </div>
                    {isActive && (
                      <div style={{ display:'flex', gap:'6px' }}>
                        <input className="input-field" readOnly value={shareUrl(s.token)} style={{ fontSize:'11px', fontFamily:'var(--font-mono)', flex:1 }} onFocus={e => e.target.select()}/>
                        <button className="btn btn-secondary btn-sm" onClick={() => copyLink(s.token)}>{copiedToken === s.token ? '✓ Copied' : 'Copy'}</button>
                        <button className="btn btn-danger btn-sm" onClick={() => revokeShare(s.id)}>Revoke</button>
                      </div>
                    )}
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'5px' }}>
                      Created {new Date(s.created_at).toLocaleString()}
                      {s.last_viewed_at && ` · Last viewed ${new Date(s.last_viewed_at).toLocaleString()}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AutoMonitorFilterRows({ partyId, partyLabel, partyColor, autoFilters, setAutoFilters, savedConfigs, configId }) {
  const cfg = savedConfigs.find(c => String(c.id) === String(configId));
  const dims = (cfg?.dimensions || []).filter(d => d.filterable !== false);
  const filters = autoFilters[partyId] || [];

  function updateFilter(fi, field, val) {
    setAutoFilters(prev => ({
      ...prev,
      [partyId]: (prev[partyId]||[]).map((x, xi) => xi === fi ? { ...x, [field]: val } : x),
    }));
  }
  function removeFilter(fi) {
    setAutoFilters(prev => ({ ...prev, [partyId]: (prev[partyId]||[]).filter((_, xi) => xi !== fi) }));
  }
  function addFilter() {
    setAutoFilters(prev => ({ ...prev, [partyId]: [...(prev[partyId]||[]), { key:'', value:'', operator:'=' }] }));
  }

  return (
    <div style={{ marginBottom:'10px' }}>
      <div style={{ fontSize:'10px', color:partyColor, fontWeight:700, marginBottom:'5px', display:'flex', alignItems:'center', gap:'5px' }}>
        {partyLabel} filters
        {cfg && <span style={{ fontSize:'9px', color:'var(--text-muted)', fontWeight:400 }}>({cfg.name})</span>}
      </div>
      {/* Bundle-level discrepancy status (set on the partner's API config) */}
      {cfg && (
        (cfg.bundle_dimension || '').trim()
          ? <div style={{ fontSize:'9px', color:'var(--purple)', marginBottom:'6px', display:'flex', alignItems:'center', gap:'4px' }}>
              📦 Bundle-level discrepancy enabled — dimension: <code style={{ fontFamily:'var(--font-mono)' }}>{cfg.bundle_dimension}</code>
            </div>
          : <div style={{ fontSize:'9px', color:'var(--text-muted)', marginBottom:'6px', fontStyle:'italic' }}>
              📦 To enable per-day bundle-level discrepancy, set a “Bundle Dimension” in this partner’s API config (Account → Partner API Configs).
            </div>
      )}
      {filters.map((f, fi) => {
        const vals = f.value ? f.value.split(',').filter(v=>v.trim()).length : 0;
        const isMulti = vals > 1;
        return (
          <div key={fi} style={{ marginBottom:'6px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 1fr auto', gap:'5px', alignItems:'center' }}>
              {/* Dimension key dropdown */}
              <select className="select-field" style={{ fontSize:'11px' }}
                value={f.key} onChange={e => updateFilter(fi, 'key', e.target.value)}>
                <option value="">— Select dimension —</option>
                {dims.map(o => <option key={o.key} value={o.key}>{o.label} ({o.key})</option>)}
                <option value="__custom__">Custom key...</option>
              </select>

              {/* Operator — always visible, even with multi-value */}
              <select className="select-field" style={{ fontSize:'11px', padding:'4px 5px' }}
                value={f.operator || '='} onChange={e => updateFilter(fi, 'operator', e.target.value)}>
                <option value="=">= equals</option>
                <option value="!=">≠ not equals</option>
                <option value=">">{'>'} greater</option>
                <option value="<">{'<'} less</option>
                <option value="LIKE">~ contains</option>
              </select>

              {/* Value input */}
              {f.key === '__custom__' ? (
                <input className="input-field" placeholder="Type filter key" style={{ fontSize:'11px' }}
                  value={f.customKey || ''} onChange={e => updateFilter(fi, 'customKey', e.target.value)} />
              ) : (
                <input className="input-field"
                  placeholder="Value — comma-separate for multiple e.g. 627, 931"
                  style={{ fontSize:'11px', fontFamily:'var(--font-mono)' }}
                  value={f.value} onChange={e => updateFilter(fi, 'value', e.target.value)} />
              )}

              <button className="btn btn-danger btn-sm" onClick={() => removeFilter(fi)}><Trash2 size={10}/></button>
            </div>
            {isMulti && (
              <div style={{ fontSize:'9px', marginTop:'3px', paddingLeft:'2px', color:'var(--cyan)' }}>
                ✓ {vals} values →{' '}
                {f.operator === '=' || !f.operator
                  ? <code style={{ fontFamily:'var(--font-mono)' }}>IN [...]</code>
                  : <><code style={{ fontFamily:'var(--font-mono)' }}>{f.operator}</code> applied to each value</>
                }
              </div>
            )}
          </div>
        );
      })}
      <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px' }} onClick={addFilter}>
        <Plus size={10}/> Add filter
      </button>
    </div>
  );
}

// ─── Create Monitor Modal ─────────────────────────────────────────────────────
function CreateMonitorModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1=type select, 2=config
  const [monitorType, setMonitorType] = useState(null);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [adServerName, setAdServerName] = useState('');
  // For standard types: [{ id, name }]
  // For freeform: [{ id, name, role: 'supply'|'demand' }]
  // For auto_monitor: [{ id, name, role: 'supply'|'demand', savedConfigId }]
  const [partners, setPartners] = useState([{ id:'p0', name:'' }]);
  const [saving, setSaving] = useState(false);

  // Auto monitor specific
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [adServerConfigId, setAdServerConfigId] = useState('');
  const [adServerUrl, setAdServerUrl] = useState('');
  const [adServerUrlFmt, setAdServerUrlFmt] = useState('YYYY-MM-DD');
  const [revenueMapping, setRevenueMapping] = useState({ gross: '', net: '' });
  const [alertThreshold, setAlertThreshold] = useState('20');
  const [runHourUtc, setRunHourUtc] = useState(8);
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [notificationEmail, setNotificationEmail] = useState('');
  // Initial date range for first fetch (subsequent runs use yesterday automatically)
  const [initialDateFrom, setInitialDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return localYMD(d);
  });
  const [initialDateTo, setInitialDateTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return localYMD(d);
  });
  // Auto monitor per-party filters: { [partyId]: [{key, value, operator}] }
  const [autoFilters, setAutoFilters] = useState({ adserver: [] });

  const isFreeform = monitorType === 'freeform';
  const isAuto = monitorType === 'auto_monitor';

  // Load saved configs when entering auto_monitor step
  useEffect(() => {
    if (isAuto) {
      axios.get('/api/partner-configs').then(r => setSavedConfigs(r.data)).catch(() => {});
    }
  }, [isAuto]);

  function addPartner(role) {
    setPartners(p => [...p, { id:`p${Date.now()}`, name:'', role: role || (isFreeform ? 'demand' : undefined) }]);
  }
  function removePartner(id) { setPartners(p => p.filter(x => x.id !== id)); }

  const ROLE_COLORS = { supply:'var(--green)', demand:'var(--purple)', adserver:'var(--cyan)' };

  async function handleCreate() {
    if (!name.trim()) return toast.error('Monitor name required');
    if (!adServerName.trim()) return toast.error('Ad Server name required');
    if (isAuto && !adServerConfigId) return toast.error('Please select a saved API config or API URL for the Ad Server');
    if (isAuto && adServerConfigId === '__url__' && !adServerUrl.trim()) return toast.error('Enter the Ad Server API URL');
    if (!isAuto && !isFreeform && partners.some(p => !p.name.trim())) return toast.error('All partners need a name');
    if ((isFreeform || isAuto) && partners.length > 0 && partners.some(p => !p.name.trim())) return toast.error('All partners need a name');

    setSaving(true);
    try {
      const payload = {
        name, monitorType, timezone,
        adServer: isAuto
          ? (adServerConfigId === '__url__'
              ? { name: adServerName, inputType: 'url', savedConfigId: '', urlVal: adServerUrl.trim(), urlDateFmt: adServerUrlFmt || 'YYYY-MM-DD' }
              : { name: adServerName, inputType: 'saved', savedConfigId: adServerConfigId })
          : { name: adServerName, inputType: 'file' },
        partners: partners.filter(p => p.name.trim()).map(p => {
          const base = { ...p, role: p.role || (monitorType === 'supply' ? 'supply' : 'demand') };
          return isAuto ? normalizeAutoParty(base) : { ...base, inputType: 'file' };
        }),
      };
      if (isAuto) {
        payload.scheduleEnabled = scheduleEnabled;
        payload.runHourUtc = runHourUtc;
        payload.alertThreshold = parseFloat(alertThreshold) || 20;
        payload.notificationEmail = notificationEmail;
        payload.autoFilters = autoFilters;
        payload.revenueMapping = revenueMapping;
        payload.initialDateFrom = initialDateFrom;
        payload.initialDateTo = initialDateTo;
      }
      const { data } = await axios.post('/api/disc-monitor-v2/create', payload);
      toast.success('Monitor created');
      onCreated(data.monitor);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create');
    } finally { setSaving(false); }
  }

  const typeInfo = MONITOR_TYPES.find(t => t.id === monitorType);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', width:'100%', maxWidth:'800px', maxHeight:'95vh', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'16px', color:'var(--text-primary)', display:'flex', alignItems:'center', gap:'10px' }}>
            {step === 1 ? 'Choose Monitor Type' : (
              <>
                {typeInfo && <typeInfo.icon size={16} style={{ color:typeInfo.color }} />}
                Create {typeInfo?.label}
              </>
            )}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'18px' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', padding:'20px', flex:1 }}>
          {/* Step 1: Type selector */}
          {step === 1 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {MONITOR_TYPES.map(type => {
                const Icon = type.icon;
                const badgeColor = type.id === 'auto_monitor' ? 'badge-green' : type.id === 'freeform' ? 'badge-amber' : 'badge-cyan';
                return (
                  <div key={type.id}
                    onClick={() => { setMonitorType(type.id); setStep(2); }}
                    style={{ padding:'16px 20px', borderRadius:'var(--radius)', border:`2px solid ${monitorType===type.id ? type.color : 'var(--border)'}`, cursor:'pointer', transition:'all 0.15s', background: monitorType===type.id ? 'var(--bg-elevated)' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor=type.color}
                    onMouseLeave={e => e.currentTarget.style.borderColor=monitorType===type.id ? type.color : 'var(--border)'}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'6px' }}>
                      <Icon size={18} style={{ color:type.color }} />
                      <span style={{ fontWeight:700, fontSize:'14px', color:'var(--text-primary)' }}>{type.label}</span>
                      <span className={`badge ${badgeColor}`} style={{ fontSize:'10px' }}>{type.badge}</span>
                      {type.badgeExtra?.map(b => (
                        <span key={b.label} className={`badge ${b.style}`} style={{ fontSize:'9px', fontWeight:800 }}>{b.label}</span>
                      ))}
                    </div>
                    <div style={{ fontSize:'12px', color:'var(--text-muted)', lineHeight:1.6, paddingLeft:'28px' }}>{type.desc}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Config — standard types */}
          {step === 2 && !isFreeform && !isAuto && (
            <div>
              <div className="alert alert-info" style={{ marginBottom:'16px' }}>
                <Info size={12}/>
                <div style={{ fontSize:'11px' }}>
                  {monitorType === 'master_basic'
                    ? 'Upload Ad Server report (Date, Impressions, Gross Revenue) and reports from each Demand Partner. Column names are detected automatically.'
                    : monitorType === 'supply'
                    ? 'Upload Ad Server report and reports from each Supply Partner (SSP). Column names are detected automatically.'
                    : 'Upload Ad Server report with Advertiser column for partner-wise matching. Advertiser names are fuzzy-matched to partner names automatically.'}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Monitor Name *</label>
                  <input className="input-field w-full" placeholder="e.g. Main Stack Q1 2026" value={name} onChange={e=>setName(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Timezone</label>
                  <select className="select-field w-full" value={timezone} onChange={e=>setTimezone(e.target.value)}>
                    {['UTC','US/Eastern','US/Pacific','Europe/London','Europe/Berlin','Asia/Kolkata','Asia/Singapore','Asia/Tokyo'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:'12px' }}>
                <label style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px' }}>Ad Server (Baseline)</label>
                <input className="input-field w-full" placeholder="e.g. DFP, Google Ad Manager, Xandr..." value={adServerName} onChange={e=>setAdServerName(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px' }}>
                  {monitorType === 'supply' ? 'Supply Partners (SSPs)' : 'Demand Partners'}
                </label>
                {partners.map((p,i) => (
                  <div key={p.id} style={{ display:'flex', gap:'8px', marginBottom:'8px', alignItems:'center' }}>
                    <input className="input-field" style={{ flex:1 }}
                      placeholder={monitorType === 'supply' ? `SSP ${i+1} name (e.g. Magnite, PubMatic...)` : `Partner ${i+1} name (e.g. DV360, TTD, Xandr...)`}
                      value={p.name} onChange={e=>setPartners(prev=>prev.map(x=>x.id===p.id?{...x,name:e.target.value}:x))} />
                    {partners.length > 1 && <button className="btn btn-danger btn-sm" onClick={()=>removePartner(p.id)}><Trash2 size={11}/></button>}
                  </div>
                ))}
                <button className="btn btn-secondary btn-sm" onClick={() => addPartner()}><Plus size={12}/> Add Partner</button>
              </div>
            </div>
          )}

          {/* Step 2: Free Form config — partner roles */}
          {step === 2 && isFreeform && (
            <div>
              <div className="alert alert-info" style={{ marginBottom:'16px' }}>
                <Info size={12}/>
                <div style={{ fontSize:'11px', lineHeight:1.6 }}>
                  Add any mix of <strong style={{ color:'var(--green)' }}>Supply</strong> and <strong style={{ color:'var(--purple)' }}>Demand</strong> partners alongside your Ad Server. Discrepancies are computed for both sides of the chain — Supply vs Ad Server, and Ad Server vs Demand.
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Monitor Name *</label>
                  <input className="input-field w-full" placeholder="e.g. Full Stack Monitor" value={name} onChange={e=>setName(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Timezone</label>
                  <select className="select-field w-full" value={timezone} onChange={e=>setTimezone(e.target.value)}>
                    {['UTC','US/Eastern','US/Pacific','Europe/London','Europe/Berlin','Asia/Kolkata','Asia/Singapore','Asia/Tokyo'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>

              {/* Ad Server */}
              <div style={{ marginBottom:'12px', padding:'12px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                <label style={{ fontSize:'10px', color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px', fontWeight:700 }}>
                  ● Ad Server (Baseline)
                </label>
                <input className="input-field w-full" placeholder="e.g. DFP, Google Ad Manager, Xandr..." value={adServerName} onChange={e=>setAdServerName(e.target.value)} />
              </div>

              {/* Partners with role badges */}
              <div>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px', fontWeight:700 }}>
                  Partners <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', letterSpacing:0 }}>— set each partner's role</span>
                </div>
                {partners.map((p, i) => (
                  <div key={p.id} style={{ display:'flex', gap:'6px', marginBottom:'8px', alignItems:'center' }}>
                    {/* Role toggle */}
                    <select
                      className="select-field"
                      style={{ width:'110px', fontSize:'11px', flexShrink:0,
                        color: ROLE_COLORS[p.role || 'demand'],
                        borderColor: `color-mix(in srgb, ${ROLE_COLORS[p.role || 'demand']} 27%, transparent)`
                      }}
                      value={p.role || 'demand'}
                      onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,role:e.target.value} : x))}
                    >
                      <option value="supply">Supply</option>
                      <option value="demand">Demand</option>
                    </select>
                    <input className="input-field" style={{ flex:1 }}
                      placeholder={p.role === 'supply' ? `SSP ${i+1} name (e.g. Magnite, PubMatic...)` : `DSP ${i+1} name (e.g. DV360, TTD...)`}
                      value={p.name}
                      onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,name:e.target.value} : x))}
                    />
                    {partners.length > 1 && <button className="btn btn-danger btn-sm" onClick={()=>removePartner(p.id)}><Trash2 size={11}/></button>}
                  </div>
                ))}
                <div style={{ display:'flex', gap:'6px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => addPartner('supply')} style={{ borderColor:'color-mix(in srgb, var(--green) 40%, transparent)', color:'var(--green)' }}>
                    <Plus size={12}/> Supply Partner
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => addPartner('demand')} style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}>
                    <Plus size={12}/> Demand Partner
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Auto Monitor config */}
          {step === 2 && isAuto && (
            <div>
              <div className="alert alert-info" style={{ marginBottom:'16px' }}>
                <Info size={12}/>
                <div style={{ fontSize:'11px', lineHeight:1.6 }}>
                  Auto Monitor only works with <strong>Saved API Configs</strong> from Account → Partner API Configurations. CSV upload and manual entry are not supported. The monitor will fetch data and run discrepancy checks once daily at <strong>the time you pick in Alert Settings</strong> (default 8 AM UTC — late enough that demand partner data reported in PST timezone is fully available).
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Monitor Name *</label>
                  <input className="input-field w-full" placeholder="e.g. Auto Daily Monitor" value={name} onChange={e=>setName(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Timezone</label>
                  <select className="select-field w-full" value={timezone} onChange={e=>setTimezone(e.target.value)}>
                    {['UTC','US/Eastern','US/Pacific','Europe/London','Europe/Berlin','Asia/Kolkata','Asia/Singapore','Asia/Tokyo'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
              </div>

              {/* Ad Server saved config */}
              <div style={{ marginBottom:'12px', padding:'12px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                <label style={{ fontSize:'10px', color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px', display:'block', marginBottom:'6px', fontWeight:700 }}>
                  ● Ad Server
                </label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Display Name *</label>
                    <input className="input-field w-full" placeholder="e.g. Google Ad Manager" value={adServerName} onChange={e=>setAdServerName(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Saved API Config *</label>
                    <select className="select-field w-full" value={adServerConfigId} onChange={e=>setAdServerConfigId(e.target.value)}>
                      <option value="">— Select config —</option>
                      {savedConfigs.filter(c => c.role === 'adserver').map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                      <option value="__url__">Use API URL instead</option>
                    </select>
                    {savedConfigs.filter(c => c.role === 'adserver').length === 0 && (
                      <div style={{ fontSize:'10px', color:'var(--amber)', marginTop:'4px' }}>No Ad Server configs saved. Add one in Account → Partner API Configurations.</div>
                    )}
                  </div>
                </div>
                {adServerConfigId === '__url__' && (
                  <UrlSourceInputs urlVal={adServerUrl} urlDateFmt={adServerUrlFmt} onUrl={setAdServerUrl} onFmt={setAdServerUrlFmt} />
                )}
                {isAuto && (
                  <RevenueMetricPanel hasSupply={partners.some(p => p.role === 'supply')} hasDemand={partners.some(p => p.role === 'demand')} mapping={revenueMapping} onChange={setRevenueMapping} savedConfigId={adServerConfigId} savedConfigs={savedConfigs} filters={(autoFilters && autoFilters.adserver) || []} />
                )}
              </div>

              {/* Demand/Supply partners */}
              <div style={{ marginBottom:'14px' }}>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px', fontWeight:700 }}>
                  Partners <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, color:'var(--text-muted)' }}>— each must have a saved API config</span>
                </div>
                {partners.map((p, i) => (
                  <div key={p.id} style={{ marginBottom:'8px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'110px 1fr 1fr auto', gap:'6px', alignItems:'center' }}>
                    <select
                      className="select-field"
                      style={{ fontSize:'11px', color: ROLE_COLORS[p.role || 'demand'], borderColor: (`color-mix(in srgb, ${ROLE_COLORS[p.role || 'demand']} 27%, transparent)`) }}
                      value={p.role || 'demand'}
                      onChange={e => setPartners(prev => prev.map(x => x.id===p.id ? {...x,role:e.target.value} : x))}
                    >
                      <option value="supply">Supply</option>
                      <option value="demand">Demand</option>
                    </select>
                    <input className="input-field" placeholder="Partner display name"
                      value={p.name} onChange={e=>setPartners(prev=>prev.map(x=>x.id===p.id?{...x,name:e.target.value}:x))} />
                    <select className="select-field"
                      value={p.savedConfigId || ''}
                      onChange={e=>setPartners(prev=>prev.map(x=>x.id===p.id?{...x,savedConfigId:e.target.value}:x))}>
                      <option value="">— Saved config —</option>
                      {savedConfigs
                        .filter(c => c.role !== 'adserver')
                        .map(c => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
                      <option value="__url__">Use API URL instead</option>
                    </select>
                    {partners.length > 1 && <button className="btn btn-danger btn-sm" onClick={()=>removePartner(p.id)}><Trash2 size={11}/></button>}
                  </div>
                  {p.savedConfigId === '__url__' && (
                    <UrlSourceInputs urlVal={p.urlVal} urlDateFmt={p.urlDateFmt}
                      onUrl={v => setPartners(prev => prev.map(x => x.id===p.id ? {...x,urlVal:v} : x))}
                      onFmt={v => setPartners(prev => prev.map(x => x.id===p.id ? {...x,urlDateFmt:v} : x))} />
                  )}
                  </div>
                ))}
                <div style={{ display:'flex', gap:'6px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => addPartner('supply')} style={{ borderColor:'color-mix(in srgb, var(--green) 40%, transparent)', color:'var(--green)' }}>
                    <Plus size={12}/> Supply
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => addPartner('demand')} style={{ borderColor:'color-mix(in srgb, var(--purple) 40%, transparent)', color:'var(--purple)' }}>
                    <Plus size={12}/> Demand
                  </button>
                </div>
              </div>

              {/* Initial date range for first fetch */}
              <div style={{ padding:'12px', background:'color-mix(in srgb, var(--cyan) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
                <div style={{ fontSize:'10px', color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:700, marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
                  <Calendar size={11}/> Initial Date Range
                </div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px', lineHeight:1.5 }}>
                  Data is fetched for this range when the monitor first runs. Subsequent scheduled runs automatically fetch <strong>yesterday's</strong> data.
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>From</label>
                    <input type="date" className="input-field w-full" value={initialDateFrom} onChange={e=>setInitialDateFrom(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>To</label>
                    <input type="date" className="input-field w-full" value={initialDateTo} onChange={e=>setInitialDateTo(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Alert settings */}
              <div style={{ padding:'12px', background:'color-mix(in srgb, var(--green-soft) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--green-soft) 20%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
                <div style={{ fontSize:'10px', color:'var(--green-soft)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:700, marginBottom:'10px', display:'flex', alignItems:'center', gap:'6px' }}>
                  <Bell size={11}/> Alert Settings
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Critical Alert Threshold (%)</label>
                    <input type="number" className="input-field w-full" min="1" max="100" placeholder="20"
                      value={alertThreshold} onChange={e=>setAlertThreshold(e.target.value)} />
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px' }}>Trigger email alert when discrepancy exceeds this %</div>
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Daily Run Time (UTC)</label>
                    <select className="input-field w-full" value={runHourUtc} onChange={e => setRunHourUtc(parseInt(e.target.value, 10))}>
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC{h === 8 ? ' (default)' : ''}</option>
                      ))}
                    </select>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px' }}>Pick an hour after your partners finalize the previous day's data</div>
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Alert Email</label>
                    <input type="email" className="input-field w-full" placeholder="alerts@yourcompany.com"
                      value={notificationEmail} onChange={e=>setNotificationEmail(e.target.value)} />
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'3px' }}>Also alerts to emails in Account → Notifications</div>
                  </div>
                </div>
                <div style={{ marginTop:'12px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)' }}>
                  <div style={{ paddingRight:'10px' }}>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-primary)' }}>Automated daily run</div>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px', lineHeight:1.5 }}>On by default - runs once a day at the time above. Switch off to run this monitor manually instead.</div>
                  </div>
                  <button type="button" onClick={() => setScheduleEnabled(v => !v)} style={{ flexShrink:0, display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 10px', borderRadius:'var(--radius-sm)', cursor:'pointer', background: scheduleEnabled ? 'color-mix(in srgb, var(--green-soft) 15%, transparent)' : 'var(--bg-elevated)', color: scheduleEnabled ? 'var(--green-soft)' : 'var(--text-muted)', border: `1px solid ${scheduleEnabled ? 'color-mix(in srgb, var(--green-soft) 40%, transparent)' : 'var(--border)'}`, fontSize:'12px', fontWeight:600 }}>
                    {scheduleEnabled ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                    {scheduleEnabled ? 'Scheduled - On' : 'Off'}
                  </button>
                </div>
              </div>

              {/* Per-party API Filters */}
              <div style={{ padding:'12px', background:'color-mix(in srgb, var(--amber) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius:'var(--radius-sm)' }}>
                <div style={{ fontSize:'10px', color:'var(--amber)', textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:700, marginBottom:'6px', display:'flex', alignItems:'center', gap:'6px' }}>
                  <Filter size={11}/> API Filters (Optional)
                </div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'10px', lineHeight:1.6 }}>
                  Injected into each party's API request body on every run. Use them to scope data to specific publishers, channels, advertisers, etc.
                </div>
                <AutoMonitorFilterRows
                  partyId="adserver" partyLabel="Ad Server" partyColor="var(--cyan)"
                  autoFilters={autoFilters} setAutoFilters={setAutoFilters}
                  savedConfigs={savedConfigs} configId={adServerConfigId}
                />
                {partners.filter(p=>p.name.trim()).map(p => (
                  <AutoMonitorFilterRows
                    key={p.id}
                    partyId={p.id} partyLabel={p.name} partyColor={p.role==='supply' ? 'var(--green)' : 'var(--purple)'}
                    autoFilters={autoFilters} setAutoFilters={setAutoFilters}
                    savedConfigs={savedConfigs} configId={p.savedConfigId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : <><Save size={13}/> Create Monitor</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DiscrepancyMonitorV2() {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('master');
  // ── Detail view state ──
  // viewingDetailFor === <monitor.id> means render that single monitor in
  // detail mode (results-only); === null means render the full list.
  // No URL change — that intentionally avoids polluting Vercel/GA analytics
  // with per-monitor pageview rows. Trade-off: detail view isn't bookmarkable
  // or shareable internally, which the user explicitly accepted because runs
  // are fast.
  const [viewingDetailFor, setViewingDetailFor] = useState(null);
  // Remember scroll position when entering detail so we can restore on back
  const savedScrollY = useRef(0);

  // When entering detail, capture current scroll. When returning, restore.
  // The scrollable element is `.page-content` (the dashboard's inner scroll
  // container) — fall back to window for safety.
  function getScrollContainer() {
    return document.querySelector('.page-content') || window;
  }
  function enterDetail(id) {
    const sc = getScrollContainer();
    savedScrollY.current = sc === window ? window.scrollY : sc.scrollTop;
    setViewingDetailFor(id);
    // Scroll detail view to top so the user starts at the monitor header
    setTimeout(() => {
      const sc2 = getScrollContainer();
      if (sc2 === window) window.scrollTo(0, 0); else sc2.scrollTop = 0;
    }, 0);
  }
  function exitDetail() {
    setViewingDetailFor(null);
    setTimeout(() => {
      const sc = getScrollContainer();
      if (sc === window) window.scrollTo(0, savedScrollY.current);
      else sc.scrollTop = savedScrollY.current;
    }, 0);
  }

  useEffect(() => { loadMonitors(); }, []);

  async function loadMonitors() {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/disc-monitor-v2/list');
      setMonitors(data);
    } catch { toast.error('Failed to load monitors'); }
    finally { setLoading(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this monitor and all its data?')) return;
    await axios.delete(`/api/disc-monitor-v2/${id}`);
    toast.success('Deleted');
    loadMonitors();
  }

  const masterMonitors = monitors.filter(m => ['master_basic','master_advanced','supply','freeform'].includes(m.monitorType) || !m.monitorType);
  const autoMonitors   = monitors.filter(m => m.monitorType === 'auto_monitor');

  return (
    <div>
      <h1 className="page-title" style={{ display:'flex', alignItems:'center', gap:'10px' }}>
        Discrepancy Monitor
        <span style={{ fontSize:'11px', fontWeight:800, background:'color-mix(in srgb, var(--purple) 20%, transparent)', color:'var(--purple)', border:'1px solid color-mix(in srgb, var(--purple) 40%, transparent)', borderRadius:'4px', padding:'2px 8px', verticalAlign:'middle' }}>BETA</span>
      </h1>
      <p className="page-subtitle">Monitor impression and revenue discrepancies across your programmatic stack. Compare Ad Server data against Demand Partners or Supply Partners.</p>

      {/* Back to Monitors — top-level, sits below the page subtitle and above
          the detail card. Only rendered in detail mode. Uses inline-block so
          the button hugs its content (doesn't stretch the row). */}
      {viewingDetailFor != null && (
        <div style={{ marginBottom:'14px' }}>
          <button className="btn btn-secondary btn-sm" onClick={exitDetail}
            style={{ borderColor:'color-mix(in srgb, var(--cyan) 40%, transparent)', color:'var(--cyan)' }}>
            <ArrowLeft size={11}/> Back to Monitors
          </button>
        </div>
      )}

      {/* ── List view + detail view via display toggle ──────────────────────
          We always render exactly ONE MonitorCard per monitor. When a monitor
          is in detail mode, we hide all OTHER monitor cards (display:none on
          the card itself via the `hidden` prop), set detailMode={true} on the
          focused card, and hide the tabs/chrome divs around the cards. This
          keeps each card's local state intact across list↔detail switches —
          unmounting any card would wipe its `result`, `lastRun`, etc. */}

      {/* Tabs — hidden in detail mode */}
      <div style={{ display: viewingDetailFor == null ? 'flex' : 'none', gap:'2px', marginBottom:'16px', borderBottom:'2px solid var(--border)', paddingBottom:'0' }} className="tabs">
        {[
          { id:'master', icon:<Layers size={13}/>, label:'Master Monitors', badge: null, count: masterMonitors.length },
          { id:'auto',   icon:<Cpu size={13}/>,    label:'Auto Monitors',   badge: [{ label:'BETA', style:'color-mix(in srgb, var(--green-soft) 20%, transparent)', color:'var(--green-soft)', border:'color-mix(in srgb, var(--green-soft) 40%, transparent)' }, { label:'NEW', style:'color-mix(in srgb, var(--purple) 20%, transparent)', color:'var(--purple)', border:'color-mix(in srgb, var(--purple) 40%, transparent)' }], count: autoMonitors.length, dot: autoMonitors.some(m => m.scheduleEnabled) },
        ].map(t => (
          <button key={t.id}
            className={`tab ${activeTab===t.id?'active':''}`}
            onClick={() => setActiveTab(t.id)}
            style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'9px 18px', borderRadius:'var(--radius-sm) var(--radius-sm) 0 0',
              fontWeight: activeTab===t.id ? 800 : 500,
              fontSize:'12px',
              borderBottom: activeTab===t.id ? '2px solid var(--cyan)' : '2px solid transparent',
              marginBottom:'-2px',
              background: activeTab===t.id ? 'var(--bg-elevated)' : 'transparent',
              color: activeTab===t.id ? 'var(--cyan)' : 'var(--text-muted)',
              transition:'all 0.15s',
            }}
          >
            {t.icon}
            {t.label}
            {t.count !== undefined && (
              <span className="badge badge-muted" style={{ fontSize:'9px' }}>{t.count}</span>
            )}
            {t.badge && t.badge.map((b, bi) => (
              <span key={bi} style={{ fontSize:'9px', fontWeight:800, background:b.style, color:b.color, border:`1px solid ${b.border}`, borderRadius:'3px', padding:'1px 5px' }}>{b.label}</span>
            ))}
            {t.dot && (
              <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--green-soft)', display:'inline-block', boxShadow:'0 0 6px color-mix(in srgb, var(--green-soft) 70%, transparent)' }} />
            )}
          </button>
        ))}
      </div>

      {/* Auto Monitors tab */}
      {activeTab === 'auto' && (
        <>
          <div style={{ display: viewingDetailFor == null ? 'block' : 'none', marginBottom:'12px', padding:'12px 16px', background:'color-mix(in srgb, var(--green-soft) 4%, transparent)', border:'1px solid color-mix(in srgb, var(--green-soft) 20%, transparent)', borderRadius:'var(--radius)', fontSize:'12px', color:'var(--text-secondary)', lineHeight:1.7 }}>
            <Cpu size={13} style={{ color:'var(--green-soft)', verticalAlign:'middle', marginRight:'6px' }}/>
            Auto Monitors fetch data from your <strong>Saved API Configs</strong> and run discrepancy checks <strong>once daily at the time you set per monitor</strong> (default 8 AM UTC — late enough that demand partner data reported in PST timezone is fully available). Critical discrepancies send an email alert and appear in the Notifications bar. CSV and manual data entry are not supported — this tab is API-only.
          </div>
          <div style={{ display: viewingDetailFor == null ? 'flex' : 'none', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
              {autoMonitors.length} auto monitor{autoMonitors.length!==1?'s':''}
              {autoMonitors.filter(m=>m.scheduleEnabled).length > 0 && (
                <span style={{ color:'var(--green-soft)', marginLeft:'8px', fontWeight:700 }}>
                  · {autoMonitors.filter(m=>m.scheduleEnabled).length} active
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={loadMonitors} disabled={loading}><RefreshCw size={12}/></button>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <Plus size={14}/> New Auto Monitor
              </button>
            </div>
          </div>
          {loading ? (
            <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
          ) : autoMonitors.length === 0 ? (
            <div className="card" style={{ textAlign:'center', padding:'50px 20px' }}>
              <Cpu size={36} style={{ color:'var(--text-muted)', marginBottom:'14px' }}/>
              <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px' }}>No Auto Monitors yet</div>
              <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'20px', maxWidth:'480px', margin:'0 auto 20px', lineHeight:1.7 }}>
                Create an Auto Monitor to run automated discrepancy checks once daily — at the time you choose — using your saved API configs. No manual CSV uploads needed.
              </div>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={13}/> Create Auto Monitor</button>
            </div>
          ) : (
            autoMonitors.map((m, idx) => (
              <MonitorCard key={m.id} monitor={m}
                onDelete={handleDelete} onRefresh={loadMonitors} cardIndex={idx}
                onViewResults={enterDetail}
                onBackToList={exitDetail}
                detailMode={viewingDetailFor === m.id}
                hidden={viewingDetailFor != null && viewingDetailFor !== m.id}
              />
            ))
          )}
        </>
      )}

      {/* Controls + Monitor list — master tab only */}
      {activeTab === 'master' && (
        <>
          <div style={{ display: viewingDetailFor == null ? 'flex' : 'none', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
              {masterMonitors.length} monitor{masterMonitors.length!==1?'s':''}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={loadMonitors} disabled={loading}><RefreshCw size={12}/></button>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                <Plus size={14}/> New Monitor
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:'40px' }}><span className="spinner"/></div>
          ) : masterMonitors.length === 0 ? (
            <div className="card" style={{ textAlign:'center', padding:'50px 20px' }}>
              <Layers size={36} style={{ color:'var(--text-muted)', marginBottom:'14px' }}/>
              <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px' }}>No Monitors yet</div>
              <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'20px', maxWidth:'440px', margin:'0 auto 20px', lineHeight:1.7 }}>
                Create a monitor to start tracking discrepancies between your Ad Server and Demand or Supply Partners.
              </div>
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={13}/> Create Monitor</button>
            </div>
          ) : (
            masterMonitors.map((m, idx) => (
              <MonitorCard key={m.id} monitor={m}
                onDelete={handleDelete} onRefresh={loadMonitors} cardIndex={idx}
                onViewResults={enterDetail}
                onBackToList={exitDetail}
                detailMode={viewingDetailFor === m.id}
                hidden={viewingDetailFor != null && viewingDetailFor !== m.id}
              />
            ))
          )}
        </>
      )}

      {showCreate && <CreateMonitorModal onClose={() => setShowCreate(false)} onCreated={m => { setMonitors(prev => [...prev, m]); setShowCreate(false); }} />}
    </div>
  );
}
